import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Link as MuiLink,
  TextField,
  Button,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Menu,
  MenuItem,
  Fab,
} from '@mui/material';
import { CloudUpload, Send, CheckCircle, Info, ExpandMore, Work, Stream, Link, Description, Person, Receipt, Extension, ErrorOutline, RadioButtonUnchecked, RocketLaunch, LinkOff, EventAvailable, Quiz, HelpOutline, Close, ChatBubbleOutline, SmartToy, Add as AddIcon, DeleteOutline, Edit as EditIcon, WorkspacePremium, AutoAwesome } from '@mui/icons-material';
import axios from 'axios';
import Footer from './Footer';
import AdminMarketing from './AdminMarketing';
import { captureFirstTouch, attributionQuery } from './attribution';
import { experimentQuery } from './abtest';
import { ThemeSwitcher } from './ThemeController';
import VoiceMockInterview from './VoiceMockInterview';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Auto-pilot schedule helpers.
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Half-hour time options for the schedule "Run at" picker (value stays HH:MM).
const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`
);
const fmtTime = (t: string): string => {
  const [h, m] = String(t || '09:00').split(':').map((n) => parseInt(n, 10));
  const ap = (h || 0) < 12 ? 'AM' : 'PM';
  const hh = (((h || 0) + 11) % 12) + 1;
  return `${hh}:${String(m || 0).padStart(2, '0')} ${ap}`;
};
const BROWSER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();
const TIMEZONES = Array.from(
  new Set([
    BROWSER_TZ,
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ])
);

// Chrome Web Store listing for the ResumeAlign extension. Set this once the
// extension is published; until then the UI shows the manual-install fallback.
// (Kept as a constant, not a REACT_APP_* env var, because the Docker build
// doesn't bake build-time env vars — see frontend/Dockerfile.)
const CHROME_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/resumealign-autofill/odahnldnmbpnngnfmgkjoeiihogdokhg';

// Compare dotted versions ("0.6.13" vs "0.6.2"): -1 if a<b, 0 equal, 1 if a>b.
function cmpVersion(a: string, b: string): number {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

// Send the identity cookie (ra_client_id) on every request.
axios.defaults.withCredentials = true;

const CURRENCY_SYMBOLS: Record<string, string> = { usd: '$', eur: '€' };
const formatMoney = (amount: number, currency = 'usd') =>
  `${CURRENCY_SYMBOLS[currency] || '$'}${(amount ?? 0).toFixed(2)}`;

interface PaymentRecord {
  amount: number;
  currency: string;
  processed_at: string;
}

interface BalanceInfo {
  balance_usd: number;
  currency: string;
  free_tries_used: number;
  free_tries_remaining: number;
  free_tries_total: number;
  plan?: string;
  plan_name?: string;
  experimental?: boolean;
  allow_overage?: boolean;
  byok?: boolean;
  byok_mtd_cost_usd?: number;
  byok_yesterday_cost_usd?: number;
  byok_yesterday_date?: string;
  byok_credit_exhausted?: boolean;
  has_anthropic_key?: boolean;
  byok_active?: boolean;
  monthly_credits?: number;
  credits_remaining?: number;
  credits_used?: number;
  overage_credits?: number;
  credits_per_topup?: number;
  topup_amount?: number;
  sub_status?: string | null;
  current_period_end?: string | null;
  email?: string | null;
  name?: string | null;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  yearly_price?: number | null;
  yearly_savings_pct?: number;
  has_yearly?: boolean;
  monthly_credits: number;
  experimental: boolean;
  allow_overage: boolean;
  byok?: boolean;
}

const LEADS_PER_PAGE = 10;
// Celerity-BBS-style neon palette for the queue area — bright 16-color ANSI vibe
// over the dark matrix theme.
const BBS = {
  cyan: '#00e5ff',
  magenta: '#ff5fd2',
  yellow: '#ffe14d',
  green: '#39ff14',
  orange: '#ff7a18',
  blue: '#6ba3ff',
};
const BBS_CYCLE = [BBS.cyan, BBS.magenta, BBS.yellow, BBS.green, BBS.orange, BBS.blue];
const neon = (c: string) => ({ color: c, borderColor: c });
// Sources the Dice mass-apply must NOT drive — each has its own applier
// (Indeed smartapply, LinkedIn Easy Apply, the sequential ZipRecruiter loop).
// Handing these to the Dice content script leaves them un-applied AND un-marked,
// so they reappear on every mass-apply run.
const DICE_APPLY_EXCLUDE = ['indeed', 'linkedin', 'ziprecruiter'];

// Progress of the one-click auto-apply pipeline (shown as a timeline dialog).
type AutoStepStatus = 'pending' | 'active' | 'done' | 'error';

// Per-job pipeline stages, so each job visibly moves Optimize -> PDF -> Apply.
type JobStages = { optimize: AutoStepStatus; pdf: AutoStepStatus; apply: AutoStepStatus };
const STAGE_STEPS: { key: keyof JobStages; label: string }[] = [
  { key: 'optimize', label: 'Optimize' },
  { key: 'pdf', label: 'PDF' },
  { key: 'apply', label: 'Apply' },
];

function StageDot({ s }: { s: AutoStepStatus }) {
  if (s === 'done') return <CheckCircle color="success" sx={{ fontSize: 13 }} />;
  if (s === 'active') return <CircularProgress size={11} sx={{ color: '#ff7a18' }} />;
  if (s === 'error') return <ErrorOutline color="error" sx={{ fontSize: 13 }} />;
  return <RadioButtonUnchecked sx={{ fontSize: 13, color: 'action.disabled' }} />;
}

function StageStepper({ stages }: { stages: JobStages }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
      {STAGE_STEPS.map((st, i) => {
        const s = stages[st.key];
        return (
          <React.Fragment key={st.key}>
            {i > 0 && <Typography variant="caption" sx={{ color: 'action.disabled' }}>›</Typography>}
            <StageDot s={s} />
            <Typography
              variant="caption"
              sx={{
                color: s === 'pending' ? 'text.disabled' : s === 'error' ? 'error.main'
                  : s === 'active' ? '#ff7a18' : 'text.secondary',
                fontWeight: s === 'active' ? 700 : 400,
              }}
            >
              {st.label}
            </Typography>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}
interface AutoApplyStep {
  key: string;
  label: string;
  status: AutoStepStatus;
  detail?: string;
}
interface AutoApplyState {
  leadId: string;
  title: string;
  steps: AutoApplyStep[];
}
// Backend timestamps are naive UTC (datetime.utcnow().isoformat(), no 'Z'), so
// the browser would parse them as local time and show them off by the UTC
// offset. Append 'Z' when there's no timezone marker so they render in the
// user's local zone.
const fmtDateTime = (iso?: string): string => {
  if (!iso) return '';
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`).toLocaleString();
};
// Clean Dice scrape noise from a stored job title ("View Details for <title>
// (<job-id>)") so older queue entries display nicely without a re-scan.
const cleanTitle = (t?: string): string =>
  (t || '')
    .replace(/\s+/g, ' ')
    .replace(/^view details for\s+/i, '')
    .replace(/\s*\([0-9a-f-]{8,}\)\s*$/i, '')
    .trim();
// Sanitize a job title/company into a filename-safe fragment.
const safeFilePart = (s?: string | null) =>
  (s || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 40);

interface OptimizationResult {
  id: string;
  optimized_resume: string;
  suggestions: string[];
  match_score: number;
  created_at: string;
  job_posting_content: string;
  job_url?: string;
  job_title?: string | null;
  company_name?: string | null;
  has_formatted_pdf?: boolean;
  cost_usd?: number;
  raw_cost_usd?: number;
  input_tokens?: number;
  output_tokens?: number;
  autopilot_run_at?: string | null;
  autopilot_keywords?: string | null;
  interview?: boolean;
}

interface AutopilotSession {
  run_at: string;
  processed: number;
  applied: number;
  tailored: number;
  nonremote: number;
  skipped: number;
  expired?: number;
  failed?: number;   // postings that couldn't be loaded/optimized (404/expired-on-fetch/tailor error)
  keywords: string[];
  cost_usd?: number | null;
}

const formatCost = (cost?: number) => {
  if (cost === undefined || cost === null) return '—';
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(2)}`;
};

interface JobLead {
  id: string;
  source: string;
  title: string;
  company?: string | null;
  location?: string | null;
  employment_type?: string | null;
  workplace_type?: string | null;
  salary?: string | null;
  posted?: string | null;
  updated?: string | null;
  easy_apply?: boolean;
  url: string;
  score: number;
  initial_score?: number | null;
  matched_keywords: string[];
  status: 'new' | 'tailored' | 'dismissed' | 'applied';
  optimization_id?: string | null;
  match_score?: number | null;
  interview?: boolean;
  interview_at?: string | null;
  created_at: string;
}

interface BaseProfile {
  id: string;
  label: string;
  keywords: string[];
  filename?: string | null;
  has_pdf: boolean;
  chars: number;
  is_default: boolean;
  is_master?: boolean;
  text?: string;
}

interface InterviewHistoryItem {
  type: 'prep' | 'mock_interview';
  job_title?: string | null;
  company_name?: string | null;
  credits: number;
  score?: number | null;
  created_at: string;
}

interface PrepQuestion {
  category: string;
  question: string;
  why?: string | null;
  talking_points?: string[];
}

interface InterviewPrepResult {
  id: string;
  lead_id: string;
  job_title?: string | null;
  company_name?: string | null;
  questions: PrepQuestion[];
  focus_areas: string[];
  readiness_note?: string | null;
  created_at: string;
  cost_usd?: number;
}

// Home-page testimonials. PLACEHOLDER copy — swap in real, verifiable reviews
// before treating these as marketing claims. Edit this one array to update them.
const REVIEWS: { name: string; role: string; stars: number; quote: string }[] = [
  { name: 'Priya S.', role: 'Data Engineer', stars: 5, quote: 'Auto-pilot applied to 40+ remote roles while I slept. Three interviews in the first week.' },
  { name: 'Marcus T.', role: 'Product Manager', stars: 5, quote: 'The tailored resumes actually match the posting — my callback rate jumped noticeably.' },
  { name: 'Elena R.', role: 'Software Engineer', stars: 5, quote: 'The formatted PDF clones my original layout but optimized. Saved me hours per application.' },
  { name: 'Devon K.', role: 'Solutions Architect', stars: 5, quote: 'Set my criteria once and it just runs. The daily apply cap keeps it sensible.' },
  { name: 'Aisha M.', role: 'UX Designer', stars: 5, quote: 'The BYO-key plan is unlimited and fast — best money I spend on the job hunt.' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Discreet admin panel for managing enterprise orgs. Reached at ?admin=1; gated
// by the admin token (stored locally). Not a normal tab — not shown to users.
function AdminOrgsPanel() {
  const blank = {
    name: '', code: '', display_name: '', primary_color: '#0f62fe',
    disclaimer: '', logo_b64: '', seats: 0, active: true,
  };
  const [token, setToken] = useState(localStorage.getItem('ra_admin_token') || '');
  const [orgs, setOrgs] = useState<any[]>([]);
  const [form, setForm] = useState<any>(blank);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setMsg('');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/admin/orgs`, { headers: { 'X-Admin-Token': token } });
      setOrgs(r.data || []);
      localStorage.setItem('ra_admin_token', token);
    } catch {
      setMsg('Invalid admin token, or load failed.');
    }
  };
  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      setMsg('Name and code are required.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await axios.post(`${API_BASE_URL}/api/admin/orgs`, form, { headers: { 'X-Admin-Token': token } });
      setMsg(`Saved "${form.name}".`);
      setForm(blank);
      load();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setMsg((typeof d === 'string' ? d : d?.message) || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };
  const toggleActive = async (o: any) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/orgs/activate`,
        { code: o.code, active: !o.active },
        { headers: { 'X-Admin-Token': token } }
      );
      load();
    } catch {
      setMsg('Activate failed.');
    }
  };
  const f = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>Enterprise Orgs — Admin</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="password" label="Admin token" value={token}
          onChange={(e) => setToken(e.target.value)} sx={{ minWidth: 260 }} />
        <Button variant="outlined" onClick={load} disabled={!token.trim()}>Load orgs</Button>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 1.5 }}>
            Create / update org (upserts by code)
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Legal name" value={form.name} onChange={f('name')} sx={{ flex: 1, minWidth: 200 }} />
              <TextField size="small" label="Join code" value={form.code} onChange={f('code')} sx={{ minWidth: 160 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Display name (header)" placeholder="IBM Consulting" value={form.display_name} onChange={f('display_name')} sx={{ flex: 1, minWidth: 200 }} />
              <TextField size="small" type="color" label="Accent" value={form.primary_color} onChange={f('primary_color')} sx={{ width: 80 }} />
              <TextField size="small" type="number" label="Seats (0=∞)" value={form.seats} onChange={f('seats')} sx={{ width: 120 }} />
            </Stack>
            <TextField size="small" label="Footer disclaimer (optional)" value={form.disclaimer} onChange={f('disclaimer')} fullWidth />
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button component="label" size="small" variant="outlined">
                {form.logo_b64 ? 'Logo added ✓' : 'Upload logo'}
                <input hidden type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const r = new FileReader();
                  r.onload = () => setForm({ ...form, logo_b64: String(r.result || '') });
                  r.readAsDataURL(file);
                }} />
              </Button>
              {form.logo_b64 && <Button size="small" color="inherit" onClick={() => setForm({ ...form, logo_b64: '' })}>Remove</Button>}
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save org'}</Button>
            </Stack>
          </Stack>
          {msg && <Typography variant="body2" sx={{ mt: 1.5 }} color={msg.startsWith('Saved') ? 'success.main' : 'text.secondary'}>{msg}</Typography>}
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Orgs ({orgs.length})</Typography>
      <Stack spacing={1}>
        {orgs.map((o) => (
          <Card key={o.id} variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: o.primary_color }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.display_name || o.name}</Typography>
                <Chip size="small" variant="outlined" label={`code: ${o.code}`} />
                <Chip size="small" variant="outlined" label={`${o.member_count} members`} />
                {o.has_logo && <Chip size="small" variant="outlined" label="logo" />}
                {o.self_serve && <Chip size="small" color="info" variant="outlined" label="self-serve" />}
                {o.active ? (
                  <Chip size="small" color="success" variant="outlined" label="active" />
                ) : (
                  <Chip size="small" color="warning" label="inactive" />
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant={o.active ? 'text' : 'contained'} color={o.active ? 'inherit' : 'success'}
                  onClick={() => toggleActive(o)}>
                  {o.active ? 'Deactivate' : 'Activate'}
                </Button>
              </Stack>
              {o.self_serve && o.billing_email && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  billing: {o.billing_email}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}

// Admin-only help-chat transcript review (reachable at /?admin=support), gated by
// the same admin token. For spotting product issues from what users ask.
function SupportLogsPanel() {
  const [token, setToken] = useState(localStorage.getItem('ra_admin_token') || '');
  const [scope, setScope] = useState<'all' | 'ontopic' | 'offtopic' | 'blocked'>('all');
  const [rows, setRows] = useState<any[]>([]);
  const [msg, setMsg] = useState('');
  const load = async (sc = scope) => {
    setMsg('');
    try {
      const q = sc === 'all' ? '' : `&scope=${sc}`;
      const r = await axios.get(`${API_BASE_URL}/api/admin/support-logs?limit=200${q}`, { headers: { 'X-Admin-Token': token } });
      setRows(r.data || []);
      localStorage.setItem('ra_admin_token', token);
    } catch {
      setMsg('Invalid admin token, or load failed.');
    }
  };
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>Help-chat transcripts — Admin</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="password" label="Admin token" value={token}
          onChange={(e) => setToken(e.target.value)} sx={{ minWidth: 260 }} />
        <ToggleButtonGroup exclusive size="small" value={scope}
          onChange={(_, v) => { if (v) { setScope(v); load(v); } }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="ontopic">On-topic</ToggleButton>
          <ToggleButton value="offtopic">Off-topic</ToggleButton>
          <ToggleButton value="blocked">Blocked</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="outlined" onClick={() => load()} disabled={!token.trim()}>Load</Button>
      </Stack>
      {msg && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{msg}</Typography>}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{rows.length} message(s)</Typography>
      <Stack spacing={1}>
        {rows.map((r, i) => (
          <Card key={i} variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                <Chip size="small" color={r.on_topic ? 'success' : 'warning'} variant="outlined"
                  label={r.on_topic ? 'on-topic' : 'off-topic'} />
                {r.blocked && <Chip size="small" color="error" label="blocked" />}
                <Typography variant="caption" color="text.secondary">{new Date(r.created_at).toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">· {r.ip}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Q: {r.message}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>A: {r.reply}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}

// Self-serve org admin for consulting firms: create + manage your firm's branded
// org (cookie-authed, no admin token). Shown in Settings. A new org starts
// INACTIVE — its code only grants Enterprise once we activate it (billing gate).
function OrgAdminPanel({ isMember }: { isMember: boolean }) {
  const blank = {
    name: '', code: '', display_name: '', primary_color: '#0f62fe',
    logo_b64: '', disclaimer: '', billing_email: '', seats: 0,
  };
  const [data, setData] = useState<any | null>(null); // { owner, org? }
  const [form, setForm] = useState<any>(blank);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const fill = (org: any) => ({
    name: org.name || '', code: org.code || '', display_name: org.display_name || '',
    primary_color: org.primary_color || '#0f62fe', logo_b64: org.logo_b64 || '',
    disclaimer: org.disclaimer || '', billing_email: org.billing_email || '', seats: org.seats || 0,
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/org/admin`);
      setData(res.data);
      if (res.data?.owner && res.data.org) setForm(fill(res.data.org));
    } catch {
      /* not signed in / no org */
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (create: boolean) => {
    setBusy(true);
    setMsg('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/org/${create ? 'create' : 'update'}`, form);
      setData(res.data);
      if (res.data?.org) setForm(fill(res.data.org));
      setCreating(false);
      setMsg(create ? 'Created — pending activation.' : 'Saved.');
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const onLogo = (file?: File) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setForm((f: any) => ({ ...f, logo_b64: String(r.result || '') }));
    r.readAsDataURL(file);
  };

  const startBilling = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/org/subscribe`, {});
      if (res.data?.url) window.location.href = res.data.url;
      else setBusy(false);
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  const manageBilling = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/org/portal`, {});
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Could not open billing.');
    }
  };

  const fields = (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Firm name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ flex: 1, minWidth: 180 }} />
        <TextField size="small" label="Header / display name" value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })} sx={{ flex: 1, minWidth: 180 }} />
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField size="small" label="Join code (employees enter this)" value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })} sx={{ flex: 1, minWidth: 200 }} />
        <TextField size="small" type="color" label="Accent" value={form.primary_color}
          onChange={(e) => setForm({ ...form, primary_color: e.target.value })} sx={{ width: 90 }} InputLabelProps={{ shrink: true }} />
      </Stack>
      <TextField size="small" label="Billing contact email" value={form.billing_email}
        onChange={(e) => setForm({ ...form, billing_email: e.target.value })} />
      <TextField size="small" label="Footer disclaimer (optional)" value={form.disclaimer}
        onChange={(e) => setForm({ ...form, disclaimer: e.target.value })} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" component="label">
          {form.logo_b64 ? 'Replace logo' : 'Upload logo'}
          <input hidden type="file" accept="image/*" onChange={(e) => onLogo(e.target.files?.[0])} />
        </Button>
        {form.logo_b64 && (
          <>
            <img src={form.logo_b64} alt="logo" style={{ height: 28 }} />
            <Button size="small" color="inherit" onClick={() => setForm({ ...form, logo_b64: '' })}>Remove</Button>
          </>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Only use a company name/logo you're authorized to represent.
      </Typography>
    </Stack>
  );

  if (!data) return null;

  // Owner view: manage the firm's org.
  if (data.owner && data.org) {
    const org = data.org;
    return (
      <Card sx={{ mb: 3 }} data-tour="org-setup">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
              Your firm — {org.name}
            </Typography>
            <Chip size="small" label={org.active ? 'Active' : 'Pending activation'}
              color={org.active ? 'success' : 'warning'} variant="outlined" />
          </Stack>
          {!org.active && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Your org is set up but <b>not active yet</b> — the join code won't grant access until
              billing is active.
              {data.billing_available ? (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" color="warning" disabled={busy} onClick={startBilling}>
                    {busy ? 'Starting…' : `Activate billing — $${data.monthly_price}/mo`}
                  </Button>
                </Box>
              ) : (
                <> We'll reach out at <b>{org.billing_email || 'your billing email'}</b> to activate.</>
              )}
            </Alert>
          )}
          {org.active && data.billing_available && org.sub_status && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="success.main">Billing active (${data.monthly_price}/mo)</Typography>
              <Button size="small" variant="outlined" onClick={manageBilling}>Manage billing</Button>
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            Employees join with code <b style={{ fontFamily: 'monospace' }}>{org.code}</b> — they get
            unlimited usage (your firm is billed) and a résumé branded as <b>{org.display_name}</b>.
          </Typography>
          {fields}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Button variant="contained" disabled={busy} onClick={() => save(false)}>
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {org.member_count} member{org.member_count === 1 ? '' : 's'}
            </Typography>
            {msg && <Typography variant="body2" color={msg === 'Saved.' ? 'success.main' : 'text.secondary'}>{msg}</Typography>}
          </Stack>
          {org.members?.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>MEMBERS</Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {org.members.slice(0, 50).map((m: any, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary">{m.email}</Typography>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // Non-owner: offer to set up a firm (hidden for employees who already joined one).
  if (isMember) return null;
  return (
    <Card sx={{ mb: 3 }} data-tour="org-setup">
      <CardContent>
        <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 0.5 }}>
          Run a consulting firm?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set up a branded org so your people get unlimited usage and résumés styled with your
          firm's brand. You create a join code; we activate billing and your team redeems it.
        </Typography>
        {!creating ? (
          <Button variant="outlined" sx={{ mt: 1.5 }} onClick={() => setCreating(true)}>
            Set up your firm
          </Button>
        ) : (
          <>
            {fields}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <Button variant="contained" disabled={busy || !form.name.trim() || !form.code.trim()} onClick={() => save(true)}>
                {busy ? 'Creating…' : 'Create organization'}
              </Button>
              <Button color="inherit" disabled={busy} onClick={() => setCreating(false)}>Cancel</Button>
              {msg && <Typography variant="body2" color="text.secondary">{msg}</Typography>}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Floating help widget (all pages): a 3-step getting-started + key FAQs + an AI
// help chat (Claude, scoped to ResumeAlign; off-topic misuse is blocked server-side).
const HELP_FAQS: { q: string; a: string }[] = [
  { q: 'How do I optimize my resume?', a: 'On the home tab, upload your resume PDF (or paste it), then add the job — paste a job link or the job text — and click Optimize. You get a tailored, ATS-friendly resume + a match score, then can download a formatted PDF.' },
  { q: 'What do credits cost?', a: 'One credit per billable action (optimize, formatted PDF, or auto-apply). Free includes 10/mo; paid plans include more. See Settings → Plans.' },
  { q: 'What is auto-pilot?', a: 'With the Chrome extension, auto-pilot searches Dice, keeps remote roles, tailors a resume for each, and submits applications up to your daily cap. Set it up in the Job Search tab.' },
  { q: 'Can my company brand the resumes?', a: 'Yes — in Settings, “Run a consulting firm?” lets you set up a branded org with a join code. Employees redeem it for unlimited usage and firm-branded resumes.' },
];

// Whitelisted UI targets the help assistant can drive (tab to switch to + the
// optional [data-tour] element to scroll-to and highlight). Mirrors the backend.
// tab to switch to; `step` is the optimize-wizard step that reveals the target
// (resume = step 1, job/optimize = step 2), since each step gates its DOM.
const HELP_TARGETS: Record<string, { tab: number; step?: 1 | 2 }> = {
  optimize: { tab: 0, step: 1 },
  'job-input': { tab: 0, step: 2 },
  'resume-input': { tab: 0, step: 1 },
  'optimize-button': { tab: 0, step: 2 },
  history: { tab: 1 },
  'job-search': { tab: 2 },
  'run-autopilot': { tab: 2 },
  plans: { tab: 4 },
  'org-setup': { tab: 4 },
  'byok-key': { tab: 4 },
};
// Per-target fallback instruction if the assistant omits step text.
const HELP_FALLBACK: Record<string, string> = {
  optimize: 'Open the Optimize tab.',
  'job-input': 'Paste the job link or job description here.',
  'resume-input': 'Upload your resume PDF (or paste the text) here.',
  'optimize-button': 'Click Optimize to generate your tailored resume.',
  history: 'Your past optimizations are saved here.',
  'job-search': 'Set up auto-pilot here.',
  'run-autopilot': 'Run an auto-pilot pass now.',
  plans: 'View or change your plan here.',
  'org-setup': "Set up your firm's branded org here.",
  'byok-key': 'Connect your Anthropic API key here.',
};

type TourStep = { target: string; tab: number; text: string; wizStep?: 1 | 2 };

// A step-through coach-mark tour: switches to each step's tab (and optimize-wizard
// step), outlines the target element, and pins a NUMBERED callout (with an arrow).
function GuidedTour({ steps, navigate, onClose }: { steps: TourStep[]; navigate: (tab: number, wizStep?: 1 | 2) => void; onClose: () => void }) {
  const [i, setI] = useState(0);
  const [box, setBox] = useState<{ top: number; left: number; placement: 'above' | 'below' | 'center' } | null>(null);
  const step = steps[i];
  const W = 300;

  useEffect(() => {
    let cancelled = false;
    const clear = () => document.querySelectorAll('[data-tour]').forEach((e: any) => { e.style.outline = ''; e.style.outlineOffset = ''; });
    clear();
    navigate(step.tab, step.wizStep);
    const place = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
      if (!el) { if (!cancelled) setBox({ top: 90, left: Math.max(12, (window.innerWidth - W) / 2), placement: 'center' }); return; }
      el.style.outline = '3px solid #ff7a18';
      el.style.outlineOffset = '2px';
      const r = el.getBoundingClientRect();
      const below = r.bottom + 14;
      const placement: 'above' | 'below' = (below + 150 > window.innerHeight && r.top > 170) ? 'above' : 'below';
      const top = placement === 'below' ? below : Math.max(12, r.top - 164);
      const left = Math.max(12, Math.min(r.left, window.innerWidth - W - 12));
      if (!cancelled) setBox({ top, left, placement });
    };
    const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(place, 400);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { cancelled = true; clearTimeout(t); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  if (!box) return null;
  const arrowUp = box.placement === 'below';
  return (
    <Paper elevation={12} sx={{ position: 'fixed', zIndex: 2000, width: W, top: box.top, left: box.left, p: 2, borderRadius: 2, border: '2px solid #ff7a18' }}>
      {box.placement !== 'center' && (
        <Box sx={{
          position: 'absolute', left: 24, width: 0, height: 0,
          borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
          ...(arrowUp ? { top: -8, borderBottom: '8px solid #ff7a18' } : { bottom: -8, borderTop: '8px solid #ff7a18' }),
        }} />
      )}
      <Typography variant="caption" sx={{ color: '#ff7a18', fontWeight: 700 }}>Step {i + 1} of {steps.length}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>{step.text}</Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button size="small" color="inherit" onClick={onClose}>Skip</Button>
        <Stack direction="row" spacing={1}>
          <Button size="small" disabled={i === 0} onClick={() => setI(i - 1)}>Back</Button>
          {i < steps.length - 1
            ? <Button size="small" variant="contained" onClick={() => setI(i + 1)}>Next</Button>
            : <Button size="small" variant="contained" onClick={onClose}>Done</Button>}
        </Stack>
      </Stack>
    </Paper>
  );
}

function HelpWidget({ navigate }: { navigate: (tab: number, wizStep?: 1 | 2) => void }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'guide' | 'chat'>('guide');
  const [msgs, setMsgs] = useState<{ role: 'user' | 'assistant'; content: string; actions?: any[] }[]>([
    { role: 'assistant', content: "Hi! I'm the ResumeAlign helper. Ask me how to optimize a resume, about plans, or auto-pilot — I can walk you through it right on the page." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [tourSteps, setTourSteps] = useState<TourStep[] | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (view === 'chat') endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, view]);

  // Turn the assistant's actions into a step-through guided tour of the page.
  const startTour = (actions: any[]) => {
    const steps: TourStep[] = (actions || [])
      .map((a) => {
        const t = HELP_TARGETS[a?.target];
        if (!t) return null;
        return { target: a.target, tab: t.tab, wizStep: t.step, text: a.text || HELP_FALLBACK[a.target] || '' };
      })
      .filter(Boolean) as TourStep[];
    if (steps.length) {
      setOpen(false); // collapse the panel so the callouts/highlights are visible
      setTourSteps(steps);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || blocked) return;
    const history = msgs.slice(-6).map(({ role, content }) => ({ role, content }));
    setMsgs((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/support/chat`, { message: text, history });
      const actions = Array.isArray(res.data?.actions) ? res.data.actions : [];
      setMsgs((m) => [...m, { role: 'assistant', content: res.data?.reply || '…', actions }]);
      if (res.data?.blocked) setBlocked(true);
      if (actions.length) startTour(actions); // launch the guided walkthrough
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Sorry, I had trouble responding. Please try again.';
      setMsgs((m) => [...m, { role: 'assistant', content: detail }]);
      if (e?.response?.status === 403) setBlocked(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {tourSteps && (
        <GuidedTour steps={tourSteps} navigate={navigate} onClose={() => setTourSteps(null)} />
      )}
      <Fab
        color="primary"
        aria-label="Help"
        onClick={() => setOpen((o) => !o)}
        sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1300 }}
      >
        {open ? <Close /> : <HelpOutline />}
      </Fab>
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 84, right: 20, zIndex: 1300,
            width: { xs: 'calc(100vw - 40px)', sm: 360 }, maxHeight: '70vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 2,
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography sx={{ fontWeight: 700 }}>Need a hand?</Typography>
          </Box>
          <ToggleButtonGroup
            exclusive size="small" value={view}
            onChange={(_, v) => v && setView(v)}
            sx={{ p: 1, justifyContent: 'center' }}
          >
            <ToggleButton value="guide">Getting started</ToggleButton>
            <ToggleButton value="chat">Chat</ToggleButton>
          </ToggleButtonGroup>
          <Divider />

          {view === 'guide' ? (
            <Box sx={{ p: 2, overflowY: 'auto' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Start in 3 steps</Typography>
              <Box component="ol" sx={{ pl: 2.5, mb: 2, color: 'text.secondary' }}>
                <li><Typography variant="body2">Upload your resume PDF (or paste the text).</Typography></li>
                <li><Typography variant="body2">Paste a job link (or the job description text).</Typography></li>
                <li><Typography variant="body2">Click <b>Optimize</b> — then download your tailored, formatted PDF.</Typography></li>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>FAQs</Typography>
              {HELP_FAQS.map((f) => (
                <Accordion key={f.q} disableGutters sx={{ background: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, minHeight: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <Typography variant="body2" color="text.secondary">{f.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Button fullWidth variant="outlined" startIcon={<ChatBubbleOutline />} sx={{ mt: 2 }} onClick={() => setView('chat')}>
                Ask the help assistant
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                <Stack spacing={1}>
                  {msgs.map((m, i) => (
                    <Box
                      key={i}
                      sx={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                        color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                        px: 1.5, py: 1, borderRadius: 2, maxWidth: '85%',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                      {m.role === 'assistant' && m.actions && m.actions.length > 0 && (
                        <Button size="small" variant="outlined" startIcon={<RocketLaunch sx={{ fontSize: 14 }} />}
                          sx={{ mt: 0.75 }} onClick={() => startTour(m.actions!)}>
                          Show me on the page
                        </Button>
                      )}
                    </Box>
                  ))}
                  {busy && <CircularProgress size={18} sx={{ alignSelf: 'flex-start', ml: 1 }} />}
                  <div ref={endRef} />
                </Stack>
              </Box>
              <Divider />
              <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                <TextField
                  size="small" fullWidth placeholder={blocked ? 'Chat unavailable' : 'Ask about ResumeAlign…'}
                  value={input} disabled={busy || blocked}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                />
                <Button variant="contained" disabled={busy || blocked || !input.trim()} onClick={send}>
                  <Send fontSize="small" />
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}
    </>
  );
}

function App() {
  const [jobInputType, setJobInputType] = useState<'url' | 'text'>('url');
  const [jobUrl, setJobUrl] = useState('');
  const [jobText, setJobText] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  // Optimize is a 2-step wizard: 1 = resume (easiest first), 2 = the job posting.
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const prevResumeFilledRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<OptimizationResult | null>(null);
  // Restore the active tab from the URL (?tab=N) so a browser refresh keeps you
  // where you were instead of bouncing back to the optimizer.
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(new URLSearchParams(window.location.search).get('tab') || '', 10);
    return Number.isInteger(t) && t >= 0 && t <= 6 ? t : 0;
  });
  const [history, setHistory] = useState<OptimizationResult[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [apSessions, setApSessions] = useState<AutopilotSession[]>([]);
  const [historyQuery, setHistoryQuery] = useState('');
  const [usage, setUsage] = useState<{
    period_start: string;
    plan_name: string;
    monthly_credits: number;
    credits_used: number;
    credits_remaining: number;
    overage_credits: number;
    credit_value: number;
    revenue_usd: number;
    llm_cost_usd: number;
    profit_usd: number;
    margin_pct: number | null;
    breakdown: { optimize: number; formatted_pdf: number; interview_prep: number; interview_voice_review: number };
  } | null>(null);
  // Enterprise org membership (branded resumes).
  const [org, setOrg] = useState<{ member: boolean; org?: { display_name?: string; logo_b64?: string; primary_color?: string } } | null>(null);
  const [orgCode, setOrgCode] = useState('');
  const [orgMsg, setOrgMsg] = useState('');
  const [orgBusy, setOrgBusy] = useState(false);
  // Branded-resume dialog.
  const [brandedOptId, setBrandedOptId] = useState<string | null>(null);
  const [brandedCompany, setBrandedCompany] = useState('');
  const [brandedColor, setBrandedColor] = useState('#0f62fe');
  const [brandedLogo, setBrandedLogo] = useState('');
  const [brandedBusy, setBrandedBusy] = useState(false);
  const [brandedMsg, setBrandedMsg] = useState('');
  // Ids of optimization records that re-optimized a job already optimized earlier
  // (same job URL). The newest per URL is kept; older repeats are flagged. These
  // are pre-fix duplicate runs (before the worker learned to reuse an existing
  // optimization). Computed over the loaded history only.
  const duplicateOptIds = useMemo(() => {
    const dup = new Set<string>();
    const seen = new Set<string>();
    for (const h of history) {
      const u = h.job_url;
      if (!u || u === 'Pasted job content') continue;
      if (seen.has(u)) dup.add(h.id);
      else seen.add(u);
    }
    return dup;
  }, [history]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [honeypot, setHoneypot] = useState(''); // bot trap — real users never fill this
  const [streamingContent, setStreamingContent] = useState('');
  const [useStreaming, setUseStreaming] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [tempJobPostingContent, setTempJobPostingContent] = useState('');
  const [formattedLoading, setFormattedLoading] = useState(false);
  const [formattedError, setFormattedError] = useState('');
  const [formattedProgress, setFormattedProgress] = useState(0);
  const [formattedStatus, setFormattedStatus] = useState('');
  const [formattedCost, setFormattedCost] = useState<number | null>(null);
  // Finished formatted PDF held in memory so the user can rename it before
  // saving (and re-save without paying for another rebuild).
  const [formattedPdfB64, setFormattedPdfB64] = useState<string | null>(null);
  const [formattedFilename, setFormattedFilename] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [sessionCost, setSessionCost] = useState(0);
  const [balance, setBalance] = useState<BalanceInfo | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planActionId, setPlanActionId] = useState<string | null>(null);
  const [anthropicKey, setAnthropicKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [keyMsg, setKeyMsg] = useState('');
  const saveAnthropicKey = async (clear = false) => {
    setSavingKey(true);
    setKeyMsg('');
    try {
      await axios.post(`${API_BASE_URL}/api/account/anthropic-key`, {
        api_key: clear ? '' : anthropicKey,
      });
      setAnthropicKey('');
      setKeyMsg(clear ? 'Key removed.' : 'Key connected — your optimizations now run on your Anthropic account.');
      await fetchBalance();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setKeyMsg((typeof d === 'string' ? d : d?.message) || 'Could not save the key.');
    } finally {
      setSavingKey(false);
    }
  };
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [topUpCurrency, setTopUpCurrency] = useState<'usd' | 'eur'>('usd');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState('');
  // Which action was blocked by a 402, so we show the top-up call-to-action
  // right next to that action instead of only at the top of the page.
  const [topUpContext, setTopUpContext] = useState<null | 'optimize' | 'rebuild'>(null);
  const [topUpMessage, setTopUpMessage] = useState('');
  // Browser-extension connection: a freshly minted bearer token to paste into
  // the extension (shown once).
  const [extToken, setExtToken] = useState<string | null>(null);
  const [extLoading, setExtLoading] = useState(false);
  const [extDetected, setExtDetected] = useState(false);   // bridge content script present
  const [extConnected, setExtConnected] = useState(false); // token pushed into the extension
  const [extVersion, setExtVersion] = useState<{ version: string; built: string } | null>(null);
  const [extInstalledVersion, setExtInstalledVersion] = useState(''); // version the connected extension reports
  const [extError, setExtError] = useState('');
  // MCP "Connect AI Agent" tab: a freshly minted agent token (separate label
  // from the extension's, so the two don't revoke each other). Shown once.
  const [mcpToken, setMcpToken] = useState<string | null>(null);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [mcpError, setMcpError] = useState('');
  const [mcpNeedsPower, setMcpNeedsPower] = useState(false);
  const [mcpCopied, setMcpCopied] = useState('');
  // Job Search tab
  const [jobLeads, setJobLeads] = useState<JobLead[]>([]);
  // Indeed Job Search tab
  const [indeedKw, setIndeedKw] = useState('data engineer');
  const [indeedBusy, setIndeedBusy] = useState(false);
  // LinkedIn Job Search tab
  const [linkedinKw, setLinkedinKw] = useState('data engineer');
  const [linkedinBusy, setLinkedinBusy] = useState(false);
  // ZipRecruiter Job Search tab
  const [ziprecruiterKw, setZiprecruiterKw] = useState('data engineer');
  const [ziprecruiterBusy, setZiprecruiterBusy] = useState(false);
  const [leadSort, setLeadSort] = useState<'new' | 'score'>('new');
  const [leadPage, setLeadPage] = useState(0);
  const [leadDownloadId, setLeadDownloadId] = useState<string | null>(null);
  // Interview Prep tab: cached preps by lead id + in-flight / ad-hoc state.
  // undefined = not checked yet, null = checked & none saved, object = the prep.
  const [interviewPreps, setInterviewPreps] = useState<Record<string, InterviewPrepResult | null>>({});
  const [prepLoadingId, setPrepLoadingId] = useState<string | null>(null);
  const [prepUrl, setPrepUrl] = useState('');
  const [prepUrlLoading, setPrepUrlLoading] = useState(false);
  const [interviewHistory, setInterviewHistory] = useState<InterviewHistoryItem[]>([]);
  // Dice auto-pilot config (job_search_prefs). Stored whole so saving doesn't
  // clobber daily-routine fields.
  const [prefs, setPrefs] = useState<any>(null);
  const [autopilotSaving, setAutopilotSaving] = useState(false);
  const [autopilotRunningNow, setAutopilotRunningNow] = useState(false);
  // Backend-reported run state (polled), so Stop shows even after a page refresh
  // or when a prior run's batch is still finishing in the background.
  const [autopilotActive, setAutopilotActive] = useState(false);
  const [autopilotRunMsg, setAutopilotRunMsg] = useState('');
  const [runMenuAnchor, setRunMenuAnchor] = useState<null | HTMLElement>(null);
  // New (not-already-seen) jobs this run, from the kickoff ack's `started`.
  // `found` counts the whole search incl. dups; this is the meaningful number.
  // A ref (not state) so the once-registered message handler reads the current
  // value when the final result arrives in a later event.
  const autopilotNewRef = useRef<number | null>(null);
  // Job spec (description) per lead, lazy-loaded when expanded on the prep page.
  const [jobSpecs, setJobSpecs] = useState<Record<string, string | null>>({});
  const [jobSpecLoadingId, setJobSpecLoadingId] = useState<string | null>(null);
  const [hasBaseResume, setHasBaseResume] = useState(false);
  const [hasBaseResumePdf, setHasBaseResumePdf] = useState(false);
  const [baseResumeFilename, setBaseResumeFilename] = useState<string | null>(null);
  const [leadPdfId, setLeadPdfId] = useState<string | null>(null);
  const [baseResumeDraft, setBaseResumeDraft] = useState('');
  const [editingBaseResume, setEditingBaseResume] = useState(false);
  // Résumé Profiles: one truthful framing per role-type, auto-picked per job.
  const [baseProfiles, setBaseProfiles] = useState<BaseProfile[]>([]);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [profileEditId, setProfileEditId] = useState<string | null>(null); // null = new profile
  const [profileLabelDraft, setProfileLabelDraft] = useState('');
  const [profileKeywordsDraft, setProfileKeywordsDraft] = useState('');
  const [profileMasterDraft, setProfileMasterDraft] = useState(false); // editor toggle: is this the master CV?
  const [savingProfile, setSavingProfile] = useState(false);
  const [generatingProfiles, setGeneratingProfiles] = useState(false); // role-profiles-from-master in flight
  const [jobKeywords, setJobKeywords] = useState('');
  const [jobLocation, setJobLocation] = useState('');
  const [jobMinSalary, setJobMinSalary] = useState('');
  const [seedText, setSeedText] = useState('');
  const [jobError, setJobError] = useState('');
  const [jobNotice, setJobNotice] = useState('');
  const [autoApply, setAutoApply] = useState<AutoApplyState | null>(null);
  const [applyStats, setApplyStats] = useState<{ date: string; count: number }[]>([]);
  const [leaderboard, setLeaderboard] = useState<{
    tiers: { plan: string; plan_name: string; best_daily: number }[];
    you: { plan: string; today: number; best: number };
  } | null>(null);
  // Multi-select + sequential mass-apply
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [massDelaySec, setMassDelaySec] = useState(30);
  const [massApply, setMassApply] = useState<{
    items: {
      title: string; status: AutoStepStatus; detail?: string; url?: string; reason?: string;
      stages?: { optimize: AutoStepStatus; pdf: AutoStepStatus; apply: AutoStepStatus };
    }[];
    note: string;
    phase?: 'optimizing' | 'applying';
  } | null>(null);
  const [massOpen, setMassOpen] = useState(false);
  const massFinishedNote = (n?: string) =>
    n === 'Done.' || n === 'Stopped.' || !!n?.startsWith('Could not connect');
  const massBusy = !!massApply && !massFinishedNote(massApply.note);
  const [showGuide, setShowGuide] = useState(
    () => localStorage.getItem('ra_hide_guide') !== '1'
  );
  const formTopUpRef = useRef<HTMLDivElement>(null);
  const resultTopUpRef = useRef<HTMLDivElement>(null);
  const formattedBtnRef = useRef<HTMLButtonElement>(null);

  const dismissGuide = () => {
    setShowGuide(false);
    localStorage.setItem('ra_hide_guide', '1');
  };

  // Surface an out-of-credits (HTTP 402) response next to the action the user
  // just took, and scroll it into view so it's never missed.
  const handlePaymentRequired = (
    detail: any,
    context: 'optimize' | 'rebuild',
    ref: React.RefObject<HTMLDivElement | null>
  ) => {
    if (detail && typeof detail === 'object') setBalance(detail);
    setTopUpMessage(
      (detail && detail.message) ||
        "You've used all your free tries. Add balance to continue."
    );
    setTopUpContext(context);
    setTimeout(
      () => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      150
    );
  };

  // Reusable top-up call-to-action shown inline at the point of action.
  const renderTopUp = (ref: React.RefObject<HTMLDivElement | null>) => (
    <Box ref={ref} sx={{ mt: 2 }}>
      <Alert severity="warning" icon={<Info fontSize="inherit" />}>
        <AlertTitle sx={{ fontWeight: 600 }}>
          {`You've used your ${balance?.monthly_credits ?? balance?.free_tries_total ?? 5} monthly credits`}
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {topUpMessage}{' '}
          {balance?.allow_overage
            ? 'Add prepaid credit to keep going this month.'
            : 'Upgrade your plan for more monthly credits.'}
          {balance && balance.balance_usd > 0
            ? ` Current balance: ${formatMoney(balance.balance_usd, balance.currency)}.`
            : ''}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {balance?.allow_overage ? (
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={topUpCurrency}
                onChange={(e, v) => v && setTopUpCurrency(v)}
              >
                <ToggleButton value="usd">$</ToggleButton>
                <ToggleButton value="eur">€</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" color="warning" onClick={startTopUp} disabled={topUpLoading}>
                {topUpLoading
                  ? 'Redirecting…'
                  : `Add ${topUpCurrency === 'eur' ? '€30' : '$30'} (${balance?.credits_per_topup ?? 120} credits)`}
              </Button>
            </>
          ) : (
            <Button variant="contained" color="warning" onClick={() => handleTabChange(null, 4)}>
              View plans &amp; upgrade
            </Button>
          )}
        </Stack>
      </Alert>
    </Box>
  );

  const addToSessionCost = (cost?: number) => {
    if (typeof cost === 'number' && cost > 0) {
      setSessionCost((prev) => prev + cost);
    }
  };

  const fetchBalance = async () => {
    try {
      // The query string carries first-touch attribution + the assigned theme
      // A/B variant so the backend records both when it creates the account on
      // this first call.
      const response = await axios.get(
        `${API_BASE_URL}/api/billing/balance${experimentQuery(attributionQuery())}`
      );
      setBalance(response.data);
    } catch (err) {
      console.error('Failed to fetch balance', err);
    }
  };

  const startTopUp = async () => {
    setTopUpLoading(true);
    setPaymentNotice('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/billing/create-checkout-session`, {
        units: 1,
        currency: topUpCurrency,
      });
      // Redirect to Stripe's hosted Checkout page.
      window.location.href = response.data.url;
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setPaymentNotice((typeof d === 'string' ? d : d?.message) || 'Could not start checkout. Please try again.');
      setTopUpLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/plans`);
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Failed to fetch plans', err);
    }
  };

  // Start a subscription Checkout for a paid plan, or open the Customer Portal to
  // change/cancel an existing one.
  const subscribe = async (planId: string) => {
    setPlanActionId(planId);
    setPaymentNotice('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/billing/subscribe`, {
        plan: planId,
        interval: billingInterval,
      });
      window.location.href = res.data.url;
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setPaymentNotice((typeof d === 'string' ? d : d?.message) || 'Could not start checkout.');
      setPlanActionId(null);
    }
  };

  const openBillingPortal = async () => {
    setPlanActionId('portal');
    setPaymentNotice('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/billing/portal`, {});
      window.location.href = res.data.url;
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setPaymentNotice((typeof d === 'string' ? d : d?.message) || 'Could not open the billing portal.');
      setPlanActionId(null);
    }
  };

  // Detect the installed extension (its bridge content script announces itself)
  // and listen for its ack after we push a token.
  // Carry a résumé strengthened in the free /ats-score boost wizard into the app
  // so the user doesn't have to redo it. One-time: consumed then cleared.
  useEffect(() => {
    try {
      const boosted = localStorage.getItem('ra_boosted_resume');
      if (boosted && boosted.trim()) {
        setResumeText(boosted);
        localStorage.removeItem('ra_boosted_resume');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window) return;
      const d: any = e.data;
      if (!d || d.source !== 'resumealign-ext') return;
      if (d.type === 'RA_INDEED_SCAN_DONE') {
        setIndeedBusy(false);
        fetchJobLeads();
        setJobNotice(d.found ? `Indeed scan added ${d.found} job(s) to the queue — Apply below.` : 'Indeed scan finished — no new easy-apply jobs found.');
        return;
      }
      if (d.type === 'RA_INDEED_SCAN_FAILED') {
        setIndeedBusy(false);
        setJobError(`Indeed scan failed: ${d.error || 'unknown error'}. Make sure you're signed in to Indeed.`);
        return;
      }
      if (d.type === 'RA_LINKEDIN_SCAN_DONE') {
        setLinkedinBusy(false);
        fetchJobLeads();
        setJobNotice(d.found ? `LinkedIn scan added ${d.found} job(s) to the queue — Apply below.` : 'LinkedIn scan finished — no new Easy Apply jobs found.');
        return;
      }
      if (d.type === 'RA_LINKEDIN_SCAN_FAILED') {
        setLinkedinBusy(false);
        setJobError(`LinkedIn scan failed: ${d.error || 'unknown error'}. Make sure you're signed in to LinkedIn.`);
        return;
      }
      if (d.type === 'RA_ZIPRECRUITER_SCAN_DONE') {
        setZiprecruiterBusy(false);
        fetchJobLeads();
        setJobNotice(d.found ? `ZipRecruiter scan added ${d.found} job(s) to the queue — Apply below.` : 'ZipRecruiter scan finished — no new Quick Apply jobs found.');
        return;
      }
      if (d.type === 'RA_ZIPRECRUITER_SCAN_FAILED') {
        setZiprecruiterBusy(false);
        setJobError(`ZipRecruiter scan failed: ${d.error || 'unknown error'}. Make sure you're signed in to ZipRecruiter.`);
        return;
      }
      if (d.type === 'RA_PONG') {
        setExtDetected(true);
        if (d.connected) setExtConnected(true); // already holds a token — no reconnect needed
        if (d.version) setExtInstalledVersion(String(d.version));
      }
      if (d.type === 'RA_CONNECTED') { setExtDetected(true); setExtConnected(true); }
      if (d.type === 'RA_AUTOPILOT_RESULT') {
        const r = d.result || {};
        let msg: string;
        if (r.ok && r.processing) {
          // Kickoff ack — the backend is tailoring jobs; applies stream in via
          // the mass-apply progress UI. Keep the spinner until the final result.
          if (typeof r.started === 'number') autopilotNewRef.current = r.started;
          if (r.phase === 'tailoring') {
            // Phase 1: backend is optimizing every résumé first (like the manual
            // queue). Render the rich row-per-job view from run_jobs, plus a note.
            // When a batch is in flight, show its incremental "N of M" progress.
            const p = r.progress;
            let note = `Optimizing résumés — ${r.ready ?? 0} ready… applications begin when all are prepared.`;
            if (p && p.total) {
              const label = p.phase === 'pdf' ? 'Building PDFs' : 'Optimizing résumés';
              note = `${label} — ${p.done} of ${p.total}… applications begin when all are prepared.`;
            }
            setAutopilotRunMsg(note);
            const rows = Array.isArray(r.run_jobs)
              ? r.run_jobs.map((j: any) => ({ title: cleanTitle(j.label) || 'this job', status: j.status as AutoStepStatus, detail: j.detail || '', stages: j.stages, url: j.url, reason: j.reason }))
              : [];
            setMassApply({ items: rows, note, phase: 'optimizing' });
            setMassOpen(true);
          } else {
            msg = r.started
              ? `Auto-pilot started — found ${r.found ?? 0} job(s), ${r.started} new; optimizing résumés…`
              : `Auto-pilot — found ${r.found ?? 0} job(s), none new to process.${r.note ? ' ' + r.note : ''}`;
            if (!r.started) setAutopilotRunningNow(false);
            setAutopilotRunMsg(msg);
            // Open the same friendly modal the manual queue uses, seeded so it's
            // not empty while phase 1 runs. RA_MASS_PROGRESS fills the per-job
            // list once the apply phase starts.
            if (r.started) {
              setMassApply((m) =>
                m && m.items.length ? m : { items: [], note: `Optimizing ${r.started} résumé(s) — applications will appear here once all are prepared…` }
              );
              setMassOpen(true);
            }
          }
        } else if (r.ok) {
          setAutopilotRunningNow(false);
          // `found` includes jobs already seen in earlier runs; show how many were
          // actually new (from the kickoff ack) so "applied 1 of 59" makes sense.
          const newCount = autopilotNewRef.current;
          const found = r.found ?? 0;
          const applied = r.applied ?? 0;
          if (newCount != null) {
            // `found` = listings scanned on Dice (includes ones already in your
            // queue); `newCount` = how many were new this run. Showing both stops
            // "found 160, applied 4" from reading as if 156 jobs vanished.
            const seen = Math.max(0, found - newCount);
            msg = `Auto-pilot done — scanned ${found} listing(s)`
              + (seen ? `, ${seen} already in your queue` : '')
              + `, ${newCount} new; applied ${applied}.`;
          } else {
            msg = `Auto-pilot done — scanned ${found} listing(s); applied ${applied}.`;
          }
          // Reconcile the NEW leads: applied + every drop reason. The residual
          // ("other") catches optimize failures / anything uncounted so the
          // numbers always add up to newCount.
          const why: string[] = [];
          if (r.skipped_nonremote) why.push(`${r.skipped_nonremote} not remote`);
          if (r.skipped_expired) why.push(`${r.skipped_expired} expired/closed`);
          if (r.skipped_fake) why.push(`${r.skipped_fake} test/placeholder`);
          if (r.skipped_scrape) why.push(`${r.skipped_scrape} couldn’t load`);
          if (r.skipped_tailor) why.push(`${r.skipped_tailor} optimize failed`);
          if (newCount != null && newCount > 0) {
            const accounted = applied + (r.skipped_nonremote || 0) + (r.skipped_expired || 0)
              + (r.skipped_fake || 0) + (r.skipped_scrape || 0) + (r.skipped_tailor || 0);
            const other = newCount - accounted;
            if (other > 0) why.push(`${other} couldn’t be processed`);
          }
          if (why.length) msg += ` Of the new: ${why.join(', ')}.`;
          if (newCount === 0) msg += ' (Every match was already applied to in an earlier run.)';
          if (r.note) msg += ` ${r.note}`;
          setAutopilotRunMsg(msg);
          // Finalize the modal so it stops showing the "Optimizing — N of M"
          // spinner (massBusy keys off a finished note). On a Stop, say so.
          const finalNote = r.note === 'Stopped.' ? 'Stopped.' : 'Done.';
          setMassApply((m) => (m ? { ...m, note: finalNote } : m));
        } else if (r.error === 'already running') {
          // A run is still in flight — surface the Stop control so the user can
          // halt it (rather than being stuck unable to start a new one).
          setAutopilotRunningNow(true);
          setAutopilotRunMsg('Auto-pilot is already running — click "Stop auto-pilot" to halt it.');
        } else {
          setAutopilotRunningNow(false);
          setAutopilotRunMsg(`Auto-pilot couldn't run: ${r.error || 'unknown error'}`);
        }
        fetchJobLeads();
        fetchBalance();
      }
      if (d.type === 'RA_MASS_PROGRESS' && d.state) {
        // Background worker is driving the mass-apply queue — mirror its state.
        setMassApply({
          note: d.state.note || '',
          phase: 'applying',
          items: (d.state.results || []).map((r: any) => {
            // r.pdf (newer extensions): 'tailored' = the job's tailored PDF was
            // used; 'fallback' = the tailored PDF was unavailable (e.g. its build
            // failed / out of Anthropic credit) so the base résumé was sent.
            // Older extensions don't send it → keep the legacy 'done'.
            const usedFallback = r.pdf && r.pdf !== 'tailored';
            return {
              title: r.label,
              status: r.status as AutoStepStatus,
              detail: usedFallback
                ? `${r.detail ? r.detail + ' · ' : ''}⚠ tailored PDF unavailable — sent base résumé`
                : r.detail,
              url: r.url,
              stages: {
                optimize: 'done' as AutoStepStatus,
                pdf: (usedFallback ? 'error' : 'done') as AutoStepStatus,
                apply: r.status as AutoStepStatus,
              },
            };
          }),
        });
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'resumealign-web', type: 'RA_PING' }, window.location.origin);
    // Version + build date of the packaged extension .zip the site serves.
    fetch('/extension-version.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => v && setExtVersion(v))
      .catch(() => {});
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Mint a bearer token for the browser extension. If the extension is installed,
  // push it straight in (no copy/paste); otherwise the token is shown to paste.
  const connectExtension = async () => {
    setExtLoading(true);
    setExtError('');
    setExtConnected(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/extension-token`);
      const token = response.data.token;
      setExtToken(token);
      // Hand the token + this site's origin to the extension directly.
      window.postMessage(
        { source: 'resumealign-web', type: 'RA_CONNECT', token, apiBase: window.location.origin },
        window.location.origin
      );
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setExtError((typeof d === 'string' ? d : d?.message) || 'Could not generate a token. Please try again.');
    } finally {
      setExtLoading(false);
    }
  };

  const mcpEndpoint = `${window.location.origin}/mcp`;
  const mcpCommand = (token: string) =>
    `claude mcp add --transport http resumealign ${mcpEndpoint} --header "Authorization: Bearer ${token}"`;
  const copyMcp = (what: string, value: string) => {
    navigator.clipboard?.writeText(value);
    setMcpCopied(what);
    window.setTimeout(() => setMcpCopied((c) => (c === what ? '' : c)), 1500);
  };
  const generateAgentToken = async () => {
    setMcpLoading(true);
    setMcpError('');
    setMcpNeedsPower(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/agent-token`);
      setMcpToken(response.data.token);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setMcpNeedsPower(true);
      } else {
        const d = err.response?.data?.detail;
        setMcpError((typeof d === 'string' ? d : d?.message) || 'Could not generate a token. Please try again.');
      }
    } finally {
      setMcpLoading(false);
    }
  };

  // Auto-configure: the moment the extension is detected but not yet connected,
  // mint + push a token automatically — the user never has to click Connect or
  // copy/paste anything. Guarded so it runs at most once per page load.
  const extAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (extDetected && !extConnected && !extLoading && !extAutoConnectedRef.current) {
      extAutoConnectedRef.current = true;
      connectExtension();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extDetected, extConnected]);

  // ---- Job Search tab ----------------------------------------------------
  // Running history of successful auto-applies, bucketed by the user's LOCAL day
  // (backend timestamps are naive UTC, so append 'Z' before parsing).
  const fetchApplyStats = async () => {
    try {
      // Server aggregates per-day counts over ALL applies (no 500 cap), bucketed in
      // the viewer's tz so the running total + days stay accurate as they grow.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const res = await axios.get(`${API_BASE_URL}/api/jobs/apply-stats`, { params: { tz } });
      setApplyStats(res.data.by_day || []);
    } catch (err) {
      console.error('Failed to fetch apply stats', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      // Pass the browser tz so 'best day'/'today' bucket by the user's local day.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const res = await axios.get(
        `${API_BASE_URL}/api/jobs/leaderboard?tz=${encodeURIComponent(tz)}`
      );
      setLeaderboard(res.data || null);
    } catch (err) {
      console.error('Failed to fetch leaderboard', err);
    }
  };

  const fetchJobLeads = async (): Promise<JobLead[]> => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/leads`);
      const leads: JobLead[] = res.data.leads || [];
      setJobLeads(leads);
      setHasBaseResume(!!res.data.has_base_resume);
      setHasBaseResumePdf(!!res.data.has_base_resume_pdf);
      setBaseResumeFilename(res.data.base_resume_filename || null);
      return leads;
    } catch (err) {
      console.error('Failed to fetch job leads', err);
      return [];
    }
  };

  // Ingest a batch of leads (and optionally set/replace the base resume).
  const ingestLeads = async (leads: any[], resumeText?: string) => {
    const body: any = { leads };
    if (resumeText !== undefined) body.resume_text = resumeText;
    const res = await axios.post(`${API_BASE_URL}/api/jobs/leads`, body);
    setJobLeads(res.data.leads || []);
    setHasBaseResume(!!res.data.has_base_resume);
    return res.data;
  };

  const saveBaseResume = async () => {
    setJobError('');
    if (!baseResumeDraft.trim()) {
      setJobError('Paste your resume text first.');
      return;
    }
    try {
      await ingestLeads([], baseResumeDraft);
      setEditingBaseResume(false);
      setJobNotice('Base resume saved — jobs will be scored against it.');
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not save the base resume.');
    }
  };

  const useLatestResume = async () => {
    setJobError('');
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/base-resume/use-latest`);
      setHasBaseResume(true);
      setEditingBaseResume(false);
      setBaseResumeDraft('');
      setJobNotice('Using your most recent resume as the default.');
      await fetchJobLeads(); // reflect the re-ranked queue
      await fetchBaseResumes();
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'No previous resume found to reuse.');
    }
  };

  const uploadBaseResume = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setJobError('');
    const formData = new FormData();
    formData.append('resume_file', file);
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/base-resume/upload`, formData);
      setHasBaseResume(true);
      setEditingBaseResume(false);
      setJobNotice(`Base resume set from ${file.name}.`);
      await fetchJobLeads(); // reflect the re-ranked queue
      await fetchBaseResumes();
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not read that file.');
    }
  };

  // --- Résumé Profiles (multiple base résumés, one per role-type) ---
  const fetchBaseResumes = async (): Promise<BaseProfile[]> => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/base-resumes`);
      const profiles: BaseProfile[] = res.data || [];
      setBaseProfiles(profiles);
      // A master-CV-only account has no usable tailoring base.
      setHasBaseResume(profiles.some((p) => !p.is_master));
      return profiles;
    } catch (err) {
      console.error('Failed to fetch base résumés', err);
      return [];
    }
  };

  const openProfileEditor = (p?: BaseProfile) => {
    setJobError('');
    setProfileEditId(p?.id ?? null);
    setProfileLabelDraft(p?.label ?? '');
    setProfileKeywordsDraft((p?.keywords ?? []).join(', '));
    setProfileMasterDraft(!!p?.is_master);
    setBaseResumeDraft(p?.text ?? '');
    setProfileEditorOpen(true);
  };

  const closeProfileEditor = () => {
    setProfileEditorOpen(false);
    setProfileEditId(null);
    setProfileLabelDraft('');
    setProfileKeywordsDraft('');
    setProfileMasterDraft(false);
    setBaseResumeDraft('');
  };

  const saveProfile = async () => {
    setJobError('');
    if (!profileLabelDraft.trim()) {
      setJobError('Give this résumé a label (e.g. “ML / AI” or “Java backend”).');
      return;
    }
    if (!baseResumeDraft.trim()) {
      setJobError('Paste the résumé text (or use Upload PDF / TXT).');
      return;
    }
    setSavingProfile(true);
    try {
      const body: any = {
        label: profileLabelDraft.trim(),
        text: baseResumeDraft,
        keywords: profileKeywordsDraft.split(',').map((k) => k.trim()).filter(Boolean),
        is_master: profileMasterDraft,
      };
      if (profileEditId) body.id = profileEditId;
      // First NON-master profile becomes the default tailoring base.
      if (!profileMasterDraft && !baseProfiles.some((p) => !p.is_master)) body.is_default = true;
      await axios.post(`${API_BASE_URL}/api/jobs/base-resumes`, body);
      closeProfileEditor();
      setJobNotice(profileMasterDraft
        ? 'Master CV saved — generate role profiles from it below.'
        : 'Résumé saved — jobs are auto-matched to the best-fit résumé.');
      await fetchBaseResumes();
      await fetchJobLeads();
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not save the résumé.');
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadProfile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!profileLabelDraft.trim()) {
      setJobError('Give this résumé a label first, then upload the file.');
      return;
    }
    setJobError('');
    setSavingProfile(true);
    const formData = new FormData();
    formData.append('label', profileLabelDraft.trim());
    formData.append('keywords', profileKeywordsDraft);
    formData.append('resume_file', file);
    formData.append('is_master', profileMasterDraft ? 'true' : 'false');
    if (profileEditId) formData.append('id', profileEditId);
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/base-resumes/upload`, formData);
      closeProfileEditor();
      setJobNotice(profileMasterDraft
        ? `Master CV set from ${file.name} — generate role profiles from it below.`
        : `Résumé set from ${file.name}.`);
      await fetchBaseResumes();
      await fetchJobLeads();
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not read that file.');
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteProfile = async (p: BaseProfile) => {
    setJobError('');
    if (!window.confirm(`Delete the “${p.label}” résumé?`)) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/jobs/base-resumes/${p.id}`);
      if (res.data && res.data.deleted === false) {
        setJobError(res.data.note || 'Could not delete that résumé.');
      } else {
        setJobNotice(`Deleted “${p.label}”.`);
      }
      await fetchBaseResumes();
      await fetchJobLeads();
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not delete that résumé.');
    }
  };

  // Generate focused, truthful role profiles from the saved master CV (several
  // server-side LLM calls — show a spinner).
  const generateRoleProfiles = async () => {
    setJobError('');
    setGeneratingProfiles(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/base-resumes/generate-from-master`, {});
      const n = (res.data?.generated || []).length;
      const note = res.data?.note;
      setJobNotice(
        `Generated ${n} role ${n === 1 ? 'profile' : 'profiles'} from your master CV.` +
          (note ? ` ${note}` : ''),
      );
      await fetchBaseResumes();
      await fetchJobLeads();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setJobError(typeof detail === 'string' ? detail : 'Could not generate role profiles.');
    } finally {
      setGeneratingProfiles(false);
    }
  };

  const searchOnDice = () => {
    const params = new URLSearchParams();
    if (jobKeywords.trim()) params.set('q', jobKeywords.trim());
    if (jobLocation.trim()) params.set('location', jobLocation.trim());
    // Dice's minimum-salary filter param (verified: filters.salary=<amount>).
    if (jobMinSalary.trim()) params.set('filters.salary', jobMinSalary.replace(/[^\d]/g, ''));
    window.open(`https://www.dice.com/jobs?${params.toString()}`, '_blank', 'noopener');
  };

  // Temporary manual seed until the extension's "scan results" lands. Each line:
  // "URL | Title | Company" (title/company optional).
  const seedLeads = async () => {
    setJobError('');
    const leads = seedText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => /^https?:\/\//.test(line))
      .map((line) => {
        const [url, title, company] = line.split('|').map((s) => s.trim());
        return { url, title: title || '', company: company || '' };
      });
    if (!leads.length) {
      setJobError('Add at least one job URL (one per line).');
      return;
    }
    try {
      await ingestLeads(leads);
      setSeedText('');
      setJobNotice(`Added ${leads.length} job${leads.length === 1 ? '' : 's'} to the queue.`);
    } catch (err: any) {
      setJobError(err.response?.data?.detail || 'Could not add jobs.');
    }
  };

  // Download a job's optimized resume (a saved optimization) as text, named for
  // the job so it's easy to identify when uploading to the listing.
  const downloadLeadResume = async (lead: JobLead) => {
    if (!lead.optimization_id) return;
    setLeadDownloadId(lead.id);
    setJobError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/optimizations/${lead.optimization_id}`);
      const tag = [lead.company, lead.title].map(safeFilePart).filter(Boolean).join('_') || 'resume';
      downloadText(res.data.optimized_resume || '', `${tag}.txt`);
    } catch (err: any) {
      setJobError(
        err.response?.status === 404
          ? "That optimized resume isn't on this account — try opening it from the History tab."
          : "Couldn't download the optimized resume — please try again."
      );
    } finally {
      setLeadDownloadId(null);
    }
  };

  // Build/download the layout-matched formatted PDF for a job (uses the base
  // PDF as the style reference, same as the main Optimize page). Costs a credit
  // the first time; cached thereafter.
  const downloadLeadFormatted = async (lead: JobLead) => {
    setLeadPdfId(lead.id);
    setJobError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/formatted-pdf`);
      downloadBase64Pdf(res.data.pdf_base64, res.data.filename || 'resume.pdf');
      fetchBalance();
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setJobError(
        (typeof d === 'string' ? d : d?.message) ||
          'Could not build the formatted PDF — please try again.'
      );
    } finally {
      setLeadPdfId(null);
    }
  };


  const setLeadStatus = async (
    id: string,
    status: 'dismissed' | 'applied',
    dismissReason?: string
  ) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/jobs/leads/${id}`, {
        status,
        ...(dismissReason ? { dismiss_reason: dismissReason } : {}),
      });
      await fetchJobLeads();
    } catch (err) {
      console.error('Failed to update lead', err);
    }
  };

  // Mark (or unmark) that the user has an interview for this job. Display-only
  // on the queue; generating prep also sets this server-side.
  const setLeadInterview = async (id: string, interview: boolean) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/jobs/leads/${id}`, { interview });
      await fetchJobLeads();
    } catch (err) {
      console.error('Failed to update interview flag', err);
    }
  };

  // Generate AI interview prep for a lead (bills a credit; marks it interview).
  const generatePrep = async (leadId: string) => {
    setPrepLoadingId(leadId);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${leadId}/interview-prep`);
      setInterviewPreps((prev) => ({ ...prev, [leadId]: res.data }));
      addToSessionCost(res.data?.cost_usd);
      fetchBalance();
      fetchJobLeads(); // reflect the interview flag the server set
    } catch (err: any) {
      if (err.response?.status === 402) {
        setError("You're out of credits for interview prep — add a plan or top up on the Plans tab.");
      } else {
        setError(err.response?.data?.detail || 'Failed to generate interview prep.');
      }
    } finally {
      setPrepLoadingId(null);
    }
  };

  // Load a previously-saved prep for a lead (no charge). Caches null when none
  // is saved, so the card can show "Prep for interview" vs "Regenerate"
  // correctly and we don't refetch.
  const loadSavedPrep = async (leadId: string) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/leads/${leadId}/interview-prep`);
      setInterviewPreps((prev) => ({ ...prev, [leadId]: res.data }));
    } catch {
      setInterviewPreps((prev) => ({ ...prev, [leadId]: prev[leadId] ?? null }));
    }
  };

  // Lazy-load the stored job description for a lead (no charge), to read while
  // prepping. Caches null when none is stored so we don't refetch.
  const loadJobSpec = async (leadId: string) => {
    if (leadId in jobSpecs || jobSpecLoadingId === leadId) return;
    setJobSpecLoadingId(leadId);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/leads/${leadId}/job-spec`);
      setJobSpecs((prev) => ({ ...prev, [leadId]: res.data?.job_spec || null }));
    } catch {
      setJobSpecs((prev) => ({ ...prev, [leadId]: null }));
    } finally {
      setJobSpecLoadingId(null);
    }
  };

  // Ad-hoc path: the interview is for a job not in the queue. Import it from a
  // URL (creates a tracked lead), mark it interview, then generate prep.
  const addInterviewByUrl = async () => {
    const url = prepUrl.trim();
    if (!url) return;
    setPrepUrlLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/leads`, { leads: [{ url }] });
      const leads: JobLead[] = res.data?.leads || [];
      const match = leads.find((l) => l.url === url) || leads[0];
      setPrepUrl('');
      await fetchJobLeads();
      if (match) await generatePrep(match.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not add that job URL.');
    } finally {
      setPrepUrlLoading(false);
    }
  };

  // --- One-click auto-apply -------------------------------------------------
  // Optimize → build the formatted PDF → open the job listing → hand off to the
  // browser extension to fill + attach + submit. The web app drives the first
  // steps; the extension finishes inside the opened Dice tab.
  const updateAutoStep = (key: string, status: AutoStepStatus, detail?: string) =>
    setAutoApply((s) =>
      s
        ? { ...s, steps: s.steps.map((st) => (st.key === key ? { ...st, status, detail: detail ?? st.detail } : st)) }
        : s
    );

  // Ask the extension (via the page bridge) to auto-apply on the given job, and
  // wait for it to confirm the request was queued.
  const postAutoApplyIntent = (url: string) =>
    new Promise<void>((resolve, reject) => {
      const onMsg = (e: MessageEvent) => {
        const d = e.data;
        if (!d || d.source !== 'resumealign-ext') return;
        if (d.type === 'RA_AUTOAPPLY_QUEUED') {
          cleanup();
          resolve();
        } else if (d.type === 'RA_AUTOAPPLY_FAILED') {
          cleanup();
          reject(new Error(d.error || 'extension could not queue the request'));
        }
      };
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener('message', onMsg);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('extension did not respond — is it installed and connected?'));
      }, 6000);
      window.addEventListener('message', onMsg);
      window.postMessage({ source: 'resumealign-web', type: 'RA_AUTOAPPLY', url }, window.location.origin);
    });

  // Poll the queue until the extension marks this job applied, then complete the
  // final timeline step. (The actual submit happens in the extension's tab.)
  const pollLeadApplied = (leadId: string) => {
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      const leads = await fetchJobLeads();
      const l = leads.find((x) => x.id === leadId);
      if (l && l.status === 'applied') {
        clearInterval(iv);
        updateAutoStep('submit', 'done', 'Submitted — marked as applied ✅');
        fetchApplyStats();
      } else if (n > 24) {
        clearInterval(iv);
        updateAutoStep('submit', 'active', 'Still applying in the opened tab — it’ll move to Applied when done.');
      }
    }, 5000);
  };

  // Make sure the extension holds a working API token before it acts — a reload
  // or reinstall clears it, which otherwise surfaces as "Connect the extension
  // first." Reuse the token minted this session; mint one only if we have none.
  const ensureExtensionConnected = async () => {
    let token = extToken;
    if (!token) {
      const response = await axios.post(`${API_BASE_URL}/api/auth/extension-token`);
      token = response.data.token;
      setExtToken(token);
    }
    await new Promise<void>((resolve, reject) => {
      const onMsg = (e: MessageEvent) => {
        const d = e.data;
        if (!d || d.source !== 'resumealign-ext') return;
        if (d.type === 'RA_CONNECTED') {
          cleanup();
          resolve();
        } else if (d.type === 'RA_CONNECT_FAILED') {
          cleanup();
          reject(new Error(d.error || 'connect failed'));
        }
      };
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener('message', onMsg);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('extension did not confirm connection'));
      }, 6000);
      window.addEventListener('message', onMsg);
      window.postMessage(
        { source: 'resumealign-web', type: 'RA_CONNECT', token, apiBase: window.location.origin },
        window.location.origin
      );
    });
    setExtConnected(true);
  };

  const autoApplyLead = async (lead: JobLead) => {
    if (balance && balance.experimental === false) {
      setJobError(
        'One-click auto-apply is an experimental feature included on the Free and Pro plans. Upgrade to Pro (Plans) to use it.'
      );
      handleTabChange(null, 4);
      return;
    }
    if (!extDetected) {
      setJobError(
        'Install and connect the ResumeAlign browser extension (Browser Extension tab) to auto-apply.'
      );
      return;
    }
    setAutoApply({
      leadId: lead.id,
      title: cleanTitle(lead.title) || 'this job',
      steps: [
        { key: 'connect', label: 'Connect the browser extension', status: 'pending' },
        { key: 'optimize', label: 'Optimize resume for this job', status: 'pending' },
        { key: 'format', label: 'Generate formatted PDF', status: 'pending' },
        { key: 'open', label: 'Open the job posting', status: 'pending' },
        { key: 'submit', label: 'Fill, attach résumé & submit', status: 'pending' },
      ],
    });
    try {
      // 0 — Make sure the extension has a valid token (clears on reload).
      updateAutoStep('connect', 'active');
      await ensureExtensionConnected();
      updateAutoStep('connect', 'done');
      // 1 — Optimize (skip if this job was already optimized).
      if (lead.optimization_id) {
        updateAutoStep('optimize', 'done', 'Already optimized');
      } else {
        updateAutoStep('optimize', 'active');
        const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/tailor`);
        addToSessionCost(res.data.cost_usd);
        await fetchJobLeads();
        updateAutoStep('optimize', 'done');
      }
      // 2 — Build (and cache) the layout-matched formatted PDF.
      updateAutoStep('format', 'active');
      await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/formatted-pdf`);
      fetchBalance();
      updateAutoStep('format', 'done');
      // 3 — Ask the extension to open the job tab and drive the application.
      updateAutoStep('open', 'active');
      await postAutoApplyIntent(lead.url);
      updateAutoStep('open', 'done', 'Opened by the extension');
      // 4 — The extension fills, attaches and submits in that tab; watch for it.
      updateAutoStep('submit', 'active', 'Applying in the opened tab — watch it fill, attach your résumé and submit.');
      pollLeadApplied(lead.id);
    } catch (err: any) {
      const d = err.response?.data?.detail;
      const msg =
        (typeof d === 'string' ? d : d?.message) || err.message || 'Auto-apply failed.';
      // Mark whichever step was in progress as failed.
      setAutoApply((s) =>
        s
          ? { ...s, steps: s.steps.map((st) => (st.status === 'active' ? { ...st, status: 'error', detail: msg } : st)) }
          : s
      );
      if (err.response?.status === 402) {
        setJobError(
          (typeof d === 'string' ? d : d?.message) ||
            "You're out of free tries — add balance (top-up is in the header) to auto-apply."
        );
      }
    }
  };

  // --- Indeed Job Search ----------------------------------------------------
  const scanIndeed = () => {
    if (!extDetected) { setJobError('Connect the Chrome extension to scan Indeed.'); return; }
    const kw = indeedKw.trim();
    if (!kw) { setJobError('Enter keywords to scan Indeed.'); return; }
    setIndeedBusy(true);
    setJobError('');
    setJobNotice(`Scanning Indeed for “${kw}” (Easily-apply jobs)… opens a background tab, ~30-60s.`);
    // Pure scan → queue. NOT the autopilot (runAutopilotNow drags in the tailor/
    // apply pipeline + the global mass-apply modal). This only adds source='indeed'
    // leads to the queue; tailoring happens on-demand per-row via indeedApplyLead.
    // RA_INDEED_SCAN_DONE / _FAILED clears the busy state and refreshes the queue.
    window.postMessage(
      { source: 'resumealign-web', type: 'RA_INDEED_SCAN_QUEUE', keywords: kw, location: 'Remote' },
      window.location.origin
    );
    // Safety: clear busy if no done-message arrives (e.g. scan tab blocked).
    setTimeout(() => setIndeedBusy((b) => { if (b) { setJobNotice('Scan is taking a while — check the Indeed tab is signed in; results will appear when done.'); } return b; }), 90000);
  };

  // Tailor (+ PDF) the Indeed lead, then have the extension drive the Indeed
  // wizard (it stops at the reCAPTCHA for the user to clear + submit).
  const indeedApplyLead = async (lead: JobLead) => {
    if (!extDetected) { setJobError('Connect the Chrome extension to apply on Indeed.'); return; }
    try {
      setJobNotice(`Preparing “${cleanTitle(lead.title) || 'this job'}” for Indeed…`);
      await ensureExtensionConnected();
      if (!lead.optimization_id) {
        const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/tailor`);
        addToSessionCost(res.data.cost_usd);
      }
      await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/formatted-pdf`);
      await fetchJobLeads();
      window.postMessage({ source: 'resumealign-web', type: 'RA_INDEED_APPLY', url: lead.url }, window.location.origin);
      setJobNotice('Driving the Indeed application — it fills everything, then stops at the reCAPTCHA for you to clear + Submit.');
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setJobError((typeof d === 'string' ? d : d?.message) || err.message || 'Indeed apply failed.');
    }
  };

  // --- LinkedIn Job Search ---------------------------------------------------
  const scanLinkedIn = () => {
    if (!extDetected) { setJobError('Connect the Chrome extension to scan LinkedIn.'); return; }
    const kw = linkedinKw.trim();
    if (!kw) { setJobError('Enter keywords to scan LinkedIn.'); return; }
    setLinkedinBusy(true);
    setJobError('');
    setJobNotice(`Scanning LinkedIn for “${kw}” (Easy Apply jobs)… opens a background tab, ~30-60s.`);
    // Pure scan → queue. NOT the autopilot (runAutopilotNow drags in the tailor/
    // apply pipeline + the global mass-apply modal). This only adds source='linkedin'
    // leads to the queue; tailoring happens on-demand per-row via linkedinApplyLead.
    // RA_LINKEDIN_SCAN_DONE / _FAILED clears the busy state and refreshes the queue.
    window.postMessage(
      { source: 'resumealign-web', type: 'RA_LINKEDIN_SCAN_QUEUE', keywords: kw, location: 'Remote' },
      window.location.origin
    );
    // Safety: clear busy if no done-message arrives (e.g. scan tab blocked).
    setTimeout(() => setLinkedinBusy((b) => { if (b) { setJobNotice('Scan is taking a while — check the LinkedIn tab is signed in; results will appear when done.'); } return b; }), 90000);
  };

  // Tailor (+ PDF) the LinkedIn lead, then have the extension click Easy Apply
  // and drive the modal (it stops at the review step for the user to Submit).
  const linkedinApplyLead = async (lead: JobLead) => {
    if (!extDetected) { setJobError('Connect the Chrome extension to apply on LinkedIn.'); return; }
    try {
      setJobNotice(`Preparing “${cleanTitle(lead.title) || 'this job'}” for LinkedIn…`);
      await ensureExtensionConnected();
      if (!lead.optimization_id) {
        const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/tailor`);
        addToSessionCost(res.data.cost_usd);
      }
      await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/formatted-pdf`);
      await fetchJobLeads();
      window.postMessage({ source: 'resumealign-web', type: 'RA_LINKEDIN_APPLY', url: lead.url }, window.location.origin);
      setJobNotice('Driving the LinkedIn Easy Apply — it fills everything, then stops at the review step for you to Submit.');
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setJobError((typeof d === 'string' ? d : d?.message) || err.message || 'LinkedIn apply failed.');
    }
  };

  // --- ZipRecruiter Job Search ------------------------------------------------
  const scanZipRecruiter = () => {
    if (!extDetected) { setJobError('Connect the Chrome extension to scan ZipRecruiter.'); return; }
    const kw = ziprecruiterKw.trim();
    if (!kw) { setJobError('Enter keywords to scan ZipRecruiter.'); return; }
    setZiprecruiterBusy(true);
    setJobError('');
    setJobNotice(`Scanning ZipRecruiter for “${kw}” (Quick Apply jobs)… opens a background tab; this one clicks through each result to read its share link, so it can take a couple minutes.`);
    // Pure scan → queue. NOT the autopilot (runAutopilotNow drags in the tailor/
    // apply pipeline + the global mass-apply modal). This only adds
    // source='ziprecruiter' leads to the queue; tailoring happens on-demand
    // per-row via ziprecruiterApplyLead. RA_ZIPRECRUITER_SCAN_DONE / _FAILED
    // clears the busy state and refreshes the queue.
    window.postMessage(
      { source: 'resumealign-web', type: 'RA_ZIPRECRUITER_SCAN_QUEUE', keywords: kw, location: 'USA' },
      window.location.origin
    );
    // Safety: clear busy if no done-message arrives (e.g. scan tab blocked).
    setTimeout(() => setZiprecruiterBusy((b) => { if (b) { setJobNotice('Scan is taking a while — check the ZipRecruiter tab is signed in; results will appear when done.'); } return b; }), 150000);
  };

  // Clear cached optimization/PDF for every not-yet-applied ZipRecruiter lead
  // so each re-tailors fresh (current tailoring logic — captured description,
  // correct default profile) the next time it's applied to, instead of
  // reusing stale output from before those fixes shipped. No LLM cost here;
  // that happens lazily on the next actual Apply click per job.
  const reprocessZipRecruiterQueue = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/reprocess-source`, { source: 'ziprecruiter' });
      await fetchJobLeads();
      setJobNotice(`Reset ${res.data.reset} ZipRecruiter lead(s) — each re-tailors fresh on its next Apply click.`);
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setJobError((typeof d === 'string' ? d : d?.message) || err.message || 'Reprocess failed.');
    }
  };

  // Tailor (+ PDF) the ZipRecruiter lead, then have the extension click Quick
  // Apply and drive the modal all the way through submission. autoSubmit=true
  // per Alex's explicit confirmation (2026-07-23) after reviewing several
  // filled-out applications live — ZipRecruiter has no distinct final review
  // step, so the last Continue click in a multi-page question flow IS the
  // submit action; the extension's own safety net still forces a stop if any
  // dropdown/checkbox question couldn't be auto-answered (unresolved > 0).
  const ziprecruiterApplyLead = async (lead: JobLead) => {
    if (!extDetected) { setJobError('Connect the Chrome extension to apply on ZipRecruiter.'); return; }
    try {
      setJobNotice(`Preparing “${cleanTitle(lead.title) || 'this job'}” for ZipRecruiter…`);
      await ensureExtensionConnected();
      if (!lead.optimization_id) {
        const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/tailor`);
        addToSessionCost(res.data.cost_usd);
      }
      await axios.post(`${API_BASE_URL}/api/jobs/leads/${lead.id}/formatted-pdf`);
      await fetchJobLeads();
      window.postMessage(
        { source: 'resumealign-web', type: 'RA_ZIPRECRUITER_APPLY', url: lead.url, autoSubmit: true },
        window.location.origin
      );
      setJobNotice('Driving the ZipRecruiter Quick Apply — filling every field/page and submitting automatically.');
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setJobError((typeof d === 'string' ? d : d?.message) || err.message || 'ZipRecruiter apply failed.');
    }
  };

  // --- Multi-select + background mass-apply ---------------------------------
  const toggleSelectLead = (id: string) =>
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Mass-dismiss the checked leads, remembering their prior status so it can be
  // undone in one click.
  const [dismissUndo, setDismissUndo] = useState<{ id: string; status: string }[] | null>(null);
  const dismissSelected = async () => {
    const leadsNow = await fetchJobLeads();
    const snapshot = Array.from(selectedLeads)
      .map((id) => leadsNow.find((l) => l.id === id))
      .filter((l): l is JobLead => !!l && l.status !== 'dismissed')
      .map((l) => ({ id: l.id, status: l.status }));
    if (!snapshot.length) return;
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/leads/bulk-status`, {
        ids: snapshot.map((s) => s.id),
        status: 'dismissed',
      });
      setDismissUndo(snapshot);
      setSelectedLeads(new Set());
      setJobNotice(`Dismissed ${snapshot.length} job(s).`);
      fetchJobLeads();
    } catch {
      setJobError('Could not dismiss the selected jobs.');
    }
  };
  // Mark the checked leads as applied (for jobs finished manually, e.g. a custom
  // questions page the auto-apply couldn't complete).
  const markSelectedApplied = async () => {
    const ids = Array.from(selectedLeads);
    if (!ids.length) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/leads/bulk-status`, {
        ids,
        status: 'applied',
      });
      setSelectedLeads(new Set());
      setJobNotice(`Marked ${res.data?.updated ?? ids.length} job(s) as applied.`);
      fetchJobLeads();
    } catch {
      setJobError('Could not mark the selected jobs as applied.');
    }
  };
  const undoDismiss = async () => {
    if (!dismissUndo || !dismissUndo.length) return;
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/leads/bulk-status`, { updates: dismissUndo });
      setJobNotice(`Restored ${dismissUndo.length} job(s).`);
      setDismissUndo(null);
      fetchJobLeads();
    } catch {
      setJobError('Could not undo the dismissal.');
    }
  };

  // Prepare each selected job (optimize + formatted PDF), then hand the URL list
  // to the extension's background worker, which runs the apply queue independent
  // of tab focus (no throttling) and reports progress back via RA_MASS_PROGRESS.
  const runMassApply = async (ids?: string[]) => {
    if (balance && balance.experimental === false) {
      setJobError('Auto-apply is included on the Free and Pro plans. Upgrade to Pro (Plans) to use it.');
      handleTabChange(null, 4);
      return;
    }
    if (!extDetected) {
      setJobError('Install and connect the ResumeAlign browser extension to mass-apply.');
      return;
    }
    const leadsNow = await fetchJobLeads();
    // Explicit ids (e.g. the MCP apply_queued relay) override the manual selection.
    const idSet = ids && ids.length ? new Set(ids) : selectedLeads;
    const sel = Array.from(idSet)
      .map((id) => leadsNow.find((l) => l.id === id))
      .filter((l): l is JobLead => !!l && l.status !== 'applied' && !DICE_APPLY_EXCLUDE.includes(l.source || '')); // Dice mass-apply only runs Dice leads — Indeed/LinkedIn/ZipRecruiter have their own drivers
    if (!sel.length) return;

    const setItem = (i: number, status: AutoStepStatus, detail: string) =>
      setMassApply((m) =>
        m ? { ...m, items: m.items.map((it, j) => (j === i ? { ...it, status, detail } : it)) } : m
      );

    setMassApply({
      items: sel.map((l) => ({ title: cleanTitle(l.title) || 'this job', status: 'pending', detail: '' })),
      note: 'Preparing résumés…',
    });
    setMassOpen(true);
    try {
      await ensureExtensionConnected();
    } catch {
      setMassApply((m) =>
        m ? { ...m, note: 'Could not connect the extension — open the Chrome Auto-Fill Extension page and Connect.' } : m
      );
      return;
    }

    const jobs: { url: string; label: string }[] = [];
    for (let i = 0; i < sel.length; i++) {
      const l = sel[i];
      setMassApply((m) => (m ? { ...m, note: `Preparing ${i + 1} of ${sel.length}…` } : m));
      setItem(i, 'active', 'Optimizing & building PDF…');
      try {
        const fresh = (await fetchJobLeads()).find((x) => x.id === l.id);
        if (fresh && !fresh.optimization_id) {
          const r = await axios.post(`${API_BASE_URL}/api/jobs/leads/${l.id}/tailor`);
          addToSessionCost(r.data.cost_usd);
        }
        await axios.post(`${API_BASE_URL}/api/jobs/leads/${l.id}/formatted-pdf`);
        jobs.push({ url: l.url, label: cleanTitle(l.title) || 'this job' });
        setItem(i, 'pending', 'Ready');
      } catch (err: any) {
        const d = err.response?.data?.detail;
        setItem(i, 'error', (typeof d === 'string' ? d : d?.message) || err.message || 'Failed to prepare');
      }
    }
    fetchBalance();
    setSelectedLeads(new Set());
    if (!jobs.length) {
      setMassApply((m) => (m ? { ...m, note: 'Done.' } : m));
      return;
    }
    // Hand the queue to the background worker; RA_MASS_PROGRESS now drives the UI.
    setMassApply((m) => (m ? { ...m, note: `Applying ${jobs.length} job(s) in the background…` } : m));
    window.postMessage(
      { source: 'resumealign-web', type: 'RA_MASS_APPLY', jobs, delaySec: massDelaySec },
      window.location.origin
    );
  };

  const stopMassApply = () =>
    window.postMessage({ source: 'resumealign-web', type: 'RA_MASS_STOP' }, window.location.origin);

  // Apply ALL already-tailored ('ready') leads — used by the MCP apply_queued relay.
  // Dice-only: Indeed/LinkedIn/ZipRecruiter can't be driven by the Dice content
  // script (they have their own drivers), and mixing them in leaves them
  // un-applied AND un-marked, so they reappear on every run.
  const applyAllReady = async () => {
    const leadsNow = await fetchJobLeads();
    const ids = leadsNow
      .filter((l) => l.status === 'tailored' && !DICE_APPLY_EXCLUDE.includes(l.source || ''))
      .map((l) => l.id);
    if (ids.length) await runMassApply(ids);
  };


  // Load balance on mount; show a notice when returning from Stripe Checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topup = params.get('topup');
    const sub = params.get('sub');
    const portal = params.get('portal');
    const orgParam = params.get('org');
    if (topup === 'success') {
      setPaymentNotice('Payment successful — your balance has been updated.');
    } else if (topup === 'cancel') {
      setPaymentNotice('Checkout cancelled.');
    } else if (sub === 'success') {
      setPaymentNotice('Subscription active — your plan and monthly credits are updated.');
    } else if (sub === 'cancel') {
      setPaymentNotice('Checkout cancelled.');
    } else if (orgParam === 'activated') {
      setPaymentNotice('Your firm’s org is active — employees can now redeem the join code.');
    } else if (orgParam === 'cancel') {
      setPaymentNotice('Org checkout cancelled.');
    }
    // Record where this visitor came from before we clean the URL or fetch.
    captureFirstTouch();
    if (topup || sub || portal || orgParam) {
      // Clean the query param out of the URL.
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchBalance();
    fetchPlans();
    fetchApplyStats();
    fetchLeaderboard();  // sidebar is visible across tabs — load on mount, not just on tab 2
  }, []);

  const streamingContentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when streaming content updates
  useEffect(() => {
    if (streamingContentRef.current) {
      streamingContentRef.current.scrollTop = streamingContentRef.current.scrollHeight;
    }
  }, [streamingContent]);

  // On the Interview Prep tab, auto-load any saved prep for each interview job
  // (no click, no charge) so the card shows it and offers Regenerate, not Prep.
  useEffect(() => {
    if (tabValue !== 5) return;
    jobLeads.forEach((l) => {
      if (l.interview && !(l.id in interviewPreps)) loadSavedPrep(l.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, jobLeads]);

  // When a PDF result is ready, draw attention to the formatted-PDF download as
  // the suggested next action by scrolling it into view.
  useEffect(() => {
    if (result && resumeFile && resumeFile.name.toLowerCase().endsWith('.pdf')) {
      const t = setTimeout(
        () => formattedBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        400
      );
      return () => clearTimeout(t);
    }
  }, [result, resumeFile]);

  // Keep the job queue fresh while the Job Search tab is open (e.g. the
  // extension marks jobs applied in the background) — poll every 30s. Also poll
  // backend auto-pilot run state so the Stop button appears whenever a run is in
  // flight (even after a refresh / from a prior session's lingering batch).
  useEffect(() => {
    if (tabValue !== 2) return;
    fetchAutopilotStatus();
    fetchBaseResumes();
    fetchLeaderboard();
    const id = setInterval(() => {
      fetchJobLeads();
      fetchApplyStats();
      fetchAutopilotStatus();
    }, 30000);
    return () => clearInterval(id);
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent | null, newValue: number) => {
    setTabValue(newValue);
    setError('');
    // Keep the URL in sync so a refresh stays on this tab.
    try {
      const url = newValue === 0 ? window.location.pathname : `${window.location.pathname}?tab=${newValue}`;
      window.history.replaceState(null, '', url);
    } catch (e) {
      /* */
    }
    if (newValue === 1) {
      fetchHistory();
      fetchPayments();
      fetchInterviewHistory();
      fetchBalance();
      fetchUsage();
      fetchOrg();
      fetchApSessions();
    } else if (newValue === 2) {
      fetchJobLeads();
      fetchBaseResumes();
      fetchHistory(); // to offer "use my most recent resume"
      fetchPrefs();
      fetchBalance();
    } else if (newValue === 4) {
      fetchBalance();
      fetchPlans();
      fetchOrg();
    } else if (newValue === 5) {
      fetchBalance();
      fetchJobLeads(); // a useEffect then auto-loads saved prep per interview job
    }
  };

  // If the tab was restored from the URL on load, run its data fetches (the
  // initial useState bypasses handleTabChange, which is where they live).
  useEffect(() => {
    if (tabValue !== 0) handleTabChange(null, tabValue);
    fetchOrg(); // so the branded-résumé button reflects org membership everywhere
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const HISTORY_PAGE = 50;
  const fetchHistory = async (append = false, q?: string) => {
    try {
      if (append) setHistoryLoadingMore(true);
      const skip = append ? history.length : 0;
      const query = (q !== undefined ? q : historyQuery).trim();
      const response = await axios.get(
        `${API_BASE_URL}/api/optimizations?skip=${skip}&limit=${HISTORY_PAGE}${query ? `&q=${encodeURIComponent(query)}` : ''}`
      );
      const data: OptimizationResult[] = response.data || [];
      setHistory((prev) => (append ? [...prev, ...data] : data));
      setHistoryHasMore(data.length === HISTORY_PAGE);
    } catch (err) {
      console.error('Failed to fetch history', err);
    } finally {
      if (append) setHistoryLoadingMore(false);
    }
  };

  // Toggle 'got an interview' on a History card (by its job URL); reflects in the
  // interviews list + interview prep.
  const markHistoryInterview = async (item: OptimizationResult) => {
    const next = !item.interview;
    setHistory((h) => h.map((o) => (o.id === item.id ? { ...o, interview: next } : o)));
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/leads/mark-interview`, {
        url: item.job_url, title: item.job_title, company: item.company_name, interview: next,
      });
      fetchJobLeads();
    } catch (e: any) {
      setHistory((h) => h.map((o) => (o.id === item.id ? { ...o, interview: !next } : o)));
      setJobError(e?.response?.data?.detail || 'Could not update interview status.');
    }
  };

  const fetchApSessions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/autopilot/sessions`);
      setApSessions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch auto-pilot sessions', err);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/billing/usage`);
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch usage', err);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/billing/payments`);
      setPayments(response.data);
    } catch (err) {
      console.error('Failed to fetch payments', err);
    }
  };

  const fetchInterviewHistory = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/interview/history`);
      setInterviewHistory(response.data?.items || []);
    } catch (err) {
      console.error('Failed to fetch interview history', err);
    }
  };

  const fetchPrefs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/prefs`);
      setPrefs(res.data || {});
    } catch (err) {
      console.error('Failed to fetch job prefs', err);
    }
  };

  const saveAutopilot = async () => {
    if (!prefs) return;
    setAutopilotSaving(true);
    setJobError('');
    try {
      // Send the whole prefs object so daily-routine fields aren't reset.
      await axios.put(`${API_BASE_URL}/api/jobs/prefs`, prefs);
      setJobNotice('Auto-pilot settings saved.');
    } catch (e: any) {
      setJobError(e?.response?.data?.detail || 'Could not save auto-pilot settings.');
    } finally {
      setAutopilotSaving(false);
    }
  };

  // Trigger a one-off auto-pilot run via the extension (instead of waiting for
  // the daily alarm). Result comes back as RA_AUTOPILOT_RESULT.
  // Run auto-pilot now. payload: {} = primary/first schedule; {keywords,location}
  // = a specific schedule; {searches:[...]} = all schedules in one pass.
  const runAutopilotNow = (payload: any = {}) => {
    if (!extDetected) {
      setAutopilotRunMsg('Connect the Chrome extension first (and sign in to Dice).');
      return;
    }
    setAutopilotRunningNow(true);
    setAutopilotRunMsg(payload.offload
      // Offload: the extension only scans; the user's AI agent tailors the matches and
      // then triggers apply (the mass-apply modal opens at that point).
      ? 'Scanning Dice for matches… your AI agent will tailor them, then applications run here.'
      : 'Searching Dice and verifying remote… tailoring + applying continues in the background.');
    window.postMessage({ source: 'resumealign-web', type: 'RA_AUTOPILOT_NOW', ...payload }, window.location.origin);
    window.setTimeout(() => {
      setAutopilotRunningNow((running) => {
        if (running) setAutopilotRunMsg('Working in the background — applications appear below as each job is tailored.');
        return false;
      });
    }, 120000);
  };

  // Relay MCP-queued commands to the extension: the backend can't message the
  // extension, so while this tab is open AND the extension is connected, poll for a
  // pending command (e.g. run_autopilot set via MCP) and fire the same path the
  // on-page button uses, then ack it. Gated on extDetected so non-extension sessions
  // don't poll. Refs keep the interval stable across renders.
  const runAutopilotNowRef = useRef(runAutopilotNow);
  runAutopilotNowRef.current = runAutopilotNow;
  const applyAllReadyRef = useRef(applyAllReady);
  applyAllReadyRef.current = applyAllReady;
  const extDetectedRef = useRef(extDetected);
  extDetectedRef.current = extDetected;
  const handledCmdRef = useRef('');
  useEffect(() => {
    const tick = async () => {
      if (!extDetectedRef.current) return;  // only a connected extension can act on it
      try {
        const r = await axios.get(`${API_BASE_URL}/api/jobs/pending-command`);
        const cmd = r.data?.command;
        // Only claim command types THIS web app can run. Others (e.g.
        // drive_indeed_apply) must be left for the extension's service-worker
        // poller — if we ack them here we'd clear them without acting, dropping
        // the command. `apply_queued` with a `site` (e.g. ziprecruiter) is such a
        // case: the web app only runs the Dice mass-apply (applyAllReady), so a
        // site-specific apply MUST go to the extension's per-site driver
        // (applyZipRecruiterQueueNow) — otherwise the web app wins the claim and
        // wrongly runs a Dice mass-apply with no ZipRecruiter jobs in it.
        const WEB_HANDLED = ['run_autopilot', 'apply_queued'];
        const webCanHandle = WEB_HANDLED.includes(cmd?.type)
          && !(cmd?.type === 'apply_queued' && cmd?.site);
        if (cmd?.id && cmd.id !== handledCmdRef.current && webCanHandle) {
          // Claim FIRST. The extension's own service-worker poller may also be
          // running; the atomic ack means exactly one of us clears it. Only run
          // it here if this tab won the claim (cleared===1).
          const ack = await axios.post(`${API_BASE_URL}/api/jobs/pending-command/ack`, { id: cmd.id });
          handledCmdRef.current = cmd.id;
          if (ack.data?.cleared) {
            if (cmd.type === 'run_autopilot') {
              const opts: any = {};
              if (cmd.searches) opts.searches = cmd.searches;
              if (cmd.offload) opts.offload = true;
              if (cmd.site) opts.site = cmd.site; // 'indeed' | 'dice' (default)
              runAutopilotNowRef.current(opts);
            }
            else if (cmd.type === 'apply_queued') applyAllReadyRef.current();
          }
        }
      } catch { /* ignore poll errors */ }
    };
    const iv = setInterval(tick, 10000);
    return () => clearInterval(iv);
  }, []);

  // Stop an in-flight auto-pilot run: tells the extension to halt polling +
  // release its "already running" guard, and the backend to cancel the batch and
  // release the leads. Same path the modal's Stop uses.
  // Override a wrong 'not remote' verdict from the run modal: requeue the job and
  // mark it remote-approved so the next run keeps it. Updates the row in place.
  const requeueRemote = async (url?: string) => {
    if (!url) return;
    try {
      await axios.post(`${API_BASE_URL}/api/jobs/leads/requeue-remote`, { url });
      setMassApply((m) => m && {
        ...m,
        items: m.items.map((it) =>
          it.url === url ? { ...it, reason: undefined, detail: 'Requeued as remote ✓ — runs next pass', status: 'done' as AutoStepStatus } : it
        ),
      });
      fetchJobLeads();
    } catch (e: any) {
      setJobError(e?.response?.data?.detail || 'Could not requeue that job.');
    }
  };

  // Poll backend run state so Stop shows whenever a run is actually in flight,
  // regardless of whether this browser session started it.
  const fetchAutopilotStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/autopilot/status`);
      setAutopilotActive(!!res.data?.processing);
    } catch (e) {
      /* best-effort */
    }
  };

  const stopAutopilot = () => {
    // Stop the extension's poll loop…
    window.postMessage({ source: 'resumealign-web', type: 'RA_MASS_STOP' }, window.location.origin);
    // …and cancel directly on the backend too, so it works even if the extension
    // isn't polling (e.g. after a refresh with a batch still running).
    axios.post(`${API_BASE_URL}/api/jobs/autopilot/cancel`).catch(() => {});
    setAutopilotRunningNow(false);
    setAutopilotActive(false);
    setAutopilotRunMsg('Stopping auto-pilot…');
    setMassApply((m) => (m ? { ...m, note: 'Stopped.' } : m));
  };

  // Retry jobs dropped due to a transient/technical error (scrape/tailor failure)
  // so the next run picks them up again. Settled determinations (non-remote /
  // expired / fake) are intentionally left dismissed — re-checking them would just
  // re-confirm the same conclusion at LLM cost.
  const [reevaluating, setReevaluating] = useState(false);
  const reevaluateDismissed = async () => {
    setReevaluating(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/jobs/reevaluate-dismissed`);
      const n = res.data?.reset ?? 0;
      setAutopilotRunMsg(
        n > 0
          ? `Restored ${n} job(s) that hit a temporary error — run auto-pilot to retry them.`
          : 'No jobs to retry. (Jobs judged non-remote, expired, or fake stay dismissed so they are not re-analyzed.)'
      );
      fetchJobLeads();
    } catch (err) {
      setAutopilotRunMsg('Could not restore dismissed jobs.');
    } finally {
      setReevaluating(false);
    }
  };

  const selectFile = (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.txt')) {
      setError('Please upload a PDF or TXT file');
      return;
    }
    setError('');
    setResumeFile(file);
    setResumeText(''); // Clear text when a file is selected
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      selectFile(event.target.files[0]);
    }
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      selectFile(event.dataTransfer.files[0]);
    }
  };

  const handleSubmitWithStreaming = async (event: React.FormEvent) => {
    console.log('handleSubmitWithStreaming called');
    event.preventDefault();
    setLoading(true);
    setTopUpContext(null);
    setError('');
    setResult(null);
    setFormattedPdfB64(null);
    setFormattedCost(null);
    setStreamingContent('');
    setStatusMessage('');
    setTempJobPostingContent('');

    try {
      const formData = new FormData();
      formData.append('company_website', honeypot); // honeypot: humans leave blank

      // Add job posting information based on input type
      if (jobInputType === 'url') {
        formData.append('job_url', jobUrl);
      } else {
        formData.append('job_text', jobText);
      }

      if (resumeFile) {
        formData.append('resume_file', resumeFile);
      } else if (resumeText) {
        formData.append('resume_text', resumeText);
      } else {
        throw new Error('Please provide either a resume file or text');
      }

      // Use EventSource for proper SSE handling
      const streamUrl = `${API_BASE_URL}/optimize-stream`;
      console.log('>>> STREAMING REQUEST TO:', streamUrl);
      console.log('API_BASE_URL:', API_BASE_URL);
      const response = await fetch(streamUrl, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.status === 402) {
        const body = await response.json().catch(() => ({} as any));
        const detail = body.detail || {};
        handlePaymentRequired(detail, 'optimize', formTopUpRef);
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';
      let jobPostingContent = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.slice(6); // Remove 'data: ' prefix
              if (dataStr.trim()) {
                try {
                  const data = JSON.parse(dataStr);
                  
                  if (data.type === 'job_posting_preview') {
                    console.log('Received job posting preview:', data.content?.length || 0, 'chars');
                    setTempJobPostingContent(data.content); // Show preview immediately
                  } else if (data.type === 'job_posting') {
                    console.log('Received full job posting content:', data.content?.length || 0, 'chars');
                    jobPostingContent = data.content;
                    setTempJobPostingContent(data.content); // Show full content in UI
                  } else if (data.type === 'status') {
                    console.log('Status:', data.message);
                    setStatusMessage(data.message);
                  } else if (data.type === 'progress') {
                    accumulatedContent += data.content;
                    // Clean the live view: turn the resume markers into a
                    // readable header and hide the trailing JSON metadata block.
                    const display = accumulatedContent
                      .replace('[RESUME_START]', '\n=== Optimized Resume ===\n')
                      .replace('[RESUME_END]', '')
                      .replace(/```json[\s\S]*?```/, '')
                      .replace(/```json[\s\S]*$/, '');
                    setStreamingContent(display);
                  } else if (data.type === 'complete') {
                    // The streaming is complete, we have the optimized result
                    // Include the job posting content we received earlier
                    setResult({
                      id: 'temp-' + Date.now(),
                      optimized_resume: data.result.optimized_resume,
                      suggestions: data.result.suggestions,
                      match_score: data.result.match_score,
                      created_at: new Date().toISOString(),
                      job_posting_content: jobPostingContent // Use the content we received
                    });
                    setStreamingContent('');
                    setStatusMessage('');
                    // No need to call /optimize anymore - will be saved by backend
                  } else if (data.type === 'cost') {
                    // Cost arrives after 'complete'; merge it into the result
                    setResult(prev => prev ? {
                      ...prev,
                      cost_usd: data.cost_usd,
                      input_tokens: data.input_tokens,
                      output_tokens: data.output_tokens,
                    } : prev);
                    addToSessionCost(data.cost_usd);
                    fetchBalance();
                  } else if (data.type === 'database_saved') {
                    // Update the result with the real database ID
                    console.log('Optimization saved to database with ID:', data.id);
                    setResult(prev => prev ? {...prev, id: data.id} : null);
                  } else if (data.type === 'database_error') {
                    console.error('Failed to save to database:', data.message);
                  } else if (data.type === 'error') {
                    setError(data.message);
                    setStreamingContent('');
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e, 'Line:', dataStr);
                }
              }
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Streaming error:', err);
      setError(err.message || 'An error occurred during streaming');
      setStreamingContent('');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    console.log('=== SUBMIT HANDLER ===');
    console.log('handleSubmit called, useStreaming:', useStreaming);
    console.log('Streaming toggle state:', useStreaming);
    trackEvent('opt_submitted'); // funnel: wizard completed

    if (useStreaming) {
      console.log('>>> ROUTING TO STREAMING MODE <<<');
      return handleSubmitWithStreaming(event);
    }
    
    console.log('>>> ROUTING TO REGULAR MODE (NO STREAMING) <<<');
    event.preventDefault();
    setLoading(true);
    setTopUpContext(null);
    setError('');
    setResult(null);
    setFormattedPdfB64(null);
    setFormattedCost(null);

    try {
      const formData = new FormData();
      formData.append('company_website', honeypot); // honeypot: humans leave blank

      // Add job posting information based on input type
      if (jobInputType === 'url') {
        formData.append('job_url', jobUrl);
      } else {
        formData.append('job_text', jobText);
      }

      if (resumeFile) {
        formData.append('resume_file', resumeFile);
      } else if (resumeText) {
        formData.append('resume_text', resumeText);
      } else {
        throw new Error('Please provide either a resume file or text');
      }

      const response = await axios.post(`${API_BASE_URL}/optimize`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('API Response:', response.data);
      console.log('Job posting content:', response.data.job_posting_content);
      console.log('Job posting content length:', response.data.job_posting_content?.length);
      setResult(response.data);
      addToSessionCost(response.data?.cost_usd);
      fetchBalance();
    } catch (err: any) {
      if (err.response?.status === 402) {
        const detail = err.response.data?.detail || {};
        handlePaymentRequired(detail, 'optimize', formTopUpRef);
      } else {
        const detail = err.response?.data?.detail;
        setError((typeof detail === 'string' ? detail : null) || err.message || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const isPdfUpload = !!resumeFile && resumeFile.name.toLowerCase().endsWith('.pdf');

  const downloadBase64Pdf = (base64: string, filename: string) => {
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'optimized_resume.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Save the already-built formatted PDF under the (possibly edited) filename,
  // ensuring a .pdf extension. No rebuild, so it doesn't cost another credit.
  const saveFormattedPdf = () => {
    if (!formattedPdfB64) return;
    let name = (formattedFilename || 'optimized_resume.pdf').trim();
    if (!name.toLowerCase().endsWith('.pdf')) name += '.pdf';
    downloadBase64Pdf(formattedPdfB64, name);
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Download a formatted PDF previously generated and cached on a history entry.
  const [historyPdfLoadingId, setHistoryPdfLoadingId] = useState<string | null>(null);
  const downloadHistoryFormatted = async (id: string) => {
    setHistoryPdfLoadingId(id);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/optimizations/${id}/formatted-pdf`);
      downloadBase64Pdf(res.data.pdf_base64, res.data.filename || 'optimized_resume.pdf');
    } catch (err) {
      console.error('Failed to download formatted PDF', err);
    } finally {
      setHistoryPdfLoadingId(null);
    }
  };

  const downloadBase64Docx = (base64: string, filename: string) => {
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'optimized_resume.docx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Build + download an editable Word (.docx) version. Pass an optimization id
  // (cached server-side after first build) or raw resume text for the live result.
  const [docxLoadingId, setDocxLoadingId] = useState<string | null>(null);
  const downloadWord = async (key: string, body: { optimization_id?: string; resume_text?: string }) => {
    setDocxLoadingId(key);
    try {
      const res = await axios.post(`${API_BASE_URL}/export-docx`, body);
      downloadBase64Docx(res.data.docx_base64, res.data.filename || 'optimized_resume.docx');
    } catch (err: any) {
      console.error('Failed to build Word document', err);
      setError(err?.response?.data?.detail || 'Could not build the Word document. Please try again.');
    } finally {
      setDocxLoadingId(null);
    }
  };

  const fetchOrg = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/org`);
      setOrg(res.data);
    } catch {
      setOrg(null);
    }
  };
  const redeemOrg = async () => {
    if (!orgCode.trim()) return;
    setOrgBusy(true);
    setOrgMsg('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/org/redeem`, { code: orgCode.trim() });
      setOrgCode('');
      setOrg({ member: true, org: res.data.org });
      setOrgMsg(`Joined ${res.data.org?.display_name || 'your organization'}.`);
      await fetchBalance();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setOrgMsg((typeof d === 'string' ? d : d?.message) || 'Invalid organization code.');
    } finally {
      setOrgBusy(false);
    }
  };
  // Open the branded-resume dialog for an optimization. Members brand to their
  // org automatically; everyone else types a company to brand to.
  const openBranded = (optId: string) => {
    setBrandedOptId(optId);
    setBrandedMsg('');
    if (!org?.member) {
      setBrandedCompany('');
      setBrandedLogo('');
      setBrandedColor('#0f62fe');
    }
  };
  const generateBranded = async () => {
    if (!brandedOptId) return;
    setBrandedBusy(true);
    setBrandedMsg('');
    try {
      const body: any = { optimization_id: brandedOptId };
      if (!org?.member) {
        body.display_name = brandedCompany.trim();
        body.primary_color = brandedColor;
        if (brandedLogo) body.logo_b64 = brandedLogo;
      }
      const res = await axios.post(`${API_BASE_URL}/api/branded-resume`, body);
      downloadBase64Pdf(res.data.pdf_base64, res.data.filename || 'branded_resume.pdf');
      setBrandedOptId(null);
      fetchBalance();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setBrandedMsg((typeof d === 'string' ? d : d?.message) || 'Could not build the branded resume.');
    } finally {
      setBrandedBusy(false);
    }
  };

  const handleDownloadFormatted = async () => {
    if (!resumeFile || !result) return;
    setFormattedLoading(true);
    setTopUpContext(null);
    setFormattedError('');
    setFormattedProgress(0);
    setFormattedStatus('Starting...');
    setFormattedCost(null);
    setFormattedPdfB64(null);

    try {
      const formData = new FormData();
      formData.append('resume_file', resumeFile);
      formData.append('optimized_text', result.optimized_resume);
      // Link the generated PDF to its saved optimization so it can be
      // re-downloaded from History (skip the pre-save temp id).
      if (result.id && !result.id.startsWith('temp-')) {
        formData.append('optimization_id', result.id);
      }
      // Lets the backend name the file like alex_chang_resume_tl.pdf.
      if (result.job_title) {
        formData.append('job_title', result.job_title);
      }

      const response = await fetch(`${API_BASE_URL}/rebuild-formatted-resume-stream`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (response.status === 402) {
        const body = await response.json().catch(() => ({} as any));
        const detail = body.detail || {};
        handlePaymentRequired(detail, 'rebuild', resultTopUpRef);
        return;
      }
      if (!response.ok) {
        let detail = `HTTP error ${response.status}`;
        try {
          const err = await response.json();
          detail = err.detail || detail;
        } catch (e) { /* non-JSON error body */ }
        throw new Error(detail);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const dataStr = line.slice(6);
            if (!dataStr.trim()) continue;
            try {
              const data = JSON.parse(dataStr);
              if (data.type === 'progress') {
                setFormattedProgress(data.progress);
                setFormattedStatus(data.message || '');
              } else if (data.type === 'complete') {
                setFormattedProgress(100);
                setFormattedStatus('Done');
                // Hold the PDF so the user can confirm/rename before saving.
                setFormattedPdfB64(data.pdf_base64);
                setFormattedFilename(data.filename || 'optimized_resume.pdf');
                if (typeof data.cost_usd === 'number') {
                  setFormattedCost(data.cost_usd);
                  addToSessionCost(data.cost_usd);
                }
                fetchBalance();
                completed = true;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e: any) {
              if (e instanceof SyntaxError) {
                console.error('Error parsing SSE data:', e, 'Line:', dataStr);
              } else {
                throw e;
              }
            }
          }
        }
      }

      if (!completed) {
        throw new Error('The server finished without returning a PDF');
      }
    } catch (err: any) {
      setFormattedError(err.message || 'Failed to generate formatted PDF');
    } finally {
      setFormattedLoading(false);
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  };

  // Step-by-step onboarding pointer: 1=job field, 2=resume area, 3=optimize.
  const jobFilled = jobInputType === 'url' ? !!jobUrl.trim() : !!jobText.trim();
  const resumeFilled = !!resumeFile || !!resumeText.trim();
  const guideStep = loading || result ? 0 : !jobFilled ? 1 : !resumeFilled ? 2 : 3;

  // Fire-and-forget product funnel event (whitelisted server-side).
  const trackEvent = (name: string) => {
    axios.post(`${API_BASE_URL}/api/events`, { name }).catch(() => {});
  };
  // Count an optimize-form view once per mount.
  useEffect(() => {
    trackEvent('opt_view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Once a resume is provided, advance the wizard to the job step (and track it).
  // Guarded so going Back to edit the resume doesn't re-trap them on step 2.
  useEffect(() => {
    if (resumeFilled && !prevResumeFilledRef.current) {
      trackEvent('opt_resume_added');
      setWizardStep((s) => (s === 1 ? 2 : s));
    }
    prevResumeFilledRef.current = resumeFilled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeFilled]);

  const guideMarker = (text: string) => (
    <Box className="guide-marker" sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          bgcolor: '#ff7a18',
          color: '#1a0a00',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          fontWeight: 700,
          fontSize: 13,
          fontFamily: "'Share Tech Mono', monospace",
          boxShadow: '0 0 14px rgba(255,122,24,0.7)',
        }}
      >
        {text}
      </Box>
      <Box
        sx={{
          width: 0,
          height: 0,
          ml: 2.5,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '9px solid #ff7a18',
        }}
      />
    </Box>
  );

  // Internal marketing dashboard — gated by an admin token, reachable at
  // /?admin=marketing. Kept out of the main UI/navigation on purpose.
  if (new URLSearchParams(window.location.search).get('admin') === 'marketing') {
    return <AdminMarketing />;
  }
  if (new URLSearchParams(window.location.search).get('admin') === 'orgs') {
    return <AdminOrgsPanel />;
  }
  if (new URLSearchParams(window.location.search).get('admin') === 'support') {
    return <SupportLogsPanel />;
  }

  return (
    <>
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          align="center"
          className="matrix-glow"
        >
          Align Resume
        </Typography>
        <Typography
          variant="subtitle1"
          component="h2"
          align="center"
          gutterBottom
          className="matrix-cursor"
          sx={{ color: 'primary.main' }}
        >
          &gt; Tailor your resume to every job — and auto-apply while you sleep
        </Typography>

        {balance?.byok && balance?.byok_credit_exhausted && (
          <Alert
            severity="error"
            sx={{ mb: 2, maxWidth: 720, mx: 'auto' }}
            action={
              <Button
                color="inherit"
                size="small"
                href="https://console.anthropic.com/settings/billing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Recharge
              </Button>
            }
          >
            <b>Your Anthropic API key is out of credit.</b> Auto-pilot is paused. Add
            credit at{' '}
            <a
              href="https://console.anthropic.com/settings/billing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', fontWeight: 700 }}
            >
              console.anthropic.com → Billing
            </a>
            , then restart auto-pilot. Until then, no résumés are tailored and no
            applications are sent.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <Chip
            size="small"
            clickable
            onClick={() => handleTabChange(null, 3)}
            icon={extDetected ? <Extension /> : <LinkOff />}
            label={extDetected ? 'Chrome extension connected' : 'Chrome extension not connected'}
            variant="outlined"
            title="Chrome extension connectivity — click to manage"
            sx={{
              color: extDetected ? BBS.green : '#ff5252',
              borderColor: extDetected ? BBS.green : '#ff5252',
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
          <ThemeSwitcher />
        </Stack>

        {sessionCost > 0 && (
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            <Chip
              variant="outlined"
              color="primary"
              label={`Session cost: ${formatCost(sessionCost)}`}
            />
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}
        >
          {balance && (
            <>
              <Chip
                color="primary"
                variant="outlined"
                clickable
                onClick={() => handleTabChange(null, 4)}
                label={`Plan: ${balance.plan_name || 'Free'}`}
                title="View plans"
              />
              {balance.byok ? (
                // Power (BYO key): unmetered — usage is billed to the user's own
                // Anthropic account, so there's no monthly credit cap to show.
                <>
                  <Chip
                    color="success"
                    variant="outlined"
                    title="Power (BYO key): unlimited usage — billed to your own Anthropic API key"
                    label="Usage: Unlimited"
                  />
                  {balance.byok_mtd_cost_usd != null && (
                    <Chip
                      color="success"
                      variant="outlined"
                      title="Estimated token cost billed to your own Anthropic API key this calendar month to date, priced at Anthropic API rates. An estimate from our token logs — close to but not an exact mirror of your Anthropic console."
                      label={`API cost (MTD est.): ${formatCost(balance.byok_mtd_cost_usd)}`}
                    />
                  )}
                  {balance.byok_yesterday_cost_usd != null && (
                    <Chip
                      color="success"
                      variant="outlined"
                      title={`Estimated API cost for ${balance.byok_yesterday_date || 'yesterday'} (full UTC day) — compare against that day's total in your Anthropic console to check accuracy.`}
                      label={`API cost (${balance.byok_yesterday_date ? balance.byok_yesterday_date.slice(5) : 'yesterday'}): ${formatCost(balance.byok_yesterday_cost_usd)}`}
                    />
                  )}
                </>
              ) : (() => {
                const total = balance.monthly_credits ?? balance.free_tries_total ?? 0;
                const remaining = balance.credits_remaining ?? balance.free_tries_remaining ?? 0;
                const pctUsed = total > 0 ? Math.round(((total - remaining) / total) * 100) : 0;
                return (
                  <Chip
                    color={pctUsed >= 100 ? 'default' : pctUsed >= 80 ? 'warning' : 'success'}
                    variant="outlined"
                    title={`${total - remaining} of ${total} monthly credits used · ${remaining} left`}
                    label={`Monthly usage: ${pctUsed}% (${remaining} left)`}
                  />
                );
              })()}
              {(balance.overage_credits ?? 0) > 0 && (
                <Chip
                  color="primary"
                  variant="outlined"
                  label={`+${balance.overage_credits} prepaid credits`}
                />
              )}
              {balance.balance_usd > 0 && (
                <Chip
                  color="primary"
                  variant="outlined"
                  label={`Balance: ${formatMoney(balance.balance_usd, balance.currency)}`}
                />
              )}
            </>
          )}
          {/* Prepaid credit packs are only for plans that allow overage (Pro). */}
          {balance?.allow_overage && (
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={topUpCurrency}
                onChange={(e, v) => v && setTopUpCurrency(v)}
              >
                <ToggleButton value="usd">$</ToggleButton>
                <ToggleButton value="eur">€</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" variant="outlined" onClick={startTopUp} disabled={topUpLoading}>
                {topUpLoading
                  ? 'Redirecting…'
                  : `Add ${topUpCurrency === 'eur' ? '€30' : '$30'} (${balance.credits_per_topup ?? 120} credits)`}
              </Button>
            </>
          )}
          <Button size="small" variant="contained" onClick={() => handleTabChange(null, 4)}>
            {balance && balance.plan && balance.plan !== 'free' ? 'Manage plan' : 'Upgrade'}
          </Button>
        </Stack>

        {paymentNotice && (
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            <Alert severity="info" onClose={() => setPaymentNotice('')}>
              {paymentNotice}
            </Alert>
          </Stack>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0, sm: 3 },
            mt: 4,
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 210 },
              flexShrink: 0,
              position: { sm: 'sticky' },
              top: 16,
            }}
          >
            {/* Main product */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <List disablePadding>
                {[
                  { idx: 0, label: 'Resume Optimization', icon: <Description /> },
                  { idx: 1, label: 'History', icon: <Receipt /> },
                  { idx: 4, label: 'Plans', icon: <RocketLaunch /> },
                ].map((item) => (
                  <ListItemButton
                    key={item.idx}
                    selected={tabValue === item.idx}
                    onClick={() => handleTabChange(null, item.idx)}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            {/* Experimental features */}
            <Paper variant="outlined" sx={{ overflow: 'hidden', mt: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  color: 'text.secondary',
                  letterSpacing: 1.5,
                }}
              >
                EXPERIMENTAL
              </Typography>
              <List disablePadding>
                {[
                  { idx: 2, label: 'Dice Job Search', icon: <Work /> },
                  { idx: 7, label: 'Indeed Job Search', icon: <Work /> },
                  { idx: 8, label: 'LinkedIn Job Search', icon: <Work /> },
                  { idx: 9, label: 'ZipRecruiter Job Search', icon: <Work /> },
                  { idx: 5, label: 'Interview Prep', icon: <Quiz /> },
                  { idx: 3, label: 'Chrome Auto-Fill Extension', icon: <Extension /> },
                  { idx: 6, label: 'Connect AI Agent (MCP)', icon: <SmartToy /> },
                ].map((item) => (
                  <ListItemButton
                    key={item.idx}
                    selected={tabValue === item.idx}
                    onClick={() => handleTabChange(null, item.idx)}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            {/* Auto-apply success metrics */}
            <Paper variant="outlined" sx={{ overflow: 'hidden', mt: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', letterSpacing: 1.5 }}
              >
                AUTO-APPLIES
              </Typography>
              <Box sx={{ px: 2, pb: 1.5 }}>
                {applyStats.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No applications yet.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="h5" sx={{ color: 'primary.main', lineHeight: 1.1 }}>
                      {applyStats.reduce((s, r) => s + r.count, 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      total submitted
                    </Typography>
                    <Stack spacing={0.25}>
                      {applyStats.slice(0, 7).map((r) => (
                        <Stack key={r.date} direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(`${r.date}T00:00:00`).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {r.count}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Box>
            </Paper>

            {/* Gamified, anonymized community leaderboard by plan tier */}
            {leaderboard && (
              <Paper variant="outlined" sx={{ overflow: 'hidden', mt: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', letterSpacing: 1.5 }}
                >
                  COMMUNITY LEADERBOARD
                </Typography>
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Best single day of auto-applies by plan (last 30 days)
                  </Typography>
                  {(() => {
                    const max = Math.max(1, ...leaderboard.tiers.map((t) => t.best_daily));
                    return (
                      <Stack spacing={1}>
                        {leaderboard.tiers.map((t) => {
                          const mine = t.plan === leaderboard.you.plan;
                          return (
                            <Box key={t.plan}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="caption" sx={{ fontWeight: mine ? 700 : 500 }}>
                                  {t.plan_name}
                                  {mine ? ' (you)' : ''}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  {t.best_daily}
                                </Typography>
                              </Stack>
                              <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${Math.round((t.best_daily / max) * 100)}%`,
                                    minWidth: t.best_daily ? 4 : 0,
                                    bgcolor: mine ? '#ff7a18' : 'primary.main',
                                    borderRadius: 1,
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    );
                  })()}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Your best day: <b>{leaderboard.you.best}</b> · today: <b>{leaderboard.you.today}</b>
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <TabPanel value={tabValue} index={0}>
          {showGuide && (
            <Alert
              severity="info"
              icon={<Info />}
              onClose={dismissGuide}
              sx={{ mt: 3 }}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>New here? How it works</AlertTitle>
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                <li>
                  <b>Add your resume</b> — upload a PDF or TXT file, drag &amp; drop, or paste the text.
                </li>
                <li>
                  <b>Add the job posting</b> — paste its URL or the full job text.
                </li>
                <li>
                  <b>Click “Optimize Resume”</b> — AI tailors your resume to the job.
                  If you uploaded a PDF, you can then download a formatted PDF that
                  matches your original layout.
                </li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Each optimization uses 1 credit. Your {balance?.plan_name ?? 'Free'} plan
                includes {balance?.monthly_credits ?? 10} credits a month — manage your plan
                or add credits on the <b>Plans</b> tab.
              </Typography>
            </Alert>
          )}
          <Paper elevation={3} sx={{ p: 4, mt: 3 }}>
            <form onSubmit={handleSubmit}>
              {/* Honeypot: hidden from real users; bots that autofill it are
                  rejected (and their IP auto-blocked) server-side. */}
              <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
              <Stack spacing={3}>
                {/* 2-step wizard: résumé first (lowest friction), then the job. */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" color={wizardStep === 1 ? 'primary' : 'default'}
                    variant={wizardStep === 1 ? 'filled' : 'outlined'} label="1 · Your résumé" />
                  <Box sx={{ width: 18, height: 2, bgcolor: 'divider' }} />
                  <Chip size="small" color={wizardStep === 2 ? 'primary' : 'default'}
                    variant={wizardStep === 2 ? 'filled' : 'outlined'} label="2 · The job" />
                </Stack>

                {wizardStep === 2 && (
                <>
                <Button variant="text" size="small" onClick={() => setWizardStep(1)} sx={{ alignSelf: 'flex-start' }}>
                  ← Back to résumé
                </Button>
                <Box data-tour="job-input">
                  <Typography variant="h6" gutterBottom>
                    Job Posting Input
                  </Typography>
                  <ToggleButtonGroup
                    value={jobInputType}
                    exclusive
                    onChange={(e, newType) => {
                      if (newType !== null) {
                        setJobInputType(newType);
                        setJobUrl('');
                        setJobText('');
                      }
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="text">
                      <Description sx={{ mr: 1 }} />
                      Paste Job Text
                    </ToggleButton>
                    <ToggleButton value="url">
                      <Link sx={{ mr: 1 }} />
                      Job URL
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {guideStep === 1 &&
                    guideMarker(
                      jobInputType === 'url'
                        ? 'Start here — paste the job posting URL'
                        : 'Start here — paste the job posting text'
                    )}
                  {jobInputType === 'url' ? (
                    <TextField
                      fullWidth
                      label="Job Posting URL"
                      variant="outlined"
                      value={jobUrl}
                      onChange={(e) => setJobUrl(e.target.value)}
                      required
                      placeholder="https://example.com/job-posting"
                      helperText="Note: Some job sites may block automated access"
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      label="Job Posting Content"
                      variant="outlined"
                      value={jobText}
                      onChange={(e) => setJobText(e.target.value)}
                      required
                      placeholder="Paste the full job posting content here..."
                      helperText="Recommended: Copy/paste the entire job description to avoid bot blocking"
                    />
                  )}
                </Box>
                </>
                )}

                {wizardStep === 1 && (
                <Box data-tour="resume-input">
                  <Typography variant="h6" gutterBottom>
                    Resume Input
                  </Typography>
                  {guideStep === 2 &&
                    guideMarker('Next — drop your resume (PDF or TXT) here')}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      component="label"
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        p: 4,
                        border: '2px dashed',
                        borderColor: dragActive ? 'primary.main' : 'grey.400',
                        borderRadius: 2,
                        backgroundColor: dragActive ? 'action.hover' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        // Children must not receive drag events, or moving over
                        // them fires dragleave on the zone and flickers the state.
                        '& > *': { pointerEvents: 'none' },
                      }}
                    >
                      <CloudUpload color={dragActive ? 'primary' : 'action'} fontSize="large" />
                      <Typography variant="body1">
                        {dragActive
                          ? 'Drop your resume here'
                          : 'Drag & drop your resume here, or click to browse'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PDF or TXT
                      </Typography>
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.txt"
                        onChange={handleFileChange}
                      />
                    </Box>
                    {resumeFile && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Selected: {resumeFile.name}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="body1" align="center" sx={{ my: 2 }}>
                    OR
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Paste Resume Text"
                    variant="outlined"
                    value={resumeText}
                    onChange={(e) => {
                      setResumeText(e.target.value);
                      setResumeFile(null); // Clear file when text is entered
                    }}
                    placeholder="Paste your resume content here..."
                  />
                  <Button variant="contained" fullWidth size="large" sx={{ mt: 2 }}
                    disabled={!resumeFilled} onClick={() => setWizardStep(2)}>
                    Next: add the job →
                  </Button>
                </Box>
                )}

                {wizardStep === 2 && (
                <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useStreaming}
                      onChange={(e) => setUseStreaming(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Stream />
                      <Typography>Enable live streaming (see AI thinking in real-time)</Typography>
                    </Stack>
                  }
                />

                <Button
                  type="submit"
                  data-tour="optimize-button"
                  variant="contained"
                  size="large"
                  fullWidth
                  className={guideStep === 3 ? 'guide-pulse-btn' : undefined}
                  disabled={loading || (jobInputType === 'url' ? !jobUrl : !jobText) || (!resumeFile && !resumeText)}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={
                    guideStep === 3
                      ? {
                          bgcolor: '#ff7a18',
                          color: '#1a0a00',
                          '&:hover': { bgcolor: '#ff8c33' },
                        }
                      : undefined
                  }
                >
                  {loading ? 'Optimizing...' : 'Optimize Resume'}
                </Button>

                {topUpContext === 'optimize' && renderTopUp(formTopUpRef)}
                </>
                )}
              </Stack>
            </form>

            {loading && useStreaming && (
              <Paper elevation={2} sx={{ mt: 3, p: 3, backgroundColor: 'rgba(0,0,0,0.55)' }}>
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary">
                    <Stream sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {statusMessage || 'AI Analysis in Progress...'}
                  </Typography>
                  <LinearProgress />
                  
                  {tempJobPostingContent && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <Work sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                          Scraped Job Posting
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.45)', maxHeight: '200px', overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {tempJobPostingContent}
                          </Typography>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  
                  {streamingContent && (
                    <Box position="relative">
                      <Paper 
                        ref={streamingContentRef}
                        variant="outlined" 
                        sx={{
                          p: 2,
                          maxHeight: 400,
                          overflow: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.45)',
                          scrollBehavior: 'smooth'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {streamingContent}
                        </Typography>
                      </Paper>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute', 
                          bottom: 8, 
                          right: 8, 
                          backgroundColor: 'rgba(0,0,0,0.6)', 
                          color: 'white', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontSize: '0.7rem'
                        }}
                      >
                        Auto-scrolling enabled
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </Paper>

          {result && (
            <Box sx={{ mt: 4 }}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="h5">
                      Optimization Results
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {result.cost_usd !== undefined && (
                        <Chip
                          variant="outlined"
                          label={`Cost: ${formatCost(result.cost_usd)}`}
                          title={`${result.input_tokens ?? 0} input / ${result.output_tokens ?? 0} output tokens`}
                        />
                      )}
                      <Chip
                        label={`Match Score: ${(result.match_score * 100).toFixed(0)}%`}
                        color={getMatchScoreColor(result.match_score)}
                        icon={<CheckCircle />}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>
                    <Info /> Suggestions
                  </Typography>
                  <List>
                    {result.suggestions.map((suggestion, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={suggestion} />
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  {(() => {
                    console.log('Rendering result:', result);
                    console.log('Job posting content in render:', result.job_posting_content);
                    console.log('Job posting content length:', result.job_posting_content?.length || 0);
                    return null;
                  })()}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="h6">
                        <Work sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Scraped Job Posting
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.45)', maxHeight: '300px', overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {result.job_posting_content ? result.job_posting_content : 'Job posting content is being loaded...'}
                        </Typography>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>
                    Optimized Resume
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {result.optimized_resume}
                    </Typography>
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="contained"
                        onClick={() => {
                          const blob = new Blob([result.optimized_resume], { type: 'text/plain' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'optimized_resume.txt';
                          a.click();
                        }}
                      >
                        Download as Text
                      </Button>
                      {isPdfUpload && (() => {
                        // Pulse orange until the user builds the formatted PDF.
                        const pulseFormatted = formattedCost === null && !formattedLoading;
                        return (
                          <Button
                            ref={formattedBtnRef}
                            variant={pulseFormatted ? 'contained' : 'outlined'}
                            className={pulseFormatted ? 'guide-pulse-btn' : undefined}
                            startIcon={formattedLoading ? <CircularProgress size={18} /> : <Description />}
                            disabled={formattedLoading}
                            onClick={handleDownloadFormatted}
                            sx={
                              pulseFormatted
                                ? { bgcolor: '#ff7a18', color: '#1a0a00', '&:hover': { bgcolor: '#ff8c33' } }
                                : undefined
                            }
                          >
                            {formattedLoading
                              ? 'Building formatted PDF...'
                              : formattedPdfB64
                              ? 'Rebuild formatted PDF'
                              : 'Download formatted PDF (matches your layout)'}
                          </Button>
                        );
                      })()}
                      <Button
                        variant="outlined"
                        startIcon={docxLoadingId === 'live' ? <CircularProgress size={18} /> : <Description />}
                        disabled={docxLoadingId === 'live'}
                        title="Editable Word document — for recruiters who need to edit the resume"
                        onClick={() =>
                          downloadWord('live', result.id
                            ? { optimization_id: result.id }
                            : { resume_text: result.optimized_resume })
                        }
                      >
                        {docxLoadingId === 'live' ? 'Building Word…' : 'Download Word (.docx)'}
                      </Button>
                      {result.id && (
                        <Button
                          variant="outlined"
                          startIcon={<Description />}
                          onClick={() => openBranded(result.id!)}
                        >
                          {org?.member ? `Branded résumé (${org.org?.display_name})` : 'Branded résumé'}
                        </Button>
                      )}
                    </Stack>

                    {topUpContext === 'rebuild' && renderTopUp(resultTopUpRef)}

                    {formattedLoading && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formattedStatus || 'Building formatted PDF...'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formattedProgress}%
                          </Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={formattedProgress} />
                      </Box>
                    )}
                    {formattedPdfB64 && !formattedLoading && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Your formatted PDF is ready — rename it if you like, then download.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                          <TextField
                            size="small"
                            fullWidth
                            value={formattedFilename}
                            onChange={(e) => setFormattedFilename(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveFormattedPdf(); }}
                            placeholder="optimized_resume.pdf"
                          />
                          <Button
                            variant="contained"
                            startIcon={<Description />}
                            onClick={saveFormattedPdf}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Download
                          </Button>
                        </Stack>
                      </Box>
                    )}
                    {formattedCost !== null && !formattedLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Formatted PDF cost: {formatCost(formattedCost)}
                      </Typography>
                    )}
                    {formattedError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {formattedError}
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* SEO / informational content — real crawlable copy on the landing. */}
          <Box component="section" sx={{ mt: 6, maxWidth: 820, mx: 'auto' }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'primary.main' }}>
              How to align your resume to a job description with AI
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recruiters and applicant tracking systems (ATS) reward resumes that mirror the job
              description. Align Resume reads the posting, finds the keywords, skills and
              requirements that matter, and rewrites your resume to highlight the matching, real
              experience you already have — then scores the fit and rebuilds a formatted PDF that
              keeps your original layout.
            </Typography>
            <Box component="ol" sx={{ color: 'text.secondary', pl: 2.5, mb: 3 }}>
              <li>
                <Typography variant="body2">Upload your resume as a PDF or paste the text.</Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Paste the job posting URL (or the full job description text).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Get an AI-optimized, ATS-friendly resume aligned to the job description with a
                  match score, then download a layout-matched formatted PDF.
                </Typography>
              </li>
            </Box>

            <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'primary.main' }}>
              Loved by job seekers
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                mb: 4,
              }}
            >
              {REVIEWS.map((rv) => (
                <Card key={rv.name} variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography sx={{ color: '#ffb400', letterSpacing: 1, fontSize: 16, lineHeight: 1 }} aria-label={`${rv.stars} out of 5 stars`}>
                      {'★'.repeat(rv.stars)}{'☆'.repeat(5 - rv.stars)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, mb: 1.5, fontStyle: 'italic' }}>
                      “{rv.quote}”
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {rv.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {' '}· {rv.role}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'primary.main' }}>
              Frequently asked questions
            </Typography>
            {[
              {
                q: 'How do I tailor my resume to a job description with AI?',
                a: 'Add the job posting and your resume; the AI rewrites and reorders your resume to match the role’s keywords and requirements, scores the match, and exports a formatted PDF that keeps your original layout.',
              },
              {
                q: 'Will it help me get past ATS filters?',
                a: 'Yes — it surfaces the keywords and skills the job description asks for and weaves in your real, matching experience so an ATS scores your resume higher.',
              },
              {
                q: 'Does my field even need a tailored resume?',
                a: 'It depends on how varied your background is and how many roles you apply to. If you apply to just a handful of similar openings — many teachers, tradespeople like power linemen, or single-track roles — one strong resume is usually enough. But if you have years of experience spanning different areas — say a software engineer who has done development, testing, leadership, and data engineering — every posting emphasizes a different slice of your background. Tailoring reorders and reweights your real experience to match each job, which lifts your ATS match and recruiter relevance. The more varied your experience and the more roles you apply to, the more tailoring pays off. And since tailoring reframes experience you already have, if you are early-career with little experience the lift is smaller — it still helps align keywords and wording, but building real skills and projects matters more first.',
              },
              {
                q: 'Does the formatted PDF keep my original design?',
                a: 'Yes. The formatted PDF is rebuilt to visually match your original resume’s layout, so it looks like your document — just optimized for the role.',
              },
              {
                q: 'Is Align Resume free?',
                a: 'You get free credits every month. Paid monthly plans add more credits, and Pro adds one-click auto-apply on Dice via the Chrome extension.',
              },
            ].map((item) => (
              <Accordion key={item.q} disableGutters sx={{ background: 'transparent' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom>
            Optimization History
          </Typography>

          {apSessions.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <RocketLaunch fontSize="small" sx={{ color: '#ff7a18' }} />
                  <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Auto-pilot runs</Typography>
                </Stack>
                {(() => {
                  const totApplied = apSessions.reduce((s, r) => s + r.applied, 0);
                  const totTailored = apSessions.reduce((s, r) => s + r.tailored, 0);
                  const totCost = apSessions.reduce((s, r) => s + (r.cost_usd || 0), 0);
                  const maxApplied = Math.max(1, ...apSessions.map((r) => r.applied));
                  return (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {apSessions.length} recent run{apSessions.length === 1 ? '' : 's'} · {totApplied} applied · {totTailored} tailored
                        {totCost > 0 ? ` · ${formatCost(totCost)} API cost` : ''}
                      </Typography>
                      <Stack spacing={0.75}>
                        {apSessions.slice(0, 10).map((r) => (
                          <Box key={r.run_at}>
                            <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(r.run_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {r.applied} applied · {r.tailored} tailored
                                {r.nonremote ? ` · ${r.nonremote} not remote` : ''}
                                {r.skipped ? ` · ${r.skipped} expired/test` : ''}
                                {r.failed ? ` · ${r.failed} couldn’t load` : ''}
                                {r.cost_usd != null && r.cost_usd > 0 ? (
                                  <Box component="span" sx={{ color: BBS.green, ml: 0.5 }}
                                    title="Estimated Anthropic API cost for this run (your BYOK key), summed from the usage ledger">
                                    · {formatCost(r.cost_usd)}
                                  </Box>
                                ) : ''}
                              </Typography>
                            </Stack>
                            {r.keywords && r.keywords.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ my: 0.25 }}>
                                {r.keywords.map((k) => (
                                  <Chip key={k} size="small" variant="outlined" label={k}
                                    sx={{ height: 18, fontSize: '0.68rem', borderColor: 'rgba(255,122,24,0.5)', color: '#ff7a18' }} />
                                ))}
                              </Stack>
                            )}
                            <Box sx={{ height: 6, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${Math.round((r.applied / maxApplied) * 100)}%`, minWidth: r.applied ? 3 : 0, bgcolor: '#ff7a18', borderRadius: 1 }} />
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {usage && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Receipt fontSize="small" color="primary" />
                  <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                    This month's credits ({usage.plan_name})
                  </Typography>
                  <Chip
                    size="small"
                    color={usage.credits_remaining <= usage.monthly_credits * 0.1 ? 'warning' : 'default'}
                    label={`${usage.credits_used} of ${usage.monthly_credits} used · ${usage.credits_remaining} left${
                      usage.overage_credits ? ` (+${usage.overage_credits} overage)` : ''
                    }`}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Since {fmtDateTime(usage.period_start)} · resets monthly
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={`${usage.breakdown.optimize} · Resume optimizations`} />
                  <Chip size="small" variant="outlined" label={`${usage.breakdown.formatted_pdf} · Formatted PDFs`} />
                  {usage.breakdown.interview_prep > 0 && (
                    <Chip size="small" variant="outlined" label={`${usage.breakdown.interview_prep} · Interview prep`} />
                  )}
                  {usage.breakdown.interview_voice_review > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${usage.breakdown.interview_voice_review} · Mock interviews`}
                    />
                  )}
                </Stack>
                {/* Profit: credit value of usage − true LLM cost this period. */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <Chip size="small" variant="outlined" label={`Revenue: ${formatCost(usage.revenue_usd)}`}
                    title="Credit value of what was used this period" />
                  <Chip size="small" variant="outlined" label={`LLM cost: ${formatCost(usage.llm_cost_usd)}`}
                    title="Underlying LLM cost of this period's usage" />
                  <Chip size="small" color={usage.profit_usd >= 0 ? 'success' : 'error'}
                    label={`Profit: ${formatCost(usage.profit_usd)}${usage.margin_pct != null ? ` · ${usage.margin_pct}% margin` : ''}`} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Revenue values credits at {formatCost(usage.credit_value)} each; profit = revenue − LLM cost
                  (excludes voice/Deepgram cost).
                </Typography>
                {usage.credits_remaining <= usage.monthly_credits * 0.1 && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    You're nearly out for this period — upgrade or add credits on the <b>Plans</b> tab.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Account identity (parsed from resumes / Stripe) + payment history */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Person fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  {balance?.name || 'Guest'}
                  {balance?.email ? `  <${balance.email}>` : ''}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {balance && (
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Balance: ${formatMoney(balance.balance_usd, balance.currency)}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${balance.free_tries_remaining}/${balance.free_tries_total} free tries left`}
                    />
                  </>
                )}
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Receipt fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                  Payment History
                </Typography>
              </Stack>
              {payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments yet.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {payments.map((p, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      justifyContent="space-between"
                      sx={{ borderBottom: '1px dashed', borderColor: 'divider', py: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {fmtDateTime(p.processed_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.main' }}>
                        + {formatMoney(p.amount, p.currency)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

            </CardContent>
          </Card>

          {/* Search history (e.g. find a job you auto-applied to after an interview reply). */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small" fullWidth
              placeholder="Search by company, job title, or URL…"
              value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchHistory(false); }}
            />
            <Button variant="outlined" onClick={() => fetchHistory(false)}>Search</Button>
            {historyQuery && (
              <Button color="inherit" onClick={() => { setHistoryQuery(''); fetchHistory(false, ''); }}>Clear</Button>
            )}
          </Stack>

          {history.length === 0 ? (
            <Typography color="text.secondary">
              {historyQuery ? `No matches for “${historyQuery}”.` : 'No optimization history yet.'}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {history.map((item) => (
                <Card key={item.id}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6">
                        Optimization from {fmtDateTime(item.created_at)}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {/* Which auto-pilot session produced this card (manual = none).
                            Shows the run's keywords when known, else the run time. */}
                        {item.autopilot_run_at && (
                          <Chip
                            variant="filled"
                            size="small"
                            icon={<RocketLaunch />}
                            label={`Auto-pilot${item.autopilot_keywords ? ` · ${item.autopilot_keywords}` : ''} · ${new Date(item.autopilot_run_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                            sx={{ bgcolor: 'rgba(255,122,24,0.18)', color: '#ff7a18' }}
                            title={`Created by an auto-pilot run${item.autopilot_keywords ? ` searching "${item.autopilot_keywords}"` : ''}`}
                          />
                        )}
                        {/* One chip per credit-consuming op (CREDIT_COST: optimize=1, rebuild=1). */}
                        <Chip
                          variant="outlined"
                          size="small"
                          color="primary"
                          label="1 credit · Resume optimization"
                        />
                        {item.has_formatted_pdf && (
                          <Chip
                            variant="outlined"
                            size="small"
                            color="primary"
                            label="1 credit · Formatted PDF"
                          />
                        )}
                        {duplicateOptIds.has(item.id) && (
                          <Chip
                            variant="outlined"
                            size="small"
                            color="warning"
                            icon={<Info />}
                            label="Duplicate — same job re-optimized"
                            title="An earlier optimization already exists for this job URL (a duplicate run before reuse was added)."
                          />
                        )}
                        {/* Profit on the optimization op: 1 credit's value − the
                            true LLM cost (raw_cost_usd). */}
                        {(() => {
                          const creditValue = usage?.credit_value ?? 30 / 180;
                          const cost = item.raw_cost_usd ?? 0;
                          const profit = creditValue - cost;
                          return (
                            <>
                              <Chip
                                variant="outlined"
                                size="small"
                                label={`Cost: ${formatCost(cost)}`}
                                title="Underlying LLM cost for this optimization"
                              />
                              <Chip
                                variant="outlined"
                                size="small"
                                color={profit >= 0 ? 'success' : 'error'}
                                label={`Profit: ${formatCost(profit)}`}
                                title={`1 credit (${formatCost(creditValue)}) − LLM cost (${formatCost(cost)})`}
                              />
                            </>
                          );
                        })()}
                        <Chip
                          label={`Score: ${(item.match_score * 100).toFixed(0)}%`}
                          color={getMatchScoreColor(item.match_score)}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                    {item.job_url && item.job_url !== 'Pasted job content' && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <Work sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                        {/^https?:\/\//i.test(item.job_url) ? (
                          <a
                            href={item.job_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ wordBreak: 'break-all' }}
                          >
                            {item.job_title || item.job_url}
                          </a>
                        ) : (
                          item.job_title || item.job_url
                        )}
                        {item.company_name ? ` · ${item.company_name}` : ''}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {item.suggestions.length} suggestions provided
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() =>
                          downloadText(item.optimized_resume, 'optimized_resume.txt')
                        }
                      >
                        Download text
                      </Button>
                      {item.has_formatted_pdf && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            historyPdfLoadingId === item.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Description />
                            )
                          }
                          disabled={historyPdfLoadingId === item.id}
                          onClick={() => downloadHistoryFormatted(item.id)}
                        >
                          Download formatted PDF
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={docxLoadingId === item.id ? <CircularProgress size={16} /> : <Description />}
                        disabled={docxLoadingId === item.id}
                        title="Editable Word document — for recruiters who need to edit the resume"
                        onClick={() => downloadWord(item.id, { optimization_id: item.id })}
                      >
                        {docxLoadingId === item.id ? 'Building Word…' : 'Download Word (.docx)'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() => openBranded(item.id)}
                      >
                        {org?.member ? `Branded résumé (${org.org?.display_name})` : 'Branded résumé'}
                      </Button>
                      <Button
                        size="small"
                        variant={item.interview ? 'contained' : 'outlined'}
                        color="success"
                        startIcon={item.interview ? <CheckCircle /> : <Work />}
                        onClick={() => markHistoryInterview(item)}
                      >
                        {item.interview ? 'Interview ✓' : 'Got an interview'}
                      </Button>
                    </Stack>

                    <Accordion sx={{ mt: 2, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                          View details
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {item.suggestions.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 0.5 }}>
                              Suggestions
                            </Typography>
                            <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
                              {item.suggestions.map((s, i) => (
                                <li key={i}>
                                  <Typography variant="body2">{s}</Typography>
                                </li>
                              ))}
                            </Box>
                          </>
                        )}

                        {item.job_posting_content && (
                          <>
                            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 0.5 }}>
                              Scraped Job Posting
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{ p: 2, mb: 2, maxHeight: 240, overflow: 'auto', backgroundColor: 'rgba(0,0,0,0.45)' }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {item.job_posting_content}
                              </Typography>
                            </Paper>
                          </>
                        )}

                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 0.5 }}>
                          Optimized Resume
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, maxHeight: 320, overflow: 'auto', backgroundColor: 'rgba(0,0,0,0.45)' }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {item.optimized_resume}
                          </Typography>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
              {historyHasMore && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Button onClick={() => fetchHistory(true)} disabled={historyLoadingMore}>
                    {historyLoadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                </Box>
              )}
            </Stack>
          )}

          {interviewHistory.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
                Interview prep &amp; mock interviews
              </Typography>
              <Stack spacing={1}>
                {interviewHistory.map((it, i) => (
                  <Card key={i}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={1}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            color={it.type === 'mock_interview' ? 'secondary' : 'primary'}
                            label={it.type === 'mock_interview' ? 'Mock interview' : 'Interview prep'}
                          />
                          <Typography variant="body2">
                            {it.job_title || 'General practice'}
                            {it.company_name ? ` — ${it.company_name}` : ''}
                          </Typography>
                          {typeof it.score === 'number' && (
                            <Chip size="small" variant="outlined" label={`${it.score}/100`} />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {fmtDateTime(it.created_at)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>
                            {it.credits} credit{it.credits === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" gutterBottom>
            Dice Job Search
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Find roles on Dice and queue them here ranked by fit. Click Apply for a true
            one-click auto-submit: the ResumeAlign Chrome extension optimizes your resume,
            generates a formatted PDF based on your original resume, attaches the PDF, and
            submits the application for you. Requires the ResumeAlign Chrome extension
            installed and connected, and that you’re signed in to your Dice account. Works
            on a computer only — the Chrome extension can’t be installed on mobile.
          </Typography>

          {jobNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJobNotice('')}>
              {jobNotice}
            </Alert>
          )}
          {jobError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError('')}>
              {jobError}
            </Alert>
          )}

          {/* Auto-pilot */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  🤖 Auto-pilot
                </Typography>
                <Chip size="small" variant="outlined" label="Beta" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                On a daily schedule, the extension searches Dice for your criteria, verifies
                each posting is <b>truly remote</b> (Dice often mislabels hybrid), tailors your
                resume, and auto-applies — up to your daily cap. Requires the Chrome extension
                running and you signed in to Dice. Automated applying may conflict with Dice’s
                terms; use at your own discretion.
              </Typography>
              {prefs && (
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!prefs.autopilot_enabled}
                        onChange={(e) => setPrefs({ ...prefs, autopilot_enabled: e.target.checked })}
                      />
                    }
                    label="Enable auto-pilot"
                  />
                  {/* Search schedules: each runs at its own time with its own keywords. */}
                  <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                    Search schedules
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Each schedule runs once a day at its time (on the days below). Add different times
                    with different keywords to widen your reach — your daily apply cap is shared across them.
                  </Typography>
                  {(() => {
                    const schedules: any[] = Array.isArray(prefs.autopilot_schedules) && prefs.autopilot_schedules.length
                      ? prefs.autopilot_schedules
                      : [{ id: 'default', time: prefs.autopilot_time || '09:00', keywords: prefs.keywords || '', location: prefs.location || '' }];
                    const setS = (next: any[]) => setPrefs({ ...prefs, autopilot_schedules: next });
                    const upd = (i: number, patch: any) => setS(schedules.map((s: any, j: number) => (j === i ? { ...s, ...patch } : s)));
                    const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().slice(0, 8) : String(Date.now()));
                    return (
                      <Stack spacing={1.5}>
                        {schedules.map((s: any, i: number) => (
                          <Stack key={s.id || i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <TextField size="small" select label="Run at" value={s.time || '09:00'}
                              onChange={(e) => upd(i, { time: e.target.value })} sx={{ width: 130 }}>
                              {(TIME_OPTIONS.includes(s.time || '09:00') ? TIME_OPTIONS : [s.time, ...TIME_OPTIONS]).map((t: string) => (
                                <MenuItem key={t} value={t}>{fmtTime(t)}</MenuItem>
                              ))}
                            </TextField>
                            <TextField size="small" label="Keywords" placeholder="e.g. remote java developer"
                              value={s.keywords || ''} onChange={(e) => upd(i, { keywords: e.target.value })}
                              sx={{ flex: 1, minWidth: 160 }} />
                            <TextField size="small" label="Location" placeholder="optional"
                              value={s.location || ''} onChange={(e) => upd(i, { location: e.target.value })}
                              sx={{ width: 150 }} />
                            <Button size="small" color="inherit" disabled={schedules.length <= 1}
                              onClick={() => setS(schedules.filter((_: any, j: number) => j !== i))}>
                              Remove
                            </Button>
                          </Stack>
                        ))}
                        {schedules.length < 6 && (
                          <Button size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }}
                            onClick={() => setS([...schedules, { id: newId(), time: '09:00', keywords: '', location: '' }])}>
                            + Add another time
                          </Button>
                        )}
                      </Stack>
                    );
                  })()}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={prefs.remote_only !== false}
                        onChange={(e) => setPrefs({ ...prefs, remote_only: e.target.checked })}
                      />
                    }
                    label="Remote only (skip verified hybrid/on-site)"
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Max auto-applies per day"
                    helperText="Up to 10000 (effectively unlimited)"
                    value={prefs.daily_apply_cap ?? 20}
                    onChange={(e) =>
                      setPrefs({ ...prefs, daily_apply_cap: Math.max(0, Math.min(10000, Number(e.target.value) || 0)) })
                    }
                    inputProps={{ min: 0, max: 10000 }}
                    sx={{ maxWidth: 220 }}
                  />

                  {/* Timezone + days apply to all schedules above. */}
                  <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                    Timezone &amp; days
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      size="small"
                      label="Timezone"
                      value={prefs.autopilot_timezone || BROWSER_TZ}
                      onChange={(e) => setPrefs({ ...prefs, autopilot_timezone: e.target.value })}
                      sx={{ minWidth: 220 }}
                    >
                      {TIMEZONES.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz === BROWSER_TZ ? `${tz} (your timezone)` : tz}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Days
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {DAY_LABELS.map((label, i) => {
                        const days: number[] = Array.isArray(prefs.autopilot_days)
                          ? prefs.autopilot_days
                          : [1, 2, 3, 4, 5];
                        const on = days.includes(i);
                        return (
                          <Chip
                            key={i}
                            label={label}
                            size="small"
                            color={on ? 'primary' : 'default'}
                            variant={on ? 'filled' : 'outlined'}
                            onClick={() =>
                              setPrefs({
                                ...prefs,
                                autopilot_days: on
                                  ? days.filter((d) => d !== i)
                                  : [...days, i].sort((a, b) => a - b),
                              })
                            }
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Runs once per selected day at/after the chosen time, while Chrome is open and
                    the extension is connected. Checked every ~15 minutes.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      onClick={saveAutopilot}
                      disabled={autopilotSaving}
                      startIcon={autopilotSaving ? <CircularProgress size={16} /> : undefined}
                    >
                      Save auto-pilot settings
                    </Button>
                    <Button
                      variant="outlined"
                      data-tour="run-autopilot"
                      onClick={(e) => setRunMenuAnchor(e.currentTarget)}
                      disabled={autopilotRunningNow || !extDetected}
                      startIcon={autopilotRunningNow ? <CircularProgress size={16} /> : <RocketLaunch />}
                      endIcon={<ExpandMore />}
                      title={extDetected ? 'Run a one-off auto-pilot pass now' : 'Connect the Chrome extension first'}
                    >
                      {autopilotRunningNow ? 'Running…' : 'Run now'}
                    </Button>
                    {(() => {
                      const scheds: any[] = Array.isArray(prefs.autopilot_schedules) && prefs.autopilot_schedules.length
                        ? prefs.autopilot_schedules
                        : [{ id: 'default', time: prefs.autopilot_time || '09:00', keywords: prefs.keywords || '', location: prefs.location || '' }];
                      const close = () => setRunMenuAnchor(null);
                      return (
                        <Menu anchorEl={runMenuAnchor} open={!!runMenuAnchor} onClose={close}>
                          {scheds.length > 1 && (
                            <MenuItem onClick={() => { close(); runAutopilotNow({ searches: scheds.map((s) => ({ keywords: s.keywords, location: s.location })) }); }}>
                              ▶ Run all {scheds.length} schedules now
                            </MenuItem>
                          )}
                          {scheds.length > 1 && <Divider />}
                          {scheds.filter((s) => s.keywords).map((s, i) => (
                            <MenuItem key={s.id || i}
                              onClick={() => { close(); runAutopilotNow({ keywords: s.keywords, location: s.location }); }}>
                              {fmtTime(s.time)} — {s.keywords}
                            </MenuItem>
                          ))}
                        </Menu>
                      );
                    })()}
                    {(autopilotRunningNow || massBusy || autopilotActive) && (
                      <Button variant="outlined" color="error" onClick={stopAutopilot}>
                        Stop auto-pilot
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={reevaluateDismissed}
                      disabled={reevaluating}
                      title="Retry jobs that hit a temporary error (scrape/tailor failure). Jobs judged non-remote, expired, or fake stay dismissed so they aren't re-analyzed."
                    >
                      {reevaluating ? 'Retrying…' : 'Retry failed jobs'}
                    </Button>
                  </Stack>
                  {autopilotRunMsg && (
                    <Typography variant="caption" color="text.secondary">
                      {autopilotRunMsg}
                    </Typography>
                  )}
                  {massApply && massApply.items.length > 0 && (() => {
                    const total = massApply.items.length;
                    const failed = massApply.items.filter((it) => it.status === 'error').length;
                    const skip = failed > 0 ? ` · ${failed} skipped` : '';
                    let summary: string;
                    if (massApply.phase === 'applying') {
                      const done = massApply.items.filter((it) => it.status === 'done').length;
                      summary = `Applied ${done} of ${total}${skip}`;
                    } else {
                      const prep = total - failed;
                      summary = `Preparing ${prep} résumé${prep === 1 ? '' : 's'}…${skip}`;
                    }
                    return (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {summary}
                        </Typography>
                        {!massOpen && (
                          <Button size="small" onClick={() => setMassOpen(true)}>
                            Details
                          </Button>
                        )}
                      </Stack>
                    );
                  })()}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* 1 — Base résumés (one truthful framing per role-type) */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>
                1 · Base résumés
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Keep one résumé per role-type (e.g. Data Engineering, ML / AI, Java backend). Each
                job is auto-matched to the best-fit résumé — add keyword tags to steer the choice.
              </Typography>

              {/* Master CV — the full source résumé, shown separately above role profiles */}
              {(() => {
                const master = baseProfiles.find((p) => p.is_master);
                if (!master || profileEditorOpen) return null;
                return (
                  <Box
                    sx={{
                      p: 1.25, mb: 1.5, border: '1px solid', borderColor: 'primary.main',
                      borderRadius: 1, bgcolor: 'action.hover',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <WorkspacePremium color="primary" fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {master.label || 'Master CV'}
                      </Typography>
                      <Chip size="small" color="primary" label="Master CV" />
                      {master.filename && (
                        <Typography variant="caption" color="text.secondary">
                          {master.filename}
                        </Typography>
                      )}
                      <Box sx={{ flexGrow: 1 }} />
                      <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openProfileEditor(master)}>
                        Edit / replace
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline fontSize="small" />}
                        onClick={() => deleteProfile(master)}
                      >
                        Delete
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Your full CV — the source for role profiles. Not used directly to tailor jobs.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mt: 1 }}
                      startIcon={generatingProfiles ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome fontSize="small" />}
                      onClick={generateRoleProfiles}
                      disabled={generatingProfiles}
                    >
                      {generatingProfiles ? 'Generating role profiles…' : 'Generate role profiles from master CV'}
                    </Button>
                    {generatingProfiles && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        This runs several AI calls — it can take a minute.
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Saved role profiles (masters excluded — they're shown above) */}
              {baseProfiles.some((p) => !p.is_master) && !profileEditorOpen && (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {baseProfiles.filter((p) => !p.is_master).map((p) => (
                    <Box
                      key={p.id}
                      sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.label || 'Untitled'}
                        </Typography>
                        {p.is_default && <Chip size="small" color="primary" variant="outlined" label="default" />}
                        {p.filename && (
                          <Typography variant="caption" color="text.secondary">
                            {p.filename}
                          </Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openProfileEditor(p)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline fontSize="small" />}
                          onClick={() => deleteProfile(p)}
                        >
                          Delete
                        </Button>
                      </Stack>
                      {p.keywords.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {p.keywords.map((k) => (
                            <Chip key={k} size="small" variant="outlined" label={k} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Editor (add or edit a profile) */}
              {profileEditorOpen ? (
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {profileEditId
                      ? (profileMasterDraft ? 'Edit master CV' : 'Edit résumé')
                      : 'Add a résumé for another role'}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label (e.g. ML / AI, Java backend)"
                    value={profileLabelDraft}
                    onChange={(e) => setProfileLabelDraft(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Keyword tags (comma-separated) — steer auto-match"
                    placeholder="machine learning, pytorch, llm"
                    value={profileKeywordsDraft}
                    onChange={(e) => setProfileKeywordsDraft(e.target.value)}
                    disabled={profileMasterDraft}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={profileMasterDraft}
                        onChange={(e) => setProfileMasterDraft(e.target.checked)}
                      />
                    }
                    label="This is my full master CV (source for role profiles)"
                    sx={{ mb: 0.5, display: 'block' }}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
                    <Button variant="outlined" size="small" component="label" startIcon={<CloudUpload />} disabled={savingProfile}>
                      Upload PDF / TXT
                      <input hidden type="file" accept=".pdf,.txt" onChange={uploadProfile} />
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    …or paste the résumé text below:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    value={baseResumeDraft}
                    onChange={(e) => setBaseResumeDraft(e.target.value)}
                    placeholder="Paste your resume text…"
                    sx={{ mt: 0.5 }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button variant="contained" size="small" onClick={saveProfile} disabled={savingProfile}>
                      Save résumé
                    </Button>
                    <Button size="small" onClick={closeProfileEditor} disabled={savingProfile}>
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openProfileEditor()}>
                    {baseProfiles.some((p) => !p.is_master) ? 'Add a résumé for another role' : 'Add your résumé'}
                  </Button>
                  {history.length > 0 && (
                    <Button variant="outlined" size="small" startIcon={<Description />} onClick={useLatestResume}>
                      Use my most recent resume
                    </Button>
                  )}
                </Stack>
              )}

              {baseProfiles.length > 0 && !baseProfiles.some((p) => p.has_pdf) && !profileEditorOpen && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Tip: upload a résumé as a <strong>PDF</strong> to get layout-matched
                  “Download formatted PDF” on each optimized job.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 2 — Find jobs */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>
                2 · Find jobs on Dice
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  fullWidth
                  label="Keywords"
                  placeholder="e.g. senior python engineer"
                  value={jobKeywords}
                  onChange={(e) => setJobKeywords(e.target.value)}
                  sx={{ flex: { sm: 1 }, minWidth: { sm: 180 } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Location"
                  placeholder="e.g. Remote"
                  value={jobLocation}
                  onChange={(e) => setJobLocation(e.target.value)}
                  sx={{ flex: { sm: 1 }, minWidth: { sm: 140 } }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Min. salary (USD)"
                  placeholder="e.g. 120000"
                  value={jobMinSalary}
                  onChange={(e) => setJobMinSalary(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}
                  inputProps={{ min: 0, step: 1000 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Work />}
                  onClick={searchOnDice}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Search
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Opens Dice in a new tab. On the results page, use the{' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => handleTabChange(null, 3)}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  ResumeAlign extension
                </MuiLink>{' '}
                to scan listings into the queue, then Refresh below. (Or add them manually.)
              </Typography>
              <Accordion sx={{ mt: 1 }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">Add jobs manually (Dice or ATS URLs)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={'One job URL per line (Dice, Greenhouse, Lever, Ashby…):\nhttps://jobs.lever.co/acme/… | Title | Company'}
                    value={seedText}
                    onChange={(e) => setSeedText(e.target.value)}
                  />
                  <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={seedLeads}>
                    Add to queue
                  </Button>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          {/* 3 — Queue */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
              3 · Your queue
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={leadSort}
                onChange={(e, v) => { if (v) { setLeadSort(v); setLeadPage(0); } }}
              >
                <ToggleButton value="new">Newest</ToggleButton>
                <ToggleButton value="score">Best match</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" onClick={fetchJobLeads}>
                Refresh
              </Button>
            </Stack>
          </Stack>

          {/* Undo bar — shown after a mass dismiss, even if the toolbar is hidden */}
          {dismissUndo && dismissUndo.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Dismissed {dismissUndo.length} job(s).
              </Typography>
              <Button size="small" color="warning" variant="outlined" onClick={undoDismiss}>
                Undo
              </Button>
            </Stack>
          )}

          {/* Multi-select mass-apply toolbar */}
          {(() => {
            const selectable = jobLeads.filter((l) => l.status !== 'dismissed' && l.status !== 'applied' && l.source !== 'indeed');
            if (!selectable.length) return null;
            const allSelected = selectable.every((l) => selectedLeads.has(l.id));
            return (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedLeads(allSelected ? new Set() : new Set(selectable.map((l) => l.id)))
                  }
                >
                  {allSelected ? 'Clear selection' : `Select all (${selectable.length})`}
                </Button>
                <TextField
                  size="small"
                  type="number"
                  label="Delay (s)"
                  value={massDelaySec}
                  onChange={(e) => setMassDelaySec(Math.max(0, Math.min(600, Number(e.target.value) || 0)))}
                  inputProps={{ min: 0, max: 600 }}
                  sx={{ width: 110 }}
                  helperText="between applies"
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<RocketLaunch />}
                  disabled={selectedLeads.size === 0 || massBusy}
                  onClick={() => runMassApply()}
                >
                  Apply {selectedLeads.size} selected
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircle />}
                  disabled={selectedLeads.size === 0}
                  onClick={markSelectedApplied}
                >
                  Mark {selectedLeads.size} applied
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  disabled={selectedLeads.size === 0}
                  onClick={dismissSelected}
                >
                  Dismiss {selectedLeads.size} selected
                </Button>
                {massApply && !massOpen && (
                  <Button size="small" onClick={() => setMassOpen(true)}>
                    Show progress
                  </Button>
                )}
              </Stack>
            );
          })()}

          {(() => {
            // Dice tab shows only Dice leads. Indeed/LinkedIn/ZipRecruiter each
            // have their own tab (with their own applier + management controls);
            // they used to leak in here because the filter only excluded Indeed.
            const active = jobLeads.filter((l) => l.status !== 'dismissed' && !DICE_APPLY_EXCLUDE.includes(l.source || ''));
            if (active.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  No jobs queued yet. Search on Dice and add some.
                </Typography>
              );
            }
            const sorted = [...active].sort((a, b) => {
              // Not-yet-applied jobs first (so the work-to-do is on page 1);
              // applied jobs sink to the end. Within each group keep the chosen sort.
              const aDone = a.status === 'applied' ? 1 : 0;
              const bDone = b.status === 'applied' ? 1 : 0;
              if (aDone !== bDone) return aDone - bDone;
              return leadSort === 'new'
                ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                : b.score - a.score;
            });
            const pageCount = Math.ceil(sorted.length / LEADS_PER_PAGE);
            const page = Math.min(leadPage, pageCount - 1);
            const visible = sorted.slice(page * LEADS_PER_PAGE, page * LEADS_PER_PAGE + LEADS_PER_PAGE);
            return (
              <>
                <Stack spacing={1.5}>
                  {visible.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        {lead.status !== 'applied' && (
                          <Checkbox
                            size="small"
                            sx={{ p: 0.5, mt: -0.5 }}
                            checked={selectedLeads.has(lead.id)}
                            onChange={() => toggleSelectLead(lead.id)}
                            inputProps={{ 'aria-label': `Select ${cleanTitle(lead.title) || lead.title}` }}
                          />
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700, color: BBS.cyan, textShadow: `0 0 6px ${BBS.cyan}55` }}
                            >
                              {cleanTitle(lead.title) || lead.title}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: BBS.magenta }}>
                            {[lead.company, lead.location].filter(Boolean).join(' · ')}
                          </Typography>
                          {(lead.posted || lead.updated) && (
                            <Typography variant="caption" sx={{ display: 'block', color: BBS.yellow }}>
                              {[lead.posted, lead.updated].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Added {fmtDateTime(lead.created_at)}
                          </Typography>
                          {(lead.easy_apply || lead.employment_type || lead.workplace_type || lead.salary) && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {lead.easy_apply && (
                                <Chip size="small" variant="outlined" label="Easy Apply" sx={neon(BBS.green)} />
                              )}
                              {lead.employment_type && (
                                <Chip size="small" variant="outlined" label={lead.employment_type} sx={neon(BBS.magenta)} />
                              )}
                              {lead.workplace_type && (
                                <Chip size="small" variant="outlined" label={lead.workplace_type} sx={neon(BBS.cyan)} />
                              )}
                              {lead.salary && (
                                <Chip size="small" variant="outlined" label={lead.salary} sx={neon(BBS.yellow)} />
                              )}
                            </Stack>
                          )}
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 0.5 }}
                          >
                            {lead.matched_keywords.slice(0, 6).map((k, i) => (
                              <Chip
                                key={k}
                                size="small"
                                variant="outlined"
                                label={k}
                                sx={neon(BBS_CYCLE[i % BBS_CYCLE.length])}
                              />
                            ))}
                          </Stack>
                        </Box>
                        {(() => {
                          const cur = Math.round(lead.score * 100);
                          const old =
                            lead.initial_score != null ? Math.round(lead.initial_score * 100) : null;
                          // Show the pre-optimization score struck through once it changed.
                          const changed = !!lead.optimization_id && old != null && old !== cur;
                          return (
                            <Chip
                              size="small"
                              variant="outlined"
                              sx={neon(lead.score >= 0.6 ? BBS.green : lead.score >= 0.3 ? BBS.cyan : BBS.blue)}
                              label={
                                changed ? (
                                  <Box component="span">
                                    <Box
                                      component="span"
                                      sx={{ textDecoration: 'line-through', opacity: 0.55, mr: 0.5 }}
                                    >
                                      {old}%
                                    </Box>
                                    {cur}% match
                                  </Box>
                                ) : (
                                  `${cur}% match`
                                )
                              }
                            />
                          );
                        })()}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                        {lead.status === 'applied' ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Link />}
                              href={lead.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </Button>
                            {lead.optimization_id && (
                              <>
                                <Chip
                                  size="small"
                                  color="success"
                                  icon={<CheckCircle />}
                                  label={`Optimized${
                                    lead.match_score != null
                                      ? ` · ${Math.round(lead.match_score * 100)}% fit`
                                      : ''
                                  }`}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    leadDownloadId === lead.id ? <CircularProgress size={16} /> : <Description />
                                  }
                                  disabled={leadDownloadId === lead.id}
                                  onClick={() => downloadLeadResume(lead)}
                                >
                                  Download text
                                </Button>
                                {hasBaseResumePdf && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={
                                      leadPdfId === lead.id ? <CircularProgress size={16} /> : <Description />
                                    }
                                    disabled={leadPdfId === lead.id}
                                    onClick={() => downloadLeadFormatted(lead)}
                                  >
                                    {leadPdfId === lead.id ? 'Building…' : 'Download formatted PDF'}
                                  </Button>
                                )}
                              </>
                            )}
                            <Chip size="small" label="Applied" />
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Link />}
                              href={lead.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<RocketLaunch />}
                              disabled={!!autoApply && autoApply.leadId === lead.id}
                              onClick={() => autoApplyLead(lead)}
                            >
                              Apply
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => setLeadStatus(lead.id, 'applied')}
                              title="Mark this job as applied (e.g. you finished it manually)"
                            >
                              Mark applied
                            </Button>
                            {/* Tailored-but-not-applied leads: let the user review the
                                résumé before applying (the formatted PDF already exists). */}
                            {lead.optimization_id && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={leadDownloadId === lead.id ? <CircularProgress size={16} /> : <Description />}
                                  disabled={leadDownloadId === lead.id}
                                  onClick={() => downloadLeadResume(lead)}
                                >
                                  Download text
                                </Button>
                                {hasBaseResumePdf && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={leadPdfId === lead.id ? <CircularProgress size={16} /> : <Description />}
                                    disabled={leadPdfId === lead.id}
                                    onClick={() => downloadLeadFormatted(lead)}
                                  >
                                    {leadPdfId === lead.id ? 'Building…' : 'Preview formatted PDF'}
                                  </Button>
                                )}
                              </>
                            )}
                          </>
                        )}
                        {lead.interview && (
                          <Chip
                            size="small"
                            color="secondary"
                            icon={<EventAvailable />}
                            label="Interview"
                          />
                        )}
                        <Button
                          size="small"
                          color={lead.interview ? 'secondary' : 'inherit'}
                          startIcon={<EventAvailable />}
                          onClick={() => setLeadInterview(lead.id, !lead.interview)}
                        >
                          {lead.interview ? 'Remove interview' : 'I have an interview'}
                        </Button>
                        <Button size="small" color="inherit" onClick={() => setLeadStatus(lead.id, 'dismissed')}>
                          Dismiss
                        </Button>
                        {lead.status !== 'applied' && (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => setLeadStatus(lead.id, 'dismissed', 'expired')}
                            title="Posting is gone — removes it and won't be restored by Re-check dismissed"
                          >
                            No longer available
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                  ))}
                </Stack>
                {pageCount > 1 && (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="small" disabled={page === 0} onClick={() => setLeadPage(page - 1)}>
                      Prev
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Page {page + 1} of {pageCount} · {sorted.length} jobs
                    </Typography>
                    <Button size="small" disabled={page >= pageCount - 1} onClick={() => setLeadPage(page + 1)}>
                      Next
                    </Button>
                  </Stack>
                )}
              </>
            );
          })()}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Typography variant="h5" gutterBottom>Indeed Job Search</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scan Indeed's <strong>“Easily apply”</strong> jobs, tailor your résumé to each, and let the
            extension drive the Indeed application — it fills everything and stops at the reCAPTCHA for you
            to clear + Submit. This queue is separate from your Dice queue.
          </Typography>
          {jobError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError('')}>{jobError}</Alert>}
          {jobNotice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setJobNotice('')}>{jobNotice}</Alert>}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Scan Indeed</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small" label="Keywords" value={indeedKw}
                  onChange={(e) => setIndeedKw(e.target.value)} placeholder="data engineer"
                  sx={{ minWidth: 240 }}
                />
                <Button variant="contained" onClick={scanIndeed} disabled={!extDetected || indeedBusy}
                  startIcon={indeedBusy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  {indeedBusy ? 'Scanning…' : 'Scan Indeed'}
                </Button>
              </Stack>
              {!extDetected && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Connect the Chrome extension (Chrome Auto-Fill Extension tab) and sign in to Indeed to scan.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Indeed queue</Typography>
            <Button size="small" onClick={() => fetchJobLeads()}>Refresh</Button>
          </Stack>
          {(() => {
            const indeedLeads = jobLeads.filter((l) => l.status !== 'dismissed' && l.source === 'indeed');
            if (!indeedLeads.length) {
              return (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No Indeed jobs yet — scan above to fill this queue.
                </Typography>
              );
            }
            return (
              <Stack spacing={1}>
                {indeedLeads.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>{cleanTitle(lead.title)}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {[lead.company, lead.location].filter(Boolean).join(' · ')}
                          </Typography>
                          <Chip size="small" label={lead.status} sx={{ mt: 0.5 }}
                            color={lead.status === 'applied' ? 'success' : lead.status === 'tailored' ? 'primary' : 'default'} />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                          <Button size="small" href={lead.url} target="_blank" rel="noopener">View</Button>
                          {lead.status === 'applied' ? (
                            <Button size="small" disabled>Applied ✓</Button>
                          ) : (
                            <Button size="small" variant="contained" disabled={!extDetected}
                              onClick={() => indeedApplyLead(lead)}>Apply</Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            );
          })()}
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <Typography variant="h5" gutterBottom>LinkedIn Job Search</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scan LinkedIn's <strong>“Easy Apply”</strong> jobs, tailor your résumé to each, and let the
            extension click Easy Apply and drive the application — it fills everything and stops at the
            review step for you to Submit. This queue is separate from your Dice and Indeed queues.
          </Typography>
          {jobError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError('')}>{jobError}</Alert>}
          {jobNotice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setJobNotice('')}>{jobNotice}</Alert>}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Scan LinkedIn</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small" label="Keywords" value={linkedinKw}
                  onChange={(e) => setLinkedinKw(e.target.value)} placeholder="data engineer"
                  sx={{ minWidth: 240 }}
                />
                <Button variant="contained" onClick={scanLinkedIn} disabled={!extDetected || linkedinBusy}
                  startIcon={linkedinBusy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  {linkedinBusy ? 'Scanning…' : 'Scan LinkedIn'}
                </Button>
              </Stack>
              {!extDetected && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Connect the Chrome extension (Chrome Auto-Fill Extension tab) and sign in to LinkedIn to scan.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">LinkedIn queue</Typography>
            <Button size="small" onClick={() => fetchJobLeads()}>Refresh</Button>
          </Stack>
          {(() => {
            const linkedinLeads = jobLeads.filter((l) => l.status !== 'dismissed' && l.source === 'linkedin');
            if (!linkedinLeads.length) {
              return (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No LinkedIn jobs yet — scan above to fill this queue.
                </Typography>
              );
            }
            return (
              <Stack spacing={1}>
                {linkedinLeads.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>{cleanTitle(lead.title)}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {[lead.company, lead.location].filter(Boolean).join(' · ')}
                          </Typography>
                          <Chip size="small" label={lead.status} sx={{ mt: 0.5 }}
                            color={lead.status === 'applied' ? 'success' : lead.status === 'tailored' ? 'primary' : 'default'} />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                          <Button size="small" href={lead.url} target="_blank" rel="noopener">View</Button>
                          {lead.status === 'applied' ? (
                            <Button size="small" disabled>Applied ✓</Button>
                          ) : (
                            <Button size="small" variant="contained" disabled={!extDetected}
                              onClick={() => linkedinApplyLead(lead)}>Apply</Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            );
          })()}
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <Typography variant="h5" gutterBottom>ZipRecruiter Job Search</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scan ZipRecruiter's <strong>“Quick Apply”</strong> jobs, tailor your résumé to each, and let the
            extension click Quick Apply and drive the application. ZipRecruiter has no distinct final review
            screen, so it stops before Continue/Submit at every step past the résumé upload for you to click.
            This queue is separate from your Dice, Indeed, and LinkedIn queues.
          </Typography>
          {jobError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError('')}>{jobError}</Alert>}
          {jobNotice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setJobNotice('')}>{jobNotice}</Alert>}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Scan ZipRecruiter</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small" label="Keywords" value={ziprecruiterKw}
                  onChange={(e) => setZiprecruiterKw(e.target.value)} placeholder="data engineer"
                  sx={{ minWidth: 240 }}
                />
                <Button variant="contained" onClick={scanZipRecruiter} disabled={!extDetected || ziprecruiterBusy}
                  startIcon={ziprecruiterBusy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  {ziprecruiterBusy ? 'Scanning…' : 'Scan ZipRecruiter'}
                </Button>
              </Stack>
              {!extDetected && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Connect the Chrome extension (Chrome Auto-Fill Extension tab) and sign in to ZipRecruiter to scan.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">ZipRecruiter queue</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={reprocessZipRecruiterQueue}
                title="Clear cached tailoring for every not-yet-applied lead so each re-tailors fresh on its next Apply click">
                Reprocess queue
              </Button>
              <Button size="small" onClick={() => fetchJobLeads()}>Refresh</Button>
            </Stack>
          </Stack>
          {(() => {
            const ziprecruiterLeads = jobLeads.filter((l) => l.status !== 'dismissed' && l.source === 'ziprecruiter');
            if (!ziprecruiterLeads.length) {
              return (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No ZipRecruiter jobs yet — scan above to fill this queue.
                </Typography>
              );
            }
            return (
              <Stack spacing={1}>
                {ziprecruiterLeads.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>{cleanTitle(lead.title)}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {[lead.company, lead.location].filter(Boolean).join(' · ')}
                          </Typography>
                          <Chip size="small" label={lead.status} sx={{ mt: 0.5 }}
                            color={lead.status === 'applied' ? 'success' : lead.status === 'tailored' ? 'primary' : 'default'} />
                        </Box>
                        <Stack direction="row" spacing={0.5}
                          sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 }}>
                          <Button size="small" href={lead.url} target="_blank" rel="noopener">View</Button>
                          {lead.status === 'applied' ? (
                            <Button size="small" disabled>Applied ✓</Button>
                          ) : (
                            <>
                              <Button size="small" variant="contained" disabled={!extDetected}
                                onClick={() => ziprecruiterApplyLead(lead)}>Apply</Button>
                              <Button size="small" color="inherit" onClick={() => setLeadStatus(lead.id, 'applied')}>
                                Mark applied
                              </Button>
                            </>
                          )}
                          <Button size="small" color={lead.interview ? 'secondary' : 'inherit'}
                            startIcon={<EventAvailable />}
                            onClick={() => setLeadInterview(lead.id, !lead.interview)}>
                            {lead.interview ? 'Remove interview' : 'I have an interview'}
                          </Button>
                          <Button size="small" color="inherit" onClick={() => setLeadStatus(lead.id, 'dismissed')}>
                            Dismiss
                          </Button>
                          {lead.status !== 'applied' && (
                            <Button size="small" color="inherit"
                              onClick={() => setLeadStatus(lead.id, 'dismissed', 'expired')}
                              title="Posting is gone — removes it and won't be restored by Re-check dismissed">
                              No longer available
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            );
          })()}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h5" gutterBottom>
            Chrome Browser Extension for auto-submit
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The ResumeAlign extension works on Dice in your own browser: it scans
            search results into your Dice Job Search queue, fills the application,
            attaches your tailored résumé, and submits it for you — a true one-click
            apply.
          </Typography>

          {extError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExtError('')}>
              {extError}
            </Alert>
          )}

          {extInstalledVersion &&
            extVersion?.version &&
            cmpVersion(extInstalledVersion, extVersion.version) < 0 && (
              <Alert severity="warning" icon={<Info />} sx={{ mb: 2 }}>
                Update available — you have <strong>v{extInstalledVersion}</strong>, latest is{' '}
                <strong>v{extVersion.version}</strong>.{' '}
                {CHROME_EXTENSION_URL
                  ? 'Chrome updates store installs automatically (usually within a day), or grab the newest build from “Advanced” below.'
                  : 'Re-download and reload it from the install steps below.'}
              </Alert>
            )}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  1 · Install
                </Typography>
                {extVersion && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={`v${extVersion.version} · built ${extVersion.built}`}
                  />
                )}
              </Stack>
              {CHROME_EXTENSION_URL && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    href={CHROME_EXTENSION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Add to Chrome
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    One click on the Chrome Web Store — then pin it from the puzzle-piece menu. It
                    updates itself automatically.
                  </Typography>
                </>
              )}
              {/* Advanced: load the very latest build before the Web Store review approves it. */}
              <Accordion sx={{ mt: 1.5, '&:before': { display: 'none' } }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" color="text.secondary">
                    {CHROME_EXTENSION_URL ? 'Advanced: install the latest build manually' : 'How to install it'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload />}
                    href="/resumealign-extension.zip"
                    download={
                      extVersion ? `resumealign-extension-v${extVersion.version}.zip` : 'resumealign-extension.zip'
                    }
                    sx={{ mb: 1.5 }}
                  >
                    {extVersion ? `Download v${extVersion.version} (.zip)` : 'Download (.zip)'}
                  </Button>
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    <li>
                      <Typography variant="body2">
                        <b>Unzip</b> the downloaded file.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Open <code>chrome://extensions</code> and turn on <b>Developer mode</b> (top-right).
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Click <b>Load unpacked</b> and select the unzipped{' '}
                        <code>resumealign-extension</code> folder.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Pin it from the puzzle-piece menu so its icon shows in the toolbar.
                      </Typography>
                    </li>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>
                2 · Connect (automatic)
              </Typography>
              {extConnected ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  Connected — you're all set. The extension is signed in to your account; no further setup needed.
                </Alert>
              ) : extDetected ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Extension detected — connecting automatically…
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Once you install the extension above and reload this page, it connects itself
                  automatically — nothing to copy or paste. (Installed it already? Just reload this page.)
                </Typography>
              )}
              {/* Manual fallback: only if the extension can't be reached after a connect attempt. */}
              {extToken && !extConnected && (
                <Box sx={{ mt: 1.5 }}>
                  <Button variant="text" size="small" onClick={connectExtension} disabled={extLoading} sx={{ mb: 1 }}>
                    {extLoading ? 'Connecting…' : 'Retry connect'}
                  </Button>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Still not connected? Paste this into the extension’s <strong>Settings → Connection token</strong>:
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={extToken}
                      InputProps={{ readOnly: true }}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="contained"
                      onClick={() => navigator.clipboard?.writeText(extToken)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Copy
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Copy this now — it won't be shown again. The URL is already defaulted to alignresume.com.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Interview Prep
              </Typography>
              <Typography variant="body2" color="text.secondary">
                AI-tailored interview questions and talking points drawn from your resume.
                Mark a job in your queue as an interview, or paste a posting URL below.
              </Typography>
            </Box>

            {error && (
              <Alert severity="warning" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎤 Voice mock interview <Chip size="small" label="Beta" sx={{ ml: 0.5 }} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  General practice with an AI voice interviewer (not tied to a specific job).
                  To practice for a specific job, use “Start voice mock interview” on that job
                  below. Use a headset for the best experience.
                </Typography>
                <VoiceMockInterview onBilled={fetchBalance} />
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Interview for a job not in your queue?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Paste the job posting URL — we'll add it to your queue and prep you for it.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Job posting URL"
                    value={prepUrl}
                    onChange={(e) => setPrepUrl(e.target.value)}
                    placeholder="https://…"
                  />
                  <Button
                    variant="contained"
                    startIcon={prepUrlLoading ? <CircularProgress size={16} /> : <Quiz />}
                    disabled={prepUrlLoading || !prepUrl.trim()}
                    onClick={addInterviewByUrl}
                  >
                    {prepUrlLoading ? 'Preparing…' : 'Prep me'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {(() => {
              const interviews = jobLeads.filter((l) => l.interview);
              if (interviews.length === 0) {
                return (
                  <Alert severity="info">
                    No interviews yet. Mark a job in{' '}
                    <MuiLink component="button" type="button" onClick={() => handleTabChange(null, 2)}>
                      Dice Job Search
                    </MuiLink>{' '}
                    as an interview, or paste a URL above.
                  </Alert>
                );
              }
              return interviews.map((lead) => {
                const prep = interviewPreps[lead.id];
                return (
                  <Card key={lead.id}>
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography variant="h6">{lead.title || '(untitled role)'}</Typography>
                          {lead.company && (
                            <Typography variant="body2" color="text.secondary">
                              {lead.company}
                            </Typography>
                          )}
                          {lead.url && (
                            <MuiLink
                              href={lead.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                            >
                              View original posting ↗
                            </MuiLink>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          {prepLoadingId === lead.id ? (
                            <Button variant="contained" disabled startIcon={<CircularProgress size={16} />}>
                              Generating…
                            </Button>
                          ) : prep === undefined ? (
                            // Still checking for a saved prep — avoid flashing
                            // "Prep for interview" before we know.
                            <Button variant="contained" disabled startIcon={<CircularProgress size={16} />}>
                              Checking…
                            </Button>
                          ) : prep ? (
                            <Button
                              variant="outlined"
                              startIcon={<Quiz />}
                              onClick={() => generatePrep(lead.id)}
                            >
                              Regenerate
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              startIcon={<Quiz />}
                              onClick={() => generatePrep(lead.id)}
                            >
                              Prep for interview
                            </Button>
                          )}
                        </Stack>
                      </Stack>

                      <Accordion
                        disableGutters
                        onChange={(_, expanded) => expanded && loadJobSpec(lead.id)}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">
                            <Description
                              fontSize="small"
                              sx={{ verticalAlign: 'text-bottom', mr: 0.5 }}
                            />
                            Job description
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {jobSpecLoadingId === lead.id ? (
                            <CircularProgress size={20} />
                          ) : jobSpecs[lead.id] ? (
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
                            >
                              {jobSpecs[lead.id]}
                            </Typography>
                          ) : lead.id in jobSpecs ? (
                            <Typography variant="body2" color="text.secondary">
                              No description stored yet.{' '}
                              {lead.url && (
                                <MuiLink href={lead.url} target="_blank" rel="noopener noreferrer">
                                  View the original posting ↗
                                </MuiLink>
                              )}{' '}
                              Generating prep will also capture the full posting.
                            </Typography>
                          ) : null}
                        </AccordionDetails>
                      </Accordion>

                      {prep && (
                        <Box sx={{ mt: 1 }}>
                          {prep.readiness_note && (
                            <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
                              {prep.readiness_note}
                            </Alert>
                          )}
                          {prep.focus_areas && prep.focus_areas.length > 0 && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Focus areas
                              </Typography>
                              <List dense>
                                {prep.focus_areas.map((f, i) => (
                                  <ListItem key={i}>
                                    <ListItemText primary={f} />
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                          {Array.from(new Set(prep.questions.map((q) => q.category))).map((cat) => (
                            <Box key={cat} sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                {cat}
                              </Typography>
                              {prep.questions
                                .filter((q) => q.category === cat)
                                .map((q, i) => (
                                  <Accordion key={i} disableGutters>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                      <Typography>{q.question}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      {q.why && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mb: 1 }}
                                        >
                                          Why they ask: {q.why}
                                        </Typography>
                                      )}
                                      {q.talking_points && q.talking_points.length > 0 && (
                                        <List dense>
                                          {q.talking_points.map((tp, j) => (
                                            <ListItem key={j} sx={{ py: 0.25 }}>
                                              <ListItemText primary={`• ${tp}`} />
                                            </ListItem>
                                          ))}
                                        </List>
                                      )}
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Voice mock interview for this job
                      </Typography>
                      <VoiceMockInterview
                        leadId={lead.id}
                        showHistory={false}
                        onBilled={fetchBalance}
                      />
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="h5" gutterBottom data-tour="plans">
            Plans
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Monthly credits cover any action (optimize, formatted PDF, auto-apply). Change or
            cancel anytime — switches are pro-rated. Experimental features (on Free and Pro)
            include the AI voice mock interview, the Chrome extension, and one-click Dice
            auto-submit. The <b>Power (BYO key)</b> plan is a flat fee with <b>unlimited</b>{' '}
            volume — you connect your own Anthropic API key and pay Anthropic directly for the AI.
          </Typography>

          {/* Enterprise org: join with a code (firm-paid, branded resumes). */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 0.5 }}>
                {org?.member ? `Organization: ${org.org?.display_name}` : 'Have an organization code?'}
              </Typography>
              {org?.member ? (
                <Typography variant="body2" color="text.secondary">
                  You're an Enterprise member — unlimited usage (your firm covers it) and a
                  resume branded for <b>{org.org?.display_name}</b> (look for “Branded résumé” in History).
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Consulting firms can enroll their people with a code — unlimited usage and a
                    company-branded resume. Enter your firm's code to join.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField size="small" placeholder="Organization code" value={orgCode}
                      onChange={(e) => setOrgCode(e.target.value)} sx={{ minWidth: 240 }} />
                    <Button variant="outlined" disabled={orgBusy || !orgCode.trim()} onClick={redeemOrg}>
                      {orgBusy ? 'Joining…' : 'Join'}
                    </Button>
                  </Stack>
                </>
              )}
              {orgMsg && (
                <Typography variant="body2" sx={{ mt: 1 }} color={orgMsg.startsWith('Joined') ? 'success.main' : 'text.secondary'}>
                  {orgMsg}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Self-serve org admin: create/manage your firm's branded org. */}
          <OrgAdminPanel isMember={!!org?.member} />

          {/* BYOK key connection — shown when the user is on the Power plan. */}
          {balance?.byok && (
            <Card data-tour="byok-key" sx={{ mb: 3, borderColor: balance.has_anthropic_key ? 'success.main' : 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 0.5 }}>
                  Your Anthropic API key {balance.has_anthropic_key ? '✓ connected' : '· required'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  On the Power plan, your optimizations, formatted PDFs and interview prep run on
                  your own Anthropic key — so your volume is unlimited and billed to you by Anthropic
                  at cost. Get a key at console.anthropic.com → API Keys. It's stored securely and
                  never shown again.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    type="password"
                    placeholder="sk-ant-…"
                    value={anthropicKey}
                    onChange={(e) => setAnthropicKey(e.target.value)}
                    sx={{ minWidth: 280 }}
                  />
                  <Button variant="contained" disabled={savingKey || !anthropicKey.trim()} onClick={() => saveAnthropicKey(false)}>
                    {savingKey ? 'Verifying…' : balance.has_anthropic_key ? 'Replace key' : 'Connect key'}
                  </Button>
                  {balance.has_anthropic_key && (
                    <Button color="inherit" disabled={savingKey} onClick={() => saveAnthropicKey(true)}>
                      Remove
                    </Button>
                  )}
                </Stack>
                {keyMsg && (
                  <Typography variant="body2" sx={{ mt: 1 }} color={balance.has_anthropic_key ? 'success.main' : 'text.secondary'}>
                    {keyMsg}
                  </Typography>
                )}
                {!balance.has_anthropic_key && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    Until a key is connected, optimizations on this plan can't run.
                  </Typography>
                )}
                {/* MCP saves even more than BYOK. */}
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.50') }}>
                  <Typography variant="body2" sx={{ color: BBS.cyan, fontWeight: 600 }}>
                    💡 Save even more with MCP
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    BYOK still bills you per token at Anthropic. If you use Claude Code, connect it via{' '}
                    <b>MCP</b> instead — your tailoring runs on your Claude subscription, so there's{' '}
                    <b>no per-token API cost at all</b>. See the “Connect AI Agent (MCP)” tab.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
          {/* MCP / BYOM explainer — the biggest cost saver. */}
          <Card variant="outlined" sx={{ mb: 3, borderColor: BBS.cyan }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <SmartToy sx={{ color: BBS.cyan }} />
                <Typography variant="subtitle1" sx={{ color: BBS.cyan }}>
                  New: Bring Your Own Model (MCP)
                </Typography>
                <Chip size="small" label="Lowest cost" sx={neon(BBS.green)} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If you use <b>Claude Code</b> (or any MCP client), connect it to ResumeAlign and your
                own AI agent does the résumé tailoring — on your Claude subscription. That means{' '}
                <b>no Anthropic API key and no per-use AI cost</b> (with BYOK you still pay Anthropic
                per token; with MCP you don't). The <b>MCP Plan</b> below makes it unlimited.
              </Typography>
              <Button size="small" variant="outlined" startIcon={<SmartToy />} onClick={() => handleTabChange(null, 6)}>
                Set up “Connect AI Agent (MCP)”
              </Button>
            </CardContent>
          </Card>
          {plans.some((p) => p.has_yearly) && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={billingInterval}
                onChange={(e, v) => v && setBillingInterval(v)}
              >
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="yearly">Yearly</ToggleButton>
              </ToggleButtonGroup>
              <Chip
                size="small"
                label={`Save ${
                  plans.find((p) => p.has_yearly && p.yearly_savings_pct)?.yearly_savings_pct ?? 17
                }% yearly`}
                sx={neon(BBS.green)}
              />
            </Stack>
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="stretch"
            sx={{ mb: 2 }}
          >
            {plans.map((p) => {
              const current = (balance?.plan || 'free') === p.id;
              const paid = p.id !== 'free';
              return (
                <Card
                  key={p.id}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    borderColor: current ? BBS.green : undefined,
                    borderWidth: current ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="h6" sx={{ color: BBS.cyan }}>
                        {p.name}
                      </Typography>
                      {current && <Chip size="small" label="Current" sx={neon(BBS.green)} />}
                    </Stack>
                    {(() => {
                      const yearly = billingInterval === 'yearly' && p.has_yearly && p.yearly_price;
                      return (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="h4" component="div">
                            {p.price === 0
                              ? 'Free'
                              : yearly
                              ? `$${(p.yearly_price! / 12).toFixed(2)}`
                              : `$${p.price.toFixed(2)}`}
                            {p.price > 0 && (
                              <Typography component="span" variant="body2" color="text.secondary">
                                {' '}/mo
                              </Typography>
                            )}
                          </Typography>
                          {yearly && (
                            <Typography variant="caption" sx={{ color: BBS.green, display: 'block' }}>
                              ${p.yearly_price!.toFixed(0)} billed yearly · save {p.yearly_savings_pct}%
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {p.byok ? (
                          <><b>Unlimited</b> usage&nbsp;*</>
                        ) : (
                          <><b>{p.monthly_credits}</b> credits / month</>
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ color: p.experimental ? BBS.green : 'text.secondary' }}>
                        {p.experimental ? '✓ Experimental features (AI mock interview + Dice auto-submit)' : '— No experimental features'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: (p.allow_overage || p.byok) ? BBS.green : 'text.secondary' }}>
                        {p.byok ? '✓ Uses your own Anthropic API key' : p.allow_overage ? '✓ Prepaid overage' : '— Hard monthly cap'}
                      </Typography>
                      {p.byok && (
                        <Typography variant="caption" color="text.secondary">
                          * No monthly cap. Anthropic API usage is billed to your own account, on your own accord.
                        </Typography>
                      )}
                    </Stack>
                    {current ? (
                      paid ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={openBillingPortal}
                          disabled={planActionId === 'portal'}
                        >
                          {planActionId === 'portal' ? 'Opening…' : 'Manage / cancel'}
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" disabled>
                          Current plan
                        </Button>
                      )
                    ) : paid ? (
                      <>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => subscribe(p.id)}
                          disabled={planActionId === p.id}
                        >
                          {planActionId === p.id
                            ? 'Redirecting…'
                            : balance?.plan && balance.plan !== 'free'
                            ? 'Switch to this plan'
                            : 'Subscribe'}
                        </Button>
                        <Typography
                          variant="caption"
                          align="center"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          No commitment — cancel anytime
                        </Typography>
                      </>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={openBillingPortal}
                        disabled={!balance?.plan || balance?.plan === 'free' || planActionId === 'portal'}
                      >
                        Downgrade to Free
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {/* MCP Plan (BYOM) — preview; billing wiring pending. */}
            {(() => {
              const yearly = billingInterval === 'yearly';
              const isCurrent = balance?.plan === 'mcp';
              return (
                <Card variant="outlined" sx={{ flex: 1, borderColor: isCurrent ? BBS.green : BBS.cyan, borderWidth: 2 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="h6" sx={{ color: BBS.cyan }}>MCP Plan</Typography>
                      <Chip size="small" label={isCurrent ? 'Current' : 'Best value'} sx={neon(BBS.green)} />
                    </Stack>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h4" component="div">
                        {yearly ? '$16.58' : '$19.99'}
                        <Typography component="span" variant="body2" color="text.secondary"> /mo</Typography>
                      </Typography>
                      {yearly && (
                        <Typography variant="caption" sx={{ color: BBS.green, display: 'block' }}>
                          $199 billed yearly · save 17%
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      <Typography variant="body2"><b>Unlimited</b> auto-apply via MCP&nbsp;*</Typography>
                      <Typography variant="body2" sx={{ color: BBS.green }}>✓ Bring Your Own Model — tailoring runs on your Claude Code</Typography>
                      <Typography variant="body2" sx={{ color: BBS.green }}>✓ No Anthropic API key needed</Typography>
                      <Typography variant="body2" sx={{ color: BBS.green }}>✓ Experimental features (AI mock interview + Dice auto-submit)</Typography>
                      <Typography variant="body2" color="text.secondary">~20 credits / month for web optimize</Typography>
                      <Typography variant="caption" color="text.secondary">
                        * Tailoring runs on your own Claude Code subscription (via MCP), so there's no per-use AI cost.
                      </Typography>
                    </Stack>
                    {isCurrent ? (
                      <Button fullWidth variant="outlined" onClick={openBillingPortal} disabled={planActionId === 'portal'}>
                        {planActionId === 'portal' ? 'Opening…' : 'Manage / cancel'}
                      </Button>
                    ) : (
                      <Button fullWidth variant="contained" onClick={() => subscribe('mcp')} disabled={planActionId === 'mcp'}>
                        {planActionId === 'mcp'
                          ? 'Redirecting…'
                          : balance?.plan && balance.plan !== 'free'
                          ? 'Switch to MCP Plan'
                          : 'Subscribe'}
                      </Button>
                    )}
                    <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Needs Claude Code · cancel anytime — set up in “Connect AI Agent (MCP)”
                    </Typography>
                  </CardContent>
                </Card>
              );
            })()}
          </Stack>
          {balance?.plan && balance.plan !== 'free' && (
            <Typography variant="caption" color="text.secondary">
              Plan changes and cancellation are handled in the Stripe billing portal (Manage / cancel).
              {balance.current_period_end
                ? ` Current period ends ${fmtDateTime(balance.current_period_end)}.`
                : ''}
            </Typography>
          )}

        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Typography variant="h5" gutterBottom>
            Connect your AI agent (MCP)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect Claude Code, Claude Desktop, or any MCP client to ResumeAlign so
            your agent can score a job, tailor and format your résumé, and drive
            auto-apply. The heavy tailoring runs on your own agent — not our servers.
          </Typography>

          {/* Server URL */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Server URL</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                value={mcpEndpoint}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Button variant="outlined" onClick={() => copyMcp('url', mcpEndpoint)} sx={{ whiteSpace: 'nowrap' }}>
                {mcpCopied === 'url' ? 'Copied' : 'Copy'}
              </Button>
            </Stack>
          </Paper>

          {/* Step 1: token */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>1. Generate your access token</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The token lets your agent reach your saved résumés and apply queue. Keep
              it private — it works like a password, and it won’t affect your Chrome
              extension connection.
            </Typography>

            {mcpNeedsPower ? (
              <Alert
                severity="info"
                sx={{ mb: 1 }}
                action={<Button color="inherit" size="small" onClick={() => handleTabChange(null, 4)}>See plans</Button>}
              >
                Connecting an agent to your account is included on the Free and Pro plans —
                upgrade to Pro to use it on your plan.
              </Alert>
            ) : mcpError ? (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMcpError('')}>{mcpError}</Alert>
            ) : null}

            {!mcpToken ? (
              <Button variant="contained" onClick={generateAgentToken} disabled={mcpLoading} startIcon={<SmartToy />}>
                {mcpLoading ? 'Generating…' : 'Generate token'}
              </Button>
            ) : (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={mcpToken}
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button variant="contained" onClick={() => copyMcp('token', mcpToken!)} sx={{ whiteSpace: 'nowrap' }}>
                    {mcpCopied === 'token' ? 'Copied' : 'Copy'}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Copy this now — it won’t be shown again. Generating a new one replaces the old.
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Step 2: command */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>2. Add the server in Claude Code</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Run this in your terminal (the token is filled in once you generate it above):
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0, p: 1.5, borderRadius: 1, fontSize: 12, overflowX: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                color: 'text.primary',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
                border: '1px solid', borderColor: 'divider',
              }}
            >
              {mcpCommand(mcpToken || '<YOUR_TOKEN>')}
            </Box>
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => copyMcp('cmd', mcpCommand(mcpToken || '<YOUR_TOKEN>'))}
            >
              {mcpCopied === 'cmd' ? 'Copied' : 'Copy command'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Claude Desktop or another client: add a remote (HTTP) MCP server with the URL above and an
              {' '}<code>Authorization: Bearer &lt;token&gt;</code> header.
            </Typography>
          </Paper>

          {/* Step 3: try it */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>3. Ask your agent</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Try this first:</strong>
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.50') }}>
              <Typography variant="body2">“Do an auto-pilot for keyword <strong>mcp developer</strong>, remote only.”</Typography>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Your agent triggers a real Dice search, tailors your résumé to each match, and auto-applies —
              keep this tab open with the extension connected and signed in to Dice.
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>More to try:</Typography>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant="body2">• “Score my résumé against this job: &lt;url&gt;”</Typography>
              <Typography variant="body2">• “Tailor my résumé to this posting and format it as a PDF”</Typography>
              <Typography variant="body2">• “Show my recent auto-pilot runs and what I spent”</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Scoring and formatting work for everyone; auto-pilot, your saved résumés, and the apply
              queue need the token above (Power plan).
            </Typography>
          </Paper>
        </TabPanel>
          </Box>
        </Box>
      </Box>
      <Footer />

      {/* Floating help/support/chat — available on every page */}
      <HelpWidget navigate={(t, wizStep) => { handleTabChange(null, t); if (wizStep) setWizardStep(wizStep); }} />

      {/* Branded résumé */}
      <Dialog open={!!brandedOptId} onClose={() => setBrandedOptId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Branded résumé</DialogTitle>
        <DialogContent>
          {org?.member ? (
            <Typography variant="body2" color="text.secondary">
              We'll style your optimized resume with <b>{org.org?.display_name}</b>'s brand — logo
              header and a clean projects/experience layout. Included with your Enterprise plan.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Style your optimized resume with a company's brand — logo header, projects &
                experience sections. Costs 1 credit.
              </Typography>
              <TextField
                label="Company name (header)"
                placeholder="e.g. IBM Consulting"
                value={brandedCompany}
                onChange={(e) => setBrandedCompany(e.target.value)}
                fullWidth
                size="small"
              />
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  label="Accent"
                  type="color"
                  value={brandedColor}
                  onChange={(e) => setBrandedColor(e.target.value)}
                  sx={{ width: 80 }}
                  size="small"
                />
                <Button component="label" size="small" variant="outlined">
                  {brandedLogo ? 'Logo added ✓' : 'Upload logo (optional)'}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const r = new FileReader();
                      r.onload = () => setBrandedLogo(String(r.result || ''));
                      r.readAsDataURL(f);
                    }}
                  />
                </Button>
                {brandedLogo && (
                  <Button size="small" color="inherit" onClick={() => setBrandedLogo('')}>
                    Remove logo
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Only use a company name/logo you're authorized to represent.
              </Typography>
            </Stack>
          )}
          {brandedMsg && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1.5 }}>
              {brandedMsg}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandedOptId(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={brandedBusy || (!org?.member && !brandedCompany.trim())}
            onClick={generateBranded}
          >
            {brandedBusy ? 'Building…' : 'Generate & download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mass-apply progress */}
      <Dialog open={massOpen} onClose={() => setMassOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch sx={{ color: '#ff7a18' }} /> Mass apply
        </DialogTitle>
        <DialogContent>
          {massApply && (
            <>
              {massApply.items.length > 0 && (() => {
                const total = massApply.items.length;
                const failed = massApply.items.filter((it) => it.status === 'error').length;
                const skip = failed > 0 ? ` · ${failed} skipped` : '';
                if (massApply.phase === 'applying') {
                  const done = massApply.items.filter((it) => it.status === 'done').length;
                  return (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Applied {done} of {total}{skip}
                    </Typography>
                  );
                }
                // Prep phase (optimize + PDF): the note + per-job steppers below
                // carry the granular progress, so just frame it as still working.
                const prep = total - failed;
                return (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Preparing {prep} résumé{prep === 1 ? '' : 's'}…{skip}
                  </Typography>
                );
              })()}
              <Typography variant="body2" sx={{ color: '#ff7a18', mb: 1.5 }}>
                {massApply.note}
              </Typography>
              <Stack spacing={1}>
                {massApply.items.map((it, idx) => (
                  <Box key={`${it.title}-${idx}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ mt: '2px', width: 22, display: 'flex', justifyContent: 'center' }}>
                      {it.status === 'done' ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : it.status === 'active' ? (
                        <CircularProgress size={16} sx={{ color: '#ff7a18' }} />
                      ) : it.status === 'error' ? (
                        <ErrorOutline color="error" fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: it.status === 'active' ? 600 : 400,
                          color: it.status === 'error' ? 'error.main' : 'text.primary',
                        }}
                      >
                        {it.url ? (
                          <MuiLink href={it.url} target="_blank" rel="noopener noreferrer"
                            color="inherit" sx={{ textDecorationColor: 'rgba(255,255,255,0.3)' }}>
                            {it.title} ↗
                          </MuiLink>
                        ) : (
                          it.title
                        )}
                      </Typography>
                      {it.detail && (
                        <Typography
                          variant="caption"
                          color={it.status === 'error' ? 'error' : 'text.secondary'}
                          sx={{ display: 'block' }}
                        >
                          {it.detail}
                        </Typography>
                      )}
                      {it.reason === 'nonremote' && it.url && (
                        <Button size="small" variant="outlined" sx={{ mt: 0.5 }}
                          onClick={() => requeueRemote(it.url)}>
                          It's remote — requeue
                        </Button>
                      )}
                      {it.stages && <StageStepper stages={it.stages} />}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {massBusy && (
            <Button
              color="error"
              onClick={() => {
                stopMassApply();
                setMassApply((m) => (m ? { ...m, note: 'Stopping…' } : m));
              }}
            >
              Stop
            </Button>
          )}
          <Button onClick={() => setMassOpen(false)}>{massBusy ? 'Hide' : 'Close'}</Button>
        </DialogActions>
      </Dialog>

      {/* One-click auto-apply timeline */}
      <Dialog open={!!autoApply} onClose={() => setAutoApply(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch sx={{ color: '#ff7a18' }} /> Auto-applying
        </DialogTitle>
        <DialogContent>
          {autoApply && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {autoApply.title}
              </Typography>
              <Stack spacing={0}>
                {autoApply.steps.map((step, i) => (
                  <Box
                    key={step.key}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      pb: i < autoApply.steps.length - 1 ? 2 : 0,
                      position: 'relative',
                    }}
                  >
                    {i < autoApply.steps.length - 1 && (
                      <Box
                        sx={{ position: 'absolute', left: '11px', top: '24px', bottom: 0, width: '2px', bgcolor: 'divider' }}
                      />
                    )}
                    <Box sx={{ zIndex: 1, mt: '2px', width: 24, display: 'flex', justifyContent: 'center' }}>
                      {step.status === 'done' ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : step.status === 'active' ? (
                        <CircularProgress size={18} sx={{ color: '#ff7a18' }} />
                      ) : step.status === 'error' ? (
                        <ErrorOutline color="error" fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: step.status === 'active' ? 600 : 400,
                          color:
                            step.status === 'error'
                              ? 'error.main'
                              : step.status === 'pending'
                              ? 'text.secondary'
                              : 'text.primary',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {step.detail && (
                        <Typography
                          variant="caption"
                          color={step.status === 'error' ? 'error' : 'text.secondary'}
                          sx={{ display: 'block' }}
                        >
                          {step.detail}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoApply(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
}

export default App;